import { observeChecklistMediaReferenceSend } from './diagnostics/ChecklistMediaSyncDiagnostic';
import { reconcileChecklistMediaRetention } from './sync/ChecklistMediaRetention';
import { parsePartnerStorageReference } from '@/utils/media/partnerStorageReference';
import { mediaPayloadHash, hasMediaCancellation, sameMediaRow, hasPendingMediaObservation } from './sync/ChecklistMediaConflictContract';
import { readChecklistMediaSuccession, predecessorObservation, confirmsChecklistMediaPredecessor } from './sync/ChecklistMediaSuccessionContract';
import { resolveTerminalChecklistMedia } from '@/services/sync/ChecklistMediaConflictResolution';
import { acknowledgeChecklistMediaObservation, retryAcknowledgedChecklistMediaObservations } from './sync/ChecklistMediaObservationAcknowledgement';
import { checklistAnswerValue, isAnswerPatch, hasAnswerObservation, checklistAnswerMatchesRow } from './checklist/checklistObservations';
import { stableStringify } from './checklist/ChecklistSnapshotCreationService';
import { buildLocalPullProtection, createEmptyLocalPullProtection, isProtectedFromRemotePull, parsePullProtectionData, type LocalPullProtection } from './sync/LocalPullProtection';
import { materializeHistoricalPhoto, historicalPhotoCacheTable, downloadHistoricalMediaBase64 } from './sync/HistoricalPhotoCacheService';
import { materializeHistoricalImage, historicalImageCacheTable } from './sync/HistoricalImageCacheService';
import type { HistoricalInsertGuard } from './sync/HistoricalInsertGuard';
import { prepareArchivedPartialReplays } from './sync/ArchivedPartialReplayRuntime';
import { finalizePendingChecklistMedia } from '@/utils/file/uploadImageHelper';
import { prepareChecklistCancellations, processAutomaticChecklistCancellations,
  isChecklistCancellationBlocked, assertChecklistNotCancelled } from './sync/ChecklistCancellationRuntime';
import { ChecklistCreateAcknowledgementService } from './sync/ChecklistCreateAcknowledgementService';
import { RetiredChecklistHistoryService, retiredHistoryTables } from './sync/RetiredChecklistHistoryService';
import { pullCanonicalMetadata } from './sync/CanonicalMetadataPullStage';
import { historicalTables } from './sync/HistoricalSnapshotHydrationService';
import { historicalValueCreateMatches, resolveHistoricalValueCreateReference } from './sync/HistoricalValueCreateReference';
import { verifyFieldDeleteDependencies } from './sync/FieldDeleteDependencyGuard';
import { hasSyncExecution, reserveSyncExecution } from './sync/SyncExecutionLease';
import { reportSyncDiagnosticResult, beginSyncDiagnosticCycle, reportUnrecoveredSyncFailure, getActiveSyncTraceId, getActiveSyncSQLiteFailures } from '@/services/diagnostics/syncDiagnosticEvents';
import { globalState, syncActions, databaseActions, userActions } from '../state/globalState';
import { SyncLogger } from '../utils/logger';
import { supabase } from './supabase';
import {
  assertPendingSyncSessionCurrent,
  capturePendingSyncSession,
  createPendingSyncSupabaseClient,
  guardPendingSyncRemoteQuery,
  isPendingSyncSessionCurrent,
  isPendingSyncSessionSnapshotStateCurrent,
  PendingSyncSessionChangedError,
  type PendingSyncSessionSnapshot,
} from './sync/PendingSyncSession';
import { databaseService } from './databaseService';
import { APP_CONSTANTS } from '../config/constants';
import { MMKV } from 'react-native-mmkv';
import { Platform } from 'react-native';
import { processPendingImages, processPendingDeletions, PendingProcessProgress, PendingProcessResult } from '../utils/file/uploadImageHelper';
import { getUserProfile } from './userService';
import { refreshPulledIdentity } from './sync/RefreshPulledIdentity';
import { debugPause } from '../utils/debugPauseUniversal';
import * as FileSystem from 'expo-file-system/legacy';
import { realtimeTrigger } from '../state/realtime';
import { v4 as uuidv4 } from 'uuid';
import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import { checkSubscriptionStatus } from './subscriptionService';
import { checkConnection } from './network';
import { sanitizeLocalTableRows } from '../utils/localTableSchema';
import {
  addSentryBreadcrumb,
  captureSentryExceptionWithScope,
  captureSentryMessageWithScope,
} from './monitoring/sentry';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '../config/recoveryDiagnostic';
import { IS_CHECKLIST_SYNC_RECOVERY_CANARY } from '../config/recoveryCanary';
import { isRecoveryRuntimeBlocked } from './recovery/RecoveryRuntimeGate';
import { isRecoveryReceiptVerifiedInProcess } from './recovery/Ed25519RecoveryReceiptVerifier';
import {
  ChecklistValuesV2Service,
  type ChecklistSnapshotFieldReference,
} from './sync/ChecklistValuesV2Service';
import {
  CHECKLIST_VALUES_V2_FEATURE,
  HISTORICAL_SNAPSHOTS_FEATURE,
  STRUCTURAL_SYNC_FEATURES,
  SYNC_TOMBSTONES_FEATURE,
  SyncCanaryFeatureService,
  getSyncClientBuild,
  syncCanaryFeatureDecisionStore,
} from './sync/SyncCanaryFeatureService';
import {
  TombstoneSyncService,
  TombstoneCursorReplayRequiredError,
  filterTombstonedRowsWithLocalRelations,
  filterTombstonedRows,
  isTombstoneBarrierActive,
  type LocalTombstoneCursor,
  type LocalTombstoneRow,
  type RemoteTombstoneRow,
  type TombstoneFeedPage,
} from './sync/TombstoneSyncService';
import {
  includeHistoricalTablesWhenEnabled,
  RecoveryCanonicalPullService,
  resolveRecoveryPullCompanyId,
  type RecoveryCanonicalPullContext,
} from './sync/RecoveryCanonicalPullService';
import { getIncrementalTimestampColumns } from './sync/PullTimestampPolicy';
import { MMKVChecklistValuesV2PreparedStore } from './sync/MMKVChecklistValuesV2PreparedStore';
import {
  ChecklistSnapshotRemoteCreateService,
  normalizeChecklistSnapshotHistoricalRow,
} from './checklist/ChecklistSnapshotRemoteCreateService';
import type {
  ChecklistValueV2Outcome,
  ChecklistValueV2LocalRow,
  ChecklistValueV2RpcResult,
} from '../types/syncRecovery';
import { downloadRemoteImageToFile } from '@/utils/file/downloadRemoteImageIOS';
import { getClientInstallationPresenceMetadata } from '@/services/runtime/clientInstallationPresence';

// Criar uma instância do MMKV para armazenar os timestamps de sincronização
const syncStorage = new MMKV();

type PendingSyncExecution = {
  snapshot: PendingSyncSessionSnapshot;
  client: ReturnType<typeof createPendingSyncSupabaseClient>;
};

let activePendingSyncExecution: PendingSyncExecution | null = null;

const getSyncSupabaseClient = (): typeof supabase | any => {
  const execution = activePendingSyncExecution;
  if (!execution) return supabase;
  return new Proxy(execution.client as any, {
    get(target, property) {
      const value = Reflect.get(target, property, target);
      if ((property === 'from' || property === 'rpc') && typeof value === 'function') {
        return (...args: any[]) => guardPendingSyncRemoteQuery(value.apply(target, args), execution.snapshot);
      }
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
};

const syncCanaryFeatureService = new SyncCanaryFeatureService({
  async isEnabled(input) {
    const { data, error } = await getSyncSupabaseClient().rpc('app_guincho_feature_enabled_v1', {
      p_empresa_id: input.companyId,
      p_feature_key: input.featureKey,
      p_client_build: input.clientBuild,
    });
    if (error) throw error;
    return data === true;
  },
}, true, syncCanaryFeatureDecisionStore);
const checklistValuesV2PreparedStore = new MMKVChecklistValuesV2PreparedStore();
export type SupportDiagnosticSyncStatus = 'success' | 'partial';

export type PostUpdateSyncOutcome = 'success' | 'partial' | 'error';

export interface PostUpdateSyncObservation {
  companyId: string;
  userId: string;
  traceId: string;
  startedAt: string;
  finishedAt: string;
  outcome: PostUpdateSyncOutcome;
  result: SyncStatus;
  appVersion: string;
  nativeBuild: number | null;
  compatibilityBuild: number;
  platform: 'web' | 'android' | 'ios';
  observedBuild: number;
}

const supportDiagnosticSyncHooks = new Set<(status: SupportDiagnosticSyncStatus) => void>();
const clientInstallationSyncHooks = new Set<(status: 'success' | 'partial' | 'error') => void>();
const postUpdateSyncObservers = new Set<(observation: PostUpdateSyncObservation) => void | Promise<void>>();
export const registerSupportDiagnosticSyncHook = (
  hook: ((status: SupportDiagnosticSyncStatus) => void) | null
): (() => void) => {
  if (!hook) return () => undefined;
  supportDiagnosticSyncHooks.add(hook);
  return () => {
    supportDiagnosticSyncHooks.delete(hook);
  };
};

/** Mantém compatibilidade com consumidores que só acompanham sync bem-sucedido. */
export const registerSupportDiagnosticSyncSuccessHook = (hook: (() => void) | null): (() => void) => {
  if (!hook) return () => undefined;
  return registerSupportDiagnosticSyncHook(status => {
    if (status === 'success') hook();
  });
};
export const registerClientInstallationSyncHook = (
  hook: (status: 'success' | 'partial' | 'error') => void
): (() => void) => {
  clientInstallationSyncHooks.add(hook);
  return () => {
    clientInstallationSyncHooks.delete(hook);
  };
};

/**
 * Observa somente o término de um syncAll que já liberou SyncExecutionLease.
 * Os hooks legados acima continuam no ponto histórico para não mudar suporte
 * automático/presença; a coleta pós-atualização usa este caminho separado.
 */
export const registerPostUpdateSyncObserver = (
  observer: ((observation: PostUpdateSyncObservation) => void | Promise<void>) | null,
): (() => void) => {
  if (!observer) return () => undefined;
  postUpdateSyncObservers.add(observer);
  return () => {
    postUpdateSyncObservers.delete(observer);
  };
};

export const isActualPostUpdateSyncResult = (result: SyncStatus): boolean => {
  const reason = String(result.details?.reason ?? '').toLowerCase();
  if (['busy', 'recovery_diagnostic_quarantine', 'recovery_runtime_gate'].includes(reason)) return false;
  if (result.details && result.details.allMet === false) return false;
  return result.details?.finalStatus === 'success'
    || result.details?.finalStatus === 'partial'
    || result.details?.finalStatus === 'error'
    || Boolean(result.details?.traceId && !('allMet' in result.details));
};

export const shouldRefreshPullViews = (
  totalItemsSynced: number,
  tombstonesApplied: number,
  tombstoneDuplicates: number
): boolean => totalItemsSynced > 0 || tombstonesApplied + tombstoneDuplicates > 0;

const loadLocalPendingMedia = async () => {
  const images = await databaseService.listItems<any>('pending_images');
  const deletions = await databaseService.listItems<any>('pending_deletions');
  return { images, deletions };
};

const loadLocalPendingSnapshot = async (options: {
  rawOperations?: boolean;
  includeConflicts?: boolean;
} = {}) => {
  const operations = options.rawOperations
    ? await databaseService.listItems<any>('pending_operations')
    : await databaseService.getPendingOperations();
  const { images, deletions } = await loadLocalPendingMedia();
  const conflicts = options.includeConflicts
    ? await databaseService.listItems<any>('sync_conflicts')
    : [];
  return { operations, images, deletions, conflicts };
};

// Chave legado mantida apenas para deixar explícito que não participa do cursor v2.
const LAST_SYNC_TIMESTAMP_KEY = 'lastSyncTimestamp';
const LAST_SYNC_TIMESTAMP_VERSION = 'v2';
const INITIAL_BOOTSTRAP_COMPLETE_KEY = 'initialBootstrapComplete:v2';

export type LastSyncScope = {
  companyId: string | null | undefined;
  userId: string | null | undefined;
};

const resolveLastSyncScope = (scope?: LastSyncScope): { companyId: string; userId: string } | null => {
  if (scope) {
    const companyId = scope.companyId?.trim();
    const userId = scope.userId?.trim();
    return companyId && userId ? { companyId, userId } : null;
  }

  const userId = globalState.user.user.get()?.id?.trim();
  const profile = globalState.user.profile.get();
  const profileUserId = profile?.id?.trim();
  const companyId = profile?.empresa_id?.trim();

  // O perfil pode permanecer transitoriamente no estado durante uma troca de conta.
  return userId && profileUserId === userId && companyId ? { companyId, userId } : null;
};

const getLastSyncTimestampKey = ({ companyId, userId }: { companyId: string; userId: string }): string =>
  `${LAST_SYNC_TIMESTAMP_KEY}:${LAST_SYNC_TIMESTAMP_VERSION}:${encodeURIComponent(companyId)}:${encodeURIComponent(userId)}`;

const getInitialBootstrapCompleteKey = ({ companyId, userId }: { companyId: string; userId: string }): string =>
  `${INITIAL_BOOTSTRAP_COMPLETE_KEY}:${encodeURIComponent(companyId)}:${encodeURIComponent(userId)}`;

// Tamanho máximo de página para buscar dados do Supabase durante o pull
const getSupabasePullPageSize = () => APP_CONSTANTS.SYNC?.SUPABASE_PULL_PAGE_SIZE ?? 500;
const getLocalBulkUpsertThreshold = () => APP_CONSTANTS.SYNC?.LOCAL_BULK_UPSERT_THRESHOLD ?? 100;

// Union centralizado para tipos de operação
export type OperationType =
  | 'create' | 'update' | 'delete'
  | 'checklist_partial_update'
  | 'checklist_delete';

// Tipo global para operações pendentes
export type PendingOperation = {
  id: string;
  operation: OperationType;
  collection: string;
  itemId: string;
  data?: any;
  timestamp: number;
  user_id?: string | null;
  empresa_id?: string | null;
};

// Tipo "raw" para operações pendentes, usado no adapter
export interface PendingOperationRaw {
  id: string;
  operation: OperationType;
  collection: string;
  item_id: string;
  data?: any;
  timestamp: number;
  user_id?: string | null;
  empresa_id?: string | null;
}

const normalizePendingMediaType = (value: unknown): string => String(value ?? '').trim().toUpperCase();

const requiresChecklistTombstoneForPendingImage = (pending: any): boolean => {
  const itemType = normalizePendingMediaType(pending?.item_type ?? pending?.itemType);
  return itemType !== 'USER' && itemType !== 'COMPANY';
};

const requiresChecklistTombstoneForPendingDeletion = (pending: any): boolean => {
  const deletionType = normalizePendingMediaType(pending?.type);
  if (deletionType === 'LOCAL') {
    return false;
  }

  const itemType = normalizePendingMediaType(pending?.item_type ?? pending?.itemType);
  return deletionType !== 'REMOTE' || (itemType !== 'USER' && itemType !== 'COMPANY');
};

const hasPendingMediaRequiringChecklistTombstone = (
  pendingImages: ReadonlyArray<any>,
  pendingDeletions: ReadonlyArray<any>
): boolean => (
  pendingImages.some(requiresChecklistTombstoneForPendingImage) ||
  pendingDeletions.some(requiresChecklistTombstoneForPendingDeletion)
);

const checklistRemoteWriteCollections = (): Set<string> => new Set([
  APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
  APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
  APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME,
  APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
  APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
  APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOTS.NAME,
  APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME,
  APP_CONSTANTS.DATABASE.TABLES.SYNC_CONFLICTS.NAME,
]);

const checklistProtectedPullCollections = (): Set<string> => new Set([
  APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
  APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
  APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME,
  APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
  APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
]);

const pendingOperationRequiresChecklistTombstone = (operation: any): boolean => {
  const collection = String(operation?.collection ?? '').trim();
  return collection.length > 0 && checklistRemoteWriteCollections().has(collection);
};

const hasPendingOperationsRequiringChecklistTombstone = (
  pendingOperations: ReadonlyArray<any>
): boolean => pendingOperations.some(pendingOperationRequiresChecklistTombstone);

/**
 * Interface para o status da sincronização
 */
export interface SyncStatus {
  success: boolean;
  message?: string;
  timestamp: Date;
  details?: Record<string, any>;
}

const recoveryDiagnosticSyncStatus = (): SyncStatus => ({
  success: false,
  message: 'Sincronizacao bloqueada pelo modo de recuperacao.',
  timestamp: new Date(),
  details: { reason: 'recovery_diagnostic_quarantine' },
});

const recoveryGateSyncStatus = (): SyncStatus => ({
  success: false,
  message: 'Sincronizacao pausada para preservar os dados locais.',
  timestamp: new Date(),
  details: { reason: 'recovery_runtime_gate' },
});

const tombstoneFeatureOffPushStatus = (): SyncStatus => ({
  success: false,
  message: 'Push adiado porque a proteção de tombstones está desligada',
  timestamp: new Date(),
  details: {
    reason: 'tombstone_feature_off',
    pendingPreserved: true,
    processedCount: 0,
    successCount: 0,
    errorCount: 0,
  },
});

export type SyncProgressContext = 'images' | 'deletions' | 'operations';

export interface SyncProgressPayload {
  context: SyncProgressContext;
  processed: number;
  success: number;
  errors: number;
  total: number;
}

/**
 * Interface para as opções de sincronização
 */
export interface SyncOptions {
  forceFullSync?: boolean;
  protectedBootstrapRepair?: boolean;
  tables?: string[];
  retryCount?: number;
  retryDelay?: number;
  skipPush?: boolean;
  skipPendingMedia?: boolean;
  skipPull?: boolean;
  managedBySyncAll?: boolean;
  tombstoneBarriers?: Map<string, Set<string>>;
  progressReporter?: (payload: SyncProgressPayload) => void;
  tracePrefix?: string;
}

/**
 * Interface para os pré-requisitos de sincronização
 */
export interface SyncPrerequisites {
  userAuthenticated: boolean;
  userExistsInDatabase: boolean;
  networkConnected: boolean;
  databaseInitialized: boolean;
  allMet: boolean;
}

/**
 * Interface para configuração de sincronização de uma tabela
 */
export interface TableSyncConfig {
  enabled: boolean;
  filterByUser?: boolean;
  filterByCompany?: boolean;
  forceCompanyFilter?: boolean;
  userField?: string;
  companyField?: string;
  description?: string;
  pullEnabled?: boolean;
  pushEnabled?: boolean;
}

// Discriminated unions para operações CRUD
export type CreateOp = PendingOperation & { operation: 'create' };
export type UpdateOp = PendingOperation & { operation: 'update' };
export type DeleteOp = PendingOperation & { operation: 'delete' };
export type CRUDOperation = CreateOp | UpdateOp | DeleteOp;

type PendingOperationSnapshot = {
  id: string;
  operation: string;
  collection: string;
  itemId: string;
  data: string | null;
  timestamp: number | null;
  user_id: string | null;
  empresa_id: string | null;
};

const parsePendingOperationPayload = (operation: any): Record<string, any> | null => {
  const value = operation?.data;
  if (!value || typeof value !== 'string') {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
  }
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Pendências novas carregam a empresa explicitamente. As filas 119–122 podem
 * ter `empresa_id=NULL`; nesses casos só é elegível com autor explícito e
 * evidência local imutável/coerente de empresa no payload ou entidade vinculada.
 */
const isPendingOperationEligibleForSession = async (
  operation: PendingOperation,
  snapshot: PendingSyncSessionSnapshot
): Promise<boolean> => {
  const checklistsTable = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME;
  const valuesTable = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME;
  const checklistCreateRpcEnabled = operation.operation === 'create' &&
    operation.collection === checklistsTable && getSyncClientBuild() >= 124;
  const payload = parsePendingOperationPayload(operation);
  const evidence = new Set<string>();
  const addEvidence = (value: unknown) => {
    if (typeof value === 'string' && value.trim()) evidence.add(value.trim());
  };
  addEvidence(operation.empresa_id);
  addEvidence(payload?.empresa_id);

  try {
    if (operation.operation === 'create' && checklistCreateRpcEnabled &&
        operation.user_id !== snapshot.userId) {

      const checklist = await databaseService.readItem<Record<string, any>>(checklistsTable, operation.itemId);
      const assignedUserId = typeof payload?.usuario_id === 'string' ? payload.usuario_id.trim() : '';
      const templateId = typeof payload?.template_id === 'string' ? payload.template_id.trim() : '';
      if (!checklist || checklist.id !== operation.itemId || !assignedUserId || !templateId ||
          (payload?.id !== undefined && payload.id !== operation.itemId) ||
          checklist.usuario_id !== assignedUserId || checklist.template_id !== templateId ||
          (payload?.snapshot_id ?? null) !== (checklist.snapshot_id ?? null)) return false;

      const [assignee, template] = await Promise.all([
        databaseService.readItem<Record<string, any>>(
          APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
          assignedUserId
        ),
        databaseService.readItem<Record<string, any>>(
          APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
          templateId
        ),
      ]);
      if (!assignee || assignee.id !== assignedUserId || assignee.empresa_id !== snapshot.empresaId ||
          !template || template.id !== templateId || template.empresa_id !== snapshot.empresaId) return false;

      if (checklist.snapshot_id) {
        const snapshotRow = await databaseService.readItem<Record<string, any>>(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOTS.NAME,
          checklist.snapshot_id
        );
        if (!snapshotRow || snapshotRow.id !== checklist.snapshot_id ||
            snapshotRow.empresa_id !== snapshot.empresaId ||
            snapshotRow.checklist_id !== operation.itemId ||
            snapshotRow.template_id !== templateId) return false;
      }

      addEvidence(checklist.empresa_id);
      addEvidence(assignee.empresa_id);
      addEvidence(template.empresa_id);
    } else if (operation.operation === 'create' && operation.user_id !== snapshot.userId) {
      if (getSyncClientBuild() < 124 || operation.collection !== valuesTable ||
          !payload || payload.id !== operation.itemId ||
          payload.empresa_id !== snapshot.empresaId ||
          !payload.checklist_id || (!payload.campo_id && !payload.snapshot_field_id)) return false;

      const [value, checklist] = await Promise.all([
        databaseService.readItem<Record<string, any>>(valuesTable, operation.itemId),
        databaseService.readItem<Record<string, any>>(checklistsTable, payload.checklist_id),
      ]);
      if (!checklist || checklist.id !== payload.checklist_id ||
          checklist.empresa_id !== snapshot.empresaId || checklist.retired_at ||
          (value && (value.id !== operation.itemId ||
            value.empresa_id !== snapshot.empresaId ||
            value.checklist_id !== payload.checklist_id ||
            (value.campo_id ?? null) !== (payload.campo_id ?? null) ||
            (value.snapshot_field_id ?? null) !== (payload.snapshot_field_id ?? null)))) return false;
      addEvidence(checklist.empresa_id);
      addEvidence(value?.empresa_id);
    } else if (operation.user_id !== snapshot.userId) {
      const relayableOperations = ['update', 'delete', 'checklist_partial_update', 'checklist_delete'];
      if (!relayableOperations.includes(operation.operation) ||
          ![checklistsTable, valuesTable].includes(operation.collection)) return false;

      if (operation.collection === checklistsTable) {
        const checklist = await databaseService.readItem<Record<string, any>>(checklistsTable, operation.itemId);
        if (checklist) {
          if (checklist.id !== operation.itemId || checklist.empresa_id !== snapshot.empresaId ||
              (payload?.id !== undefined && payload.id !== operation.itemId)) return false;
          addEvidence(checklist.empresa_id);
        } else if (!['delete', 'checklist_delete'].includes(operation.operation) ||
            payload?.id !== operation.itemId || payload.empresa_id !== snapshot.empresaId ||
            !payload.template_id || !payload.usuario_id) return false;
      } else {
        const partial = operation.operation === 'checklist_partial_update';
        const value = partial ? null : await databaseService.readItem<Record<string, any>>(
          valuesTable,
          operation.itemId
        );
        const deletedValue = operation.operation === 'delete' && !value;
        const checklistId = partial ? operation.itemId
          : String(value?.checklist_id ?? (deletedValue ? payload?.checklist_id : '') ?? '').trim();
        if (!checklistId || (!partial && !deletedValue &&
            (value?.id !== operation.itemId || value?.empresa_id !== snapshot.empresaId ||
              (payload?.checklist_id !== undefined && payload.checklist_id !== checklistId) ||
              (payload?.campo_id !== undefined && payload.campo_id !== value?.campo_id))) ||
            (deletedValue && (payload?.id !== operation.itemId ||
              payload.empresa_id !== snapshot.empresaId ||
              (!payload.campo_id && !payload.snapshot_field_id)))) return false;
        const checklist = await databaseService.readItem<Record<string, any>>(checklistsTable, checklistId);
        if (checklist && (checklist.id !== checklistId || checklist.empresa_id !== snapshot.empresaId)) return false;
        if (!checklist && (partial || operation.operation !== 'delete' ||
            payload?.id !== operation.itemId || payload.empresa_id !== snapshot.empresaId ||
            payload.checklist_id !== checklistId ||
            (!payload.campo_id && !payload.snapshot_field_id))) return false;
        addEvidence(value?.empresa_id);
        addEvidence(checklist?.empresa_id);
      }
    }

    const relation = operation.operation === 'checklist_partial_update'
      ? await databaseService.readItem<Record<string, any>>(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
          operation.itemId
        )
      : await databaseService.readItem<Record<string, any>>(operation.collection, operation.itemId);
    addEvidence(relation?.empresa_id);
  } catch {
    // A missing/unreadable local link is not proof of tenant ownership.
    return false;
  }

  return evidence.size > 0 && [...evidence].every(companyId => companyId === snapshot.empresaId);
};

/** Consulta a fila local após o bootstrap, sem iniciar um ciclo remoto vazio. */
export const hasEligiblePendingSyncWork = async (companyId: string, userId: string): Promise<boolean> => {
  const snapshot = await capturePendingSyncSession();
  if (!snapshot || snapshot.empresaId !== companyId || snapshot.userId !== userId) return false;
  const { operations, images, deletions } = await loadLocalPendingSnapshot();
  await assertPendingSyncSessionCurrent(snapshot);
  for (const operation of operations) {
    if (await isPendingOperationEligibleForSession(operation, snapshot) &&
        !await isChecklistCancellationBlocked(companyId, operation)) return true;
  }
  for (const pending of [...images, ...deletions]) {
    const itemType = String(pending.item_type ?? pending.itemType ?? '').toUpperCase();
    if (itemType !== 'CHECKLIST') {
      if (pending.empresa_id === companyId && pending.user_id === userId) return true;
      continue;
    }
    const queuedCompanyId = typeof pending.empresa_id === 'string'
      ? pending.empresa_id.trim()
      : pending.empresa_id;
    if (queuedCompanyId === companyId) return true;
    if (queuedCompanyId === null || queuedCompanyId === undefined || queuedCompanyId === '') {
      const checklist = await databaseService.readItem<Record<string, any>>(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME, pending.item_id ?? pending.itemId
      );
      if (checklist?.empresa_id === companyId) return true;
    }
  }
  await assertPendingSyncSessionCurrent(snapshot);
  return false;
};

const serializePendingOperationData = (data: unknown): string | null => {
  if (data === null || data === undefined) {
    return null;
  }

  if (typeof data === 'string') {
    return data;
  }

  try {
    return JSON.stringify(data);
  } catch {
    return String(data);
  }
};

const sha512Hex = (value: string): string => Array.from(nacl.hash(naclUtil.decodeUTF8(value)))
  .map(byte => byte.toString(16).padStart(2, '0'))
  .join('');

const buildPendingOperationSnapshot = (operation: any): PendingOperationSnapshot | null => {
  if (!operation) {
    return null;
  }

  const itemId = typeof operation.itemId === 'string'
    ? operation.itemId
    : typeof operation.item_id === 'string'
      ? operation.item_id
      : '';

  return {
    id: String(operation.id ?? ''),
    operation: String(operation.operation ?? ''),
    collection: String(operation.collection ?? ''),
    itemId,
    data: serializePendingOperationData(operation.data),
    timestamp: typeof operation.timestamp === 'number'
      ? operation.timestamp
      : operation.timestamp !== undefined && operation.timestamp !== null
        ? Number(operation.timestamp)
        : null,
    user_id: typeof operation.user_id === 'string' ? operation.user_id : null,
    empresa_id: typeof operation.empresa_id === 'string' ? operation.empresa_id : null,
  };
};

const didPendingOperationChange = (
  initialSnapshot: PendingOperationSnapshot,
  latestSnapshot: PendingOperationSnapshot
): boolean => {
  return (
    initialSnapshot.operation !== latestSnapshot.operation ||
    initialSnapshot.collection !== latestSnapshot.collection ||
    initialSnapshot.itemId !== latestSnapshot.itemId ||
    initialSnapshot.timestamp !== latestSnapshot.timestamp ||
    initialSnapshot.data !== latestSnapshot.data ||
    initialSnapshot.user_id !== latestSnapshot.user_id ||
    initialSnapshot.empresa_id !== latestSnapshot.empresa_id
  );
};

const pendingSnapshotToExpectedRaw = (
  snapshot: PendingOperationSnapshot
): Pick<PendingOperationRaw, 'operation' | 'collection' | 'item_id' | 'data' | 'timestamp' | 'user_id' | 'empresa_id'> => ({
  operation: snapshot.operation as OperationType,
  collection: snapshot.collection,
  item_id: snapshot.itemId,
  data: snapshot.data,
  timestamp: snapshot.timestamp as number,
  user_id: snapshot.user_id,
  empresa_id: snapshot.empresa_id,
});

const shouldRemovePendingOperationAfterSuccess = async (
  operation: PendingOperation,
  logPushInfo: (message: string, ...args: any[]) => void,
  logPushWarn: (message: string, ...args: any[]) => void
): Promise<boolean> => {
  try {
    const currentPendingOperation = await databaseService.readItem<any>('pending_operations', operation.id);

    if (!currentPendingOperation) {
      logPushInfo(
        `Operação ${operation.id} já não existe mais localmente após o push; nenhuma remoção adicional necessária`
      );
      return false;
    }

    const initialSnapshot = buildPendingOperationSnapshot(operation);
    const latestSnapshot = buildPendingOperationSnapshot(currentPendingOperation);

    if (!initialSnapshot || !latestSnapshot) {
      logPushWarn(
        `Não foi possível montar snapshot completo da operação ${operation.id}; mantendo pendência por segurança`
      );
      return false;
    }

    // Recarrega a pendência antes do delete para não perder dados mesclados
    // enquanto um snapshot mais antigo dessa mesma operação ainda estava em voo.
    if (didPendingOperationChange(initialSnapshot, latestSnapshot)) {
      logPushWarn(
        `Operação ${operation.id} mudou durante o push (ts inicial=${initialSnapshot.timestamp ?? 'n/a'} | ts atual=${latestSnapshot.timestamp ?? 'n/a'}). Mantendo pendência para reenviar o delta mais novo.`
      );
      return false;
    }

    logPushInfo(
      `Operação ${operation.id} confirmada sem mudanças durante o push; remoção local autorizada`
    );
    return true;
  } catch (error) {
    logPushWarn(
      `Falha ao validar se a operação ${operation.id} mudou durante o push. Mantendo pendência por segurança:`,
      error
    );
    return false;
  }
};

const getDefaultOrderColumn = (tableConfig: any): string | null => {
  const columns: Array<{ name: string }> = tableConfig?.COLUMNS ?? [];
  const columnNames = columns.map((column) => column.name);

  if (columnNames.includes('updated_at')) {
    return 'updated_at';
  }

  if (columnNames.includes('created_at')) {
    return 'created_at';
  }

  return null;
};

const getSyncSentryBaseContext = (extra: Record<string, unknown> = {}): Record<string, unknown> => ({
  userId: globalState.user.user.get()?.id || null,
  empresaId:
    globalState.user.profile.get()?.empresa_id ||
    globalState.company.data.get()?.id ||
    null,
  networkConnected: globalState.network.isConnected.get(),
  databaseInitialized: globalState.database.isInitialized.get(),
  sqliteOperations: getActiveSyncSQLiteFailures(),
  ...extra,
});

type NormalizedSyncTableError = {
  message: string;
  details: string | null;
  hint: string | null;
  code: string | null;
  signature: string;
  original: unknown;
};

const normalizeSyncTableError = (error: unknown): NormalizedSyncTableError => {
  if (error instanceof Error) {
    const details = error.stack || null;
    return {
      message: error.message || error.name || 'Erro desconhecido',
      details,
      hint: null,
      code: null,
      signature: `${error.name || 'Error'}|${error.message || 'unknown'}`,
      original: error,
    };
  }

  if (error && typeof error === 'object') {
    const maybeError = error as Record<string, unknown>;
    const message = typeof maybeError.message === 'string' && maybeError.message.trim().length
      ? maybeError.message.trim()
      : 'Erro desconhecido';
    const details = typeof maybeError.details === 'string' && maybeError.details.trim().length
      ? maybeError.details.trim()
      : null;
    const hint = typeof maybeError.hint === 'string' && maybeError.hint.trim().length
      ? maybeError.hint.trim()
      : null;
    const code = typeof maybeError.code === 'string' && maybeError.code.trim().length
      ? maybeError.code.trim()
      : null;

    return {
      message,
      details,
      hint,
      code,
      signature: `${message}|${code || 'no_code'}|${hint || 'no_hint'}`,
      original: error,
    };
  }

  const fallback = String(error);
  return {
    message: fallback || 'Erro desconhecido',
    details: null,
    hint: null,
    code: null,
    signature: `${fallback || 'unknown'}|no_code|no_hint`,
    original: error,
  };
};

const buildSyncTableErrorSummary = (error: NormalizedSyncTableError): string => {
  const pieces: string[] = [error.message];
  if (error.code) {
    pieces.push(`code=${error.code}`);
  }
  if (error.hint) {
    pieces.push(`hint=${error.hint}`);
  }
  return pieces.join(' | ');
};

const isNetworkFailureError = (error: NormalizedSyncTableError): boolean => {
  const combined = [
    error.message,
    error.details || '',
    error.hint || '',
    error.code || '',
  ]
    .join(' ')
    .toLowerCase();

  const networkSignatures = [
    'failed to fetch',
    'network request failed',
    'networkerror',
    'load failed',
    'timeout de rede',
    'timed out',
    'econnreset',
    'enotfound',
    'the network connection was lost',
  ];

  return networkSignatures.some(signature => combined.includes(signature));
};

/**
 * Verifica se todos os pré-requisitos para sincronização estão atendidos
 * 
 * @returns Objeto com o status de cada pré-requisito e uma flag indicando se todos estão atendidos
 */
export const checkSyncPrerequisites = (): SyncPrerequisites => {
  //SyncLogger.Prerequisites.info('Verificando pré-requisitos para sincronização');

  const userAuthenticated = globalState.user.isAuthenticated.get();
  const userExistsInDatabase = globalState.user.existsInDatabase.get();
  const networkConnected = globalState.network.isConnected.get();
  const databaseInitialized = globalState.database.isInitialized.get();

  // Todos os pré-requisitos devem ser verdadeiros
  const allMet =
    !IS_CHECKLIST_RECOVERY_DIAGNOSTIC &&
    userAuthenticated &&
    userExistsInDatabase &&
    networkConnected &&
    databaseInitialized;

  const prerequisites: SyncPrerequisites = {
    userAuthenticated,
    userExistsInDatabase,
    networkConnected,
    databaseInitialized,
    allMet
  };

  // Log detalhado do status de cada pré-requisito
  SyncLogger.Prerequisites.info('Pré-requisitos para sincronização:', prerequisites);

  // Atualizar o estado global com o status dos pré-requisitos utilizando a syncActions
  if (globalState.sync.prerequisitesMet.get() !== allMet) {
    SyncLogger.Prerequisites.info(`Status dos pré-requisitos alterado para: ${allMet ? 'atendidos' : 'não atendidos'}`);
    syncActions.setPrerequisitesMet(allMet);
  }

  return prerequisites;
};

/**
 * Inicializa o serviço de sincronização
 * Esta função deve ser chamada quando o usuário estiver autenticado e existir no banco de dados
 */
export const initializeSyncService = () => {
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    SyncLogger.Initialize.warn('[RECOVERY] Servico de sincronizacao bloqueado pela quarentena');
    return false;
  }

  if (isRecoveryRuntimeBlocked()) {
    SyncLogger.Initialize.warn('[RECOVERY] Servico de sincronizacao bloqueado pelo gate runtime');
    return false;
  }

  SyncLogger.Initialize.info('Inicializando serviço de sincronização');

  // Verificar pré-requisitos
  const prerequisites = checkSyncPrerequisites();

  if (!prerequisites.allMet) {
    SyncLogger.Initialize.warn('Não foi possível inicializar o serviço de sincronização: pré-requisitos não atendidos');
    return false;
  }

  // Reseta os contadores de tentativas
  syncActions.resetAttempts();

  // Configura o estado inicial usando as actions
  if (!hasSyncExecution()) syncActions.setSyncRunning(false);
  syncActions.setSyncStatus('idle');
  syncActions.updateProgress('idle', 0, 0);

  SyncLogger.Initialize.info('Serviço de sincronização inicializado com sucesso');
  return true;
};

/**
 * Desativa o serviço de sincronização
 * Esta função deve ser chamada durante o logout ou quando o usuário não atender mais aos pré-requisitos
 */
export const shutdownSyncService = () => {
  SyncLogger.Shutdown.info('Desativando serviço de sincronização');

  void import('./syncScheduler')
    .then(module => {
      module.stopPeriodicSync();
    })
    .catch(error => {
      SyncLogger.Shutdown.warn('Falha ao parar scheduler automático durante shutdown:', error);
    });

  // Limpa o estado usando as actions ao invés de alteração direta
  if (!hasSyncExecution()) syncActions.setSyncRunning(false);
  syncActions.setPrerequisitesMet(false);
  syncActions.updateProgress('idle', 0, 0);

  SyncLogger.Shutdown.info('Serviço de sincronização desativado');
};

/**
 * Monitora alterações nos pré-requisitos de sincronização
 * Esta função pode ser chamada periodicamente ou em resposta a eventos do sistema
 */
export const monitorSyncPrerequisites = () => {
  SyncLogger.Monitor.info('Monitorando pré-requisitos de sincronização');

  const prerequisites = checkSyncPrerequisites();

  // Se os pré-requisitos não estiverem mais atendidos, desativa o serviço
  if (!prerequisites.allMet && globalState.sync.isRunning.get()) {
    SyncLogger.Monitor.warn('Pré-requisitos não atendidos mais. Desativando sincronização.');
    shutdownSyncService();
    return false;
  }

  return prerequisites.allMet;
};

/**
 * Obtém o timestamp da última sincronização
 * 
 * @returns O timestamp da última sincronização ou null se nunca sincronizado
 */
export const getLastSyncTimestamp = (scope?: LastSyncScope): number | null => {
  try {
    const resolvedScope = resolveLastSyncScope(scope);
    if (!resolvedScope) return null;

    const key = getLastSyncTimestampKey(resolvedScope);
    if (Platform.OS === 'web') {
      const timestamp = localStorage.getItem(key);
      return timestamp ? parseInt(timestamp, 10) : null;
    } else {
      const timestamp = syncStorage.getString(key);
      return timestamp ? parseInt(timestamp, 10) : null;
    }
  } catch (error) {
    SyncLogger.Error.error('Erro ao obter timestamp da última sincronização:', error);
    return null;
  }
};

/**
 * Salva o timestamp da última sincronização
 * 
 * @param timestamp O timestamp a ser salvo (opcional, usa o timestamp atual se não fornecido)
 */
export const saveLastSyncTimestamp = (timestamp?: number, scope?: LastSyncScope): void => {
  try {
    const resolvedScope = resolveLastSyncScope(scope);
    if (!resolvedScope) {
      SyncLogger.Status.warn('Timestamp de sincronização não salvo: escopo de empresa/usuário ausente');
      return;
    }

    const key = getLastSyncTimestampKey(resolvedScope);

    if (timestamp === 0) {
      if (Platform.OS === 'web') {
        localStorage.removeItem(key);
      } else {
        syncStorage.delete(key);
      }
      SyncLogger.Status.info('Cursor de sincronização removido para forçar pull completo');
      syncActions.updateSyncDate();
      databaseActions.updateSyncDate();
      return;
    }

    const requestedTimestamp = timestamp ?? Date.now();
    const currentTimestamp = getLastSyncTimestamp(resolvedScope);
    const timeToSave = Math.max(currentTimestamp ?? 0, requestedTimestamp);

    // Salvar no storage apropriado para a plataforma
    if (Platform.OS === 'web') {
      localStorage.setItem(key, timeToSave.toString());
    } else {
      syncStorage.set(key, timeToSave.toString());
    }

    SyncLogger.Status.info(`Timestamp de sincronização salvo: ${new Date(timeToSave).toISOString()}`);

    // Atualizar o estado global
    syncActions.updateSyncDate();
    databaseActions.updateSyncDate();
  } catch (error) {
    SyncLogger.Error.error('Erro ao salvar timestamp de sincronização:', error);
  }
};

export const isInitialBootstrapComplete = (scope?: LastSyncScope): boolean => {
  const resolvedScope = resolveLastSyncScope(scope);
  if (!resolvedScope) return false;
  const key = getInitialBootstrapCompleteKey(resolvedScope);
  return Platform.OS === 'web'
    ? localStorage.getItem(key) === 'true'
    : syncStorage.getBoolean(key) === true;
};

export const markInitialBootstrapComplete = (scope?: LastSyncScope): void => {
  const resolvedScope = resolveLastSyncScope(scope);
  if (!resolvedScope) return;
  const key = getInitialBootstrapCompleteKey(resolvedScope);
  if (Platform.OS === 'web') {
    localStorage.setItem(key, 'true');
  } else {
    syncStorage.set(key, true);
  }
};

/**
 * Normaliza os campos de data em um objeto para formato ISO string
 * para garantir compatibilidade com o Supabase (PostgreSQL)
 * 
 * @param data Objeto contendo dados a serem enviados para o Supabase
 * @returns O mesmo objeto com campos de data normalizados para ISO string
 */
const normalizeDateFieldsForSupabase = (data: any): any => {
  if (!data || typeof data !== 'object') return data;

  const result = { ...data };

  // Lista de campos que são conhecidos por conter datas
  const dateFields = ['created_at', 'updated_at', 'expires_at', 'payment_date',
    'start_date', 'end_date', 'last_sync_at', 'last_sign_in_at', 'data_criacao', 'data_conclusao'];

  const logNormalization = (message: string, ...args: any[]) => {
    if (!APP_CONSTANTS.DEBUG.ENABLED) {
      return;
    }

    SyncLogger.Main.verbose(`[normalize-date] ${message}`, ...args);
  };

  for (const key of Object.keys(result)) {
    // Se o campo está na lista de campos de data conhecidos
    if (dateFields.includes(key)) {
      const value = result[key];

      // Se for um número (timestamp em milissegundos)
      if (typeof value === 'number') {
        try {
          const isoValue = new Date(value).toISOString();
          result[key] = isoValue;
          logNormalization(`Campo ${key} convertido de número para ISO string`, { original: value, isoValue });
        } catch (error) {
          SyncLogger.Error.warn(`Erro ao converter timestamp para ISO string (${key}: ${value}):`, error);
        }
      }
      // Se for uma string que parece ser um número
      else if (typeof value === 'string' && /^\d+$/.test(value)) {
        try {
          const parsedValue = parseInt(value, 10);
          const isoValue = new Date(parsedValue).toISOString();
          result[key] = isoValue;
          logNormalization(`Campo ${key} convertido de string numérica para ISO string`, { original: value, parsedValue, isoValue });
        } catch (error) {
          SyncLogger.Error.warn(`Erro ao converter string numérica para ISO string (${key}: ${value}):`, error);
        }
      }
      // Se for uma string não-numérica, verificar se é uma string ISO válida
      else if (typeof value === 'string' && !/^\d+$/.test(value)) {
        try {
          // Verificar se é uma data válida
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            // Se for válida, garantir que está no formato ISO
            const isoValue = date.toISOString();
            result[key] = isoValue;
            logNormalization(`Campo ${key} ajustado para ISO string`, { original: value, isoValue });
          }
        } catch (error) {
          // Se não for possível converter, manter o valor original
          SyncLogger.Error.warn(`Não foi possível validar a string de data (${key}: ${value}):`, error);
        }
      }
    }
  }

  return result;
};

const isChecklistRelayReceiptValid = (
  response: unknown,
  operation: CreateOp,
  checklist: Record<string, unknown>,
  companyId: string,
): boolean => {
  if (!response || typeof response !== 'object' || Array.isArray(response)) return false;
  const record = response as Record<string, any>;
  const receipt = record.receipt;
  const returnedChecklist = record.checklist;
  const claimedOriginUserId = operation.user_id ?? null;
  return Boolean(
    returnedChecklist && typeof returnedChecklist === 'object' &&
    returnedChecklist.id === operation.itemId &&
    returnedChecklist.empresa_id === companyId &&
    returnedChecklist.usuario_id === checklist.usuario_id &&
    returnedChecklist.template_id === checklist.template_id &&
    receipt && typeof receipt === 'object' &&
    receipt.operation_id === operation.id &&
    receipt.checklist_id === operation.itemId &&
    receipt.company_id === companyId &&
    receipt.claimed_origin_user_id === claimedOriginUserId &&
    receipt.origin_proof_kind === 'client_claim_unverified' &&
    typeof receipt.transmitted_by === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(receipt.transmitted_by) &&
    typeof receipt.payload_sha512 === 'string' && /^[0-9a-f]{128}$/i.test(receipt.payload_sha512) &&
    typeof receipt.received_at === 'string' && Number.isFinite(Date.parse(receipt.received_at))
  );
};

const processChecklistSnapshotCreate = async (
  operation: CreateOp,
  checklist: Record<string, unknown>
): Promise<boolean | null> => new ChecklistSnapshotRemoteCreateService({
  database: databaseService,
  tables: {
    checklists: APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
    templateSchemaVersions: APP_CONSTANTS.DATABASE.TABLES.GUINCHO_TEMPLATE_SCHEMA_VERSIONS.NAME,
    snapshots: APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOTS.NAME,
    snapshotFields: APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME,
  },
  getClientBuild: getSyncClientBuild,
  isFeatureEnabled: input => syncCanaryFeatureService.isEnabled(
    input.companyId,
    input.featureKey,
    input.clientBuild
  ),
  async callRpc(input) {
    await assertChecklistNotCancelled(String(input.checklist.empresa_id), operation.itemId);
    const snapshot = activePendingSyncExecution?.snapshot;
    const relay = Boolean(snapshot && getSyncClientBuild() >= 124 &&
      operation.collection === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME);
    if (relay && !snapshot) throw new PendingSyncSessionChangedError();
    await assertPendingSyncSessionCurrent(snapshot!);
    const { data, error } = await getSyncSupabaseClient().rpc(relay
      ? 'transmit_checklist_create_v1'
      : 'create_checklist_with_snapshot_v1', {
      p_checklist: input.checklist,
      p_snapshot_id: input.snapshotId,
      p_fields: input.fields,
      p_client_build: input.clientBuild,
      ...(relay ? {
        p_operation_id: operation.id,
        p_claimed_origin_user_id: operation.user_id ?? null,
      } : {}),
    });
    if (error) throw error;
    await assertPendingSyncSessionCurrent(snapshot!);
    if (relay && !isChecklistRelayReceiptValid(
      data,
      operation,
      input.checklist,
      String(input.checklist.empresa_id ?? ''),
    )) throw new Error('Recibo de transmissão do checklist inválido; CREATE mantido na fila.');
    return data;
  },
  normalizeChecklist: normalizeDateFieldsForSupabase,
}).process({ itemId: operation.itemId, checklist });

/**
 * Obtém dados remotos do Supabase e atualiza o banco de dados local
 * 
 * @param options Opções de sincronização
 * @returns Promise que resolve para um objeto com status e detalhes da operação
 */
const TOMBSTONE_FEED_PAGE_LIMIT = 500;

const parseFeedInteger = (value: unknown, field: string, allowNull = false): number | null => {
  if (allowNull && value === null) return null;
  if (typeof value !== 'number' && typeof value !== 'string') {
    throw new Error(`Campo ${field} invalido no feed de tombstones.`);
  }
  if (typeof value === 'string' && !/^\d+$/.test(value)) {
    throw new Error(`Campo ${field} invalido no feed de tombstones.`);
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < 0) {
    throw new Error(`Campo ${field} invalido no feed de tombstones.`);
  }
  return parsed;
};

const parseFeedCursor = (value: unknown, field: string): string | null => {
  if (value === null) return null;
  if (typeof value !== 'string' || value.trim().length === 0 || value !== value.trim()) {
    throw new Error(`Campo ${field} invalido no feed de tombstones.`);
  }
  return value;
};

const parseTombstoneFeedPage = (
  requestCursor: string | null,
  data: unknown
): TombstoneFeedPage => {
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error('Resposta do feed v2 de tombstones ausente ou invalida.');
  }

  const rows = data as Array<Record<string, unknown>>;
  if (rows.some(row => !row || typeof row !== 'object' || Array.isArray(row))) {
    throw new Error('Resposta do feed v2 de tombstones contem linha invalida.');
  }
  const firstTarget = parseFeedInteger(rows[0]?.target_high_watermark, 'target_high_watermark');
  const firstNextCursor = parseFeedCursor(rows[0]?.next_cursor, 'next_cursor');
  const firstHasMore = rows[0]?.has_more;
  if (typeof firstHasMore !== 'boolean') {
    throw new Error('Campo has_more invalido no feed de tombstones.');
  }

  const isEnvelope = rows[0]?.sequence === null;
  if (isEnvelope && (rows.length !== 1 || firstNextCursor !== null || firstHasMore)) {
    throw new Error('Envelope vazio do feed de tombstones invalido.');
  }

  const parsedRows: RemoteTombstoneRow[] = [];
  for (const row of rows) {
    const target = parseFeedInteger(row.target_high_watermark, 'target_high_watermark');
    const nextCursor = parseFeedCursor(row.next_cursor, 'next_cursor');
    const hasMore = row.has_more;
    if (
      target !== firstTarget ||
      nextCursor !== firstNextCursor ||
      hasMore !== firstHasMore
    ) {
      throw new Error('Metadados divergentes entre linhas do feed de tombstones.');
    }

    if (row.sequence === null) {
      if (
        !isEnvelope ||
        row.event_id !== null ||
        row.entity_type !== null ||
        row.entity_id !== null ||
        row.parent_id !== null ||
        row.deletion_kind !== null ||
        row.deleted_at !== null ||
        typeof row.metadata !== 'object' ||
        row.metadata === null ||
        Array.isArray(row.metadata) ||
        Object.keys(row.metadata).length > 0
      ) {
        throw new Error('Envelope vazio do feed de tombstones contem dados de evento.');
      }
      continue;
    }

    const sequence = parseFeedInteger(row.sequence, 'sequence');
    if (sequence === null) {
      throw new Error('Sequence ausente no feed de tombstones.');
    }
    if (typeof row.event_id !== 'string' || !row.event_id.trim()) {
      throw new Error('event_id ausente no feed de tombstones.');
    }
    if (typeof row.entity_type !== 'string' || !row.entity_type.trim()) {
      throw new Error('entity_type ausente no feed de tombstones.');
    }
    if (typeof row.entity_id !== 'string' || !row.entity_id.trim()) {
      throw new Error('entity_id ausente no feed de tombstones.');
    }
    if (row.deletion_kind !== 'delete' && row.deletion_kind !== 'retire') {
      throw new Error('deletion_kind invalido no feed de tombstones.');
    }
    if (typeof row.deleted_at !== 'string' || Number.isNaN(Date.parse(row.deleted_at))) {
      throw new Error('deleted_at invalido no feed de tombstones.');
    }
    if (
      row.parent_id !== null &&
      (typeof row.parent_id !== 'string' || !row.parent_id.trim())
    ) {
      throw new Error('parent_id invalido no feed de tombstones.');
    }
    if (
      row.metadata !== null &&
      (typeof row.metadata !== 'object' || Array.isArray(row.metadata))
    ) {
      throw new Error('metadata invalido no feed de tombstones.');
    }

    parsedRows.push({
      sequence,
      event_id: row.event_id,
      entity_type: row.entity_type,
      entity_id: row.entity_id,
      parent_id: row.parent_id === null ? null : String(row.parent_id ?? ''),
      deletion_kind: row.deletion_kind,
      metadata: row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
        ? row.metadata as Record<string, unknown>
        : null,
      deleted_at: row.deleted_at,
    });
  }

  if (!isEnvelope && parsedRows.length === 0) {
    throw new Error('Feed de tombstones sem linhas de evento validas.');
  }
  if (firstHasMore && firstNextCursor === null) {
    throw new Error('Feed de tombstones declarou continuidade sem next_cursor.');
  }
  if (!firstHasMore && parsedRows.length > 0 && firstNextCursor === null) {
    throw new Error('Página final de tombstones sem next_cursor de checkpoint.');
  }

  return {
    cursor: requestCursor,
    targetHighWatermark: firstTarget as number,
    nextCursor: firstNextCursor,
    hasMore: firstHasMore,
    rows: parsedRows,
  };
};

type TombstonePreflightMode = 'pull-only' | 'remote-write' | 'coordinated-write';

type TombstonePreflightResult = {
  enabled: boolean;
  applied: number;
  duplicates: number;
  barriersByTable: Map<string, Set<string>>;
};

const pullCanaryTombstones = async (input: {
  companyId: string;
  userId: string;
  logInfo: (message: string, ...args: any[]) => void;
  mode: TombstonePreflightMode;
}): Promise<TombstonePreflightResult> => {
  const clientBuild = getSyncClientBuild();
  let featureLookupFailed = false;
  const enabled = input.mode !== 'remote-write'
    ? await syncCanaryFeatureService.isEnabledOrFalse(
        input.companyId,
        SYNC_TOMBSTONES_FEATURE,
        clientBuild,
        () => {
          featureLookupFailed = true;
        }
      )
    : await syncCanaryFeatureService.isEnabled(
        input.companyId,
        SYNC_TOMBSTONES_FEATURE,
        clientBuild
      );

  if (!enabled) {
    if (input.mode === 'remote-write') {
      throw new Error('Feed de tombstones indisponivel para esta empresa/build.');
    }
    if (input.mode === 'coordinated-write' && featureLookupFailed) {
      throw new Error('Feed de tombstones indisponivel para esta empresa/build.');
    }

    const reason = featureLookupFailed ? 'indisponivel' : 'desligada';
    input.logInfo(
      `Feature de tombstones ${reason}; pull normal continua sem barreiras de tombstone ate a habilitacao.`
    );
    return {
      enabled: false,
      applied: 0,
      duplicates: 0,
      barriersByTable: new Map(),
    };
  }

  const tombstoneService = new TombstoneSyncService(
    {
      async fetchPage(request) {
        const { data, error } = await getSyncSupabaseClient().rpc('get_sync_tombstones_v2', {
          p_cursor: request.cursor,
          p_limit: Math.min(TOMBSTONE_FEED_PAGE_LIMIT, Math.max(1, request.limit)),
          p_client_build: request.clientBuild,
        });
        if (
          error?.code === '22023' &&
          error?.message === 'cursor de tombstones invalido'
        ) {
          throw new TombstoneCursorReplayRequiredError();
        }
        if (error) throw error;
        return parseTombstoneFeedPage(request.cursor, data);
      },
    },
    {
      getCursor: id => databaseService.readItem<LocalTombstoneCursor>('sync_cursors', id),
      async saveCursor(cursor) {
        const existing = await databaseService.readItem<LocalTombstoneCursor>('sync_cursors', cursor.id);
        if (existing) {
          if (!await databaseService.updateItem('sync_cursors', cursor.id, cursor, {
            skipPendingOperation: true,
            source: 'remote',
          })) {
            throw new Error('Falha ao atualizar cursor local de tombstones.');
          }
        } else {
          const createdId = await databaseService.createItem('sync_cursors', cursor, {
            skipPendingOperation: true,
            source: 'remote',
          });
          if (!createdId) throw new Error('Falha ao persistir cursor local de tombstones.');
        }
      },
      listTombstones: companyId => databaseService.listItems<LocalTombstoneRow>(
        'sync_tombstones',
        { empresa_id: companyId }
      ),
      getTombstone: id => databaseService.readItem<LocalTombstoneRow>('sync_tombstones', id),
      async saveTombstone(row) {
        const createdId = await databaseService.createItem('sync_tombstones', row, {
          skipPendingOperation: true,
          source: 'remote',
        });
        if (!createdId) throw new Error('Falha ao persistir tombstone local.');
      },
      readTarget: (table, id) => databaseService.readItem<Record<string, unknown>>(table, id),
      retireTarget: (table, id, deletedAt) => databaseService.updateItem(
        table,
        id,
        { retired_at: deletedAt },
        { skipPendingOperation: true, source: 'remote' }
      ),
      retireTargetIfMatches: (table, id, deletedAt, expected) => databaseService.updateItemIfUnchanged(
        table,
        id,
        { retired_at: deletedAt },
        expected,
        true
      ),
      deleteTarget: (table, id) => databaseService.deleteItem(
        table,
        id,
        { skipPendingOperation: true, source: 'remote' }
      ),
      deleteTargetIfMatches: (table, id, expected) => databaseService.deleteItemIfUnchanged(
        table,
        id,
        expected,
        true,
        () => Boolean(activePendingSyncExecution && isPendingSyncSessionSnapshotStateCurrent(activePendingSyncExecution.snapshot))
      ),
    }
  );

  const result = await tombstoneService.pull({
    companyId: input.companyId,
    userId: input.userId,
    clientBuild,
  });
  // O envelope v2 mantém targetHighWatermark e next_cursor opaco no ciclo.
  input.logInfo(
    `Preflight de tombstones concluido | novos=${result.applied} | duplicados=${result.duplicates} | target=${result.targetHighWatermark ?? 'n/a'} | has_more=${result.hasMore}`
  );
  return {
    enabled: true,
    applied: result.applied,
    duplicates: result.duplicates,
    barriersByTable: result.barriersByTable,
  };
};

const getCurrentSyncCompanyId = (): string | null => {
  const profileCompanyId = globalState.user.profile.get()?.empresa_id;
  const stateCompanyId = globalState.company.data.get()?.id;
  const companyId = profileCompanyId || stateCompanyId;
  return typeof companyId === 'string' && companyId.trim().length > 0
    ? companyId.trim()
    : null;
};

export const refreshStructuralSyncFeatureDecisions = async (): Promise<Record<string, boolean | null>> => {
  let companyId = getCurrentSyncCompanyId();
  if (!companyId && globalState.user.isAuthenticated.get()) {
    const userId = globalState.user.user.get()?.id?.trim();
    if (userId) {
      await getUserProfile(userId);
      companyId = getCurrentSyncCompanyId();
    }
  }
  if (!companyId) {
    throw new Error('Empresa local ausente para atualizar decisoes estruturais de sync.');
  }
  return syncCanaryFeatureService.refresh(
    companyId,
    STRUCTURAL_SYNC_FEATURES,
    getSyncClientBuild()
  );
};

export const getChecklistValuesV2FeatureDecision = (companyId: string): boolean | null =>
  syncCanaryFeatureService.getPersistedDecision(
    companyId,
    CHECKLIST_VALUES_V2_FEATURE,
    getSyncClientBuild()
  );

export const refreshChecklistValuesV2FeatureDecision = (companyId: string): Promise<boolean> =>
  syncCanaryFeatureService.isEnabled(
    companyId,
    CHECKLIST_VALUES_V2_FEATURE,
    getSyncClientBuild()
  );

const requireTombstonePreflightScope = (): { companyId: string; userId: string } => {
  const userId = globalState.user.user.get()?.id?.trim();
  const companyId = getCurrentSyncCompanyId();
  if (!userId || !companyId) {
    throw new Error('Escopo local ausente para TOMBSTONE_PREFLIGHT.');
  }
  return { companyId, userId };
};

export const runTombstonePreflightForRemoteWrites = async (
  logInfo: (message: string, ...args: any[]) => void = (...args) => SyncLogger.Main.info(...args)
): Promise<Map<string, Set<string>>> => {
  if (isRecoveryRuntimeBlocked()) {
    throw new Error('Escrita remota CHECKLIST bloqueada pelo gate de recuperacao.');
  }
  const { companyId, userId } = requireTombstonePreflightScope();
  logInfo('TOMBSTONE_PREFLIGHT iniciado antes de escrita remota direta');
  const preflight = await pullCanaryTombstones({
    companyId,
    userId,
    logInfo,
    mode: 'remote-write',
  });
  logInfo('TOMBSTONE_PREFLIGHT concluído antes de escrita remota direta');
  return preflight.barriersByTable;
};

const loadLocalPullProtection = async (allowMissingParentMetadata = false): Promise<LocalPullProtection> =>
  buildLocalPullProtection(await loadLocalPendingSnapshot({ rawOperations: true, includeConflicts: true }), allowMissingParentMetadata);

type TombstonePushDecision = 'allow' | 'blocked' | 'uncertain';

const hasTombstoneRowsForTable = (
  barriers: ReadonlyMap<string, ReadonlySet<string>>,
  table: string
): boolean => (barriers.get(table)?.size ?? 0) > 0;

const readLocalSyncRelation = async (
  table: string,
  id: string
): Promise<Record<string, unknown> | null> => {
  try {
    const row = await databaseService.readItem<Record<string, unknown>>(table, id);
    return row && typeof row === 'object' && !Array.isArray(row) ? row : null;
  } catch {
    return null;
  }
};

const operationRelationId = (
  data: Record<string, unknown> | null,
  snakeCase: string,
  camelCase: string
): string => String(data?.[snakeCase] ?? data?.[camelCase] ?? '').trim();

const resolvePendingOperationTombstoneDecision = async (
  operation: PendingOperation,
  barriers: Map<string, Set<string>> | undefined
): Promise<TombstonePushDecision> => {
  if (!barriers || barriers.size === 0) {
    return 'allow';
  }

  const collection = String(operation.collection ?? '').trim();
  const itemId = String(operation.itemId ?? '').trim();
  if (!collection || !itemId) {
    return 'allow';
  }

  const isPartialChecklistUpdate = operation.operation === 'checklist_partial_update';
  const templatesTable = APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME;
  const fieldsTable = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME;
  const checklistsTable = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME;
  const valuesTable = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME;
  const imagesTable = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME;

  if (
    !(collection === valuesTable && isPartialChecklistUpdate) &&
    isTombstoneBarrierActive(barriers, collection, itemId)
  ) {
    return 'blocked';
  }

  const operationData = parsePullProtectionData(operation.data);
  const templateTombstoneExists = hasTombstoneRowsForTable(barriers, templatesTable);
  const checklistTombstoneExists = hasTombstoneRowsForTable(barriers, checklistsTable);

  const operationTemplateId = operationRelationId(operationData, 'template_id', 'templateId');
  const operationCampoId = operationRelationId(operationData, 'campo_id', 'campoId');
  const operationChecklistId = operationRelationId(
    operationData,
    'checklist_id',
    'checklistId'
  );

  if (
    operationTemplateId &&
    isTombstoneBarrierActive(barriers, templatesTable, operationTemplateId)
  ) {
    return 'blocked';
  }

  if (
    operationCampoId &&
    isTombstoneBarrierActive(barriers, fieldsTable, operationCampoId)
  ) {
    return 'blocked';
  }

  if (
    operationChecklistId &&
    isTombstoneBarrierActive(barriers, checklistsTable, operationChecklistId)
  ) {
    return 'blocked';
  }

  if (
    collection === checklistsTable ||
    collection === fieldsTable
  ) {
    if (operationTemplateId) return 'allow';
    if (!templateTombstoneExists) return 'allow';

    const localRow = await readLocalSyncRelation(collection, itemId);
    const localTemplateId = String(localRow?.template_id ?? '').trim();
    if (!localTemplateId) return 'uncertain';
    return isTombstoneBarrierActive(barriers, templatesTable, localTemplateId)
      ? 'blocked'
      : 'allow';
  }

  if (collection === imagesTable) {
    if (templateTombstoneExists && !operationTemplateId) {
      const localImage = await readLocalSyncRelation(collection, itemId);
      const localTemplateId = String(localImage?.template_id ?? '').trim();
      if (!localTemplateId) return 'uncertain';
      if (isTombstoneBarrierActive(barriers, templatesTable, localTemplateId)) {
        return 'blocked';
      }
    }
    return 'allow';
  }

  if (collection !== valuesTable) {
    return 'allow';
  }

  const localValue = isPartialChecklistUpdate
    ? null
    : await readLocalSyncRelation(collection, itemId);
  const checklistId = operationChecklistId || (
    isPartialChecklistUpdate ? itemId : String(localValue?.checklist_id ?? '').trim()
  );

  if (
    !checklistId &&
    (templateTombstoneExists || checklistTombstoneExists)
  ) {
    return 'uncertain';
  }

  if (checklistId && isTombstoneBarrierActive(barriers, checklistsTable, checklistId)) {
    return 'blocked';
  }

  const campoIds = isPartialChecklistUpdate
    ? Object.keys(operationData ?? {}).filter(key => ![
        'checklist_id',
        'checklistId',
        'campo_id',
        'campoId',
        'template_id',
        'templateId',
      ].includes(key))
    : [operationCampoId || String(localValue?.campo_id ?? '').trim()].filter(Boolean);

  const tombstonedCampoIds = campoIds.filter(campoId =>
    isTombstoneBarrierActive(barriers, fieldsTable, campoId)
  );
  if (tombstonedCampoIds.length > 0) {
    if (!isPartialChecklistUpdate) return 'blocked';

    // O tombstone do campo precisa primeiro convergir localmente. Depois, V2
    // decide cada resposta com sua referência física/histórica; aposentadoria
    // não terminaliza o lote nem autoriza recriar uma resposta apagada.
    const localChecklist = await readLocalSyncRelation(checklistsTable, checklistId);
    const companyId = String(localChecklist?.empresa_id ?? '');
    if (!companyId || companyId !== getCurrentSyncCompanyId()) return 'uncertain';
    for (const campoId of tombstonedCampoIds) {
      const tombstones = await databaseService.listItems<LocalTombstoneRow>(
        APP_CONSTANTS.DATABASE.TABLES.SYNC_TOMBSTONES.NAME,
        { empresa_id: companyId, entity_type: fieldsTable, entity_id: campoId }
      );
      const exact = tombstones
        .filter(row => row.empresa_id === companyId && row.entity_type === fieldsTable &&
          row.entity_id === campoId && (row.deletion_kind === 'delete' || row.deletion_kind === 'retire'))
        .sort((left, right) => right.sequence - left.sequence || right.id.localeCompare(left.id))[0];
      if (!exact || !await confirmExactTombstoneAppliedToLocalTarget(exact)) return 'uncertain';
    }
    try {
      if (!await syncCanaryFeatureService.isEnabled(
        companyId,
        CHECKLIST_VALUES_V2_FEATURE,
        getSyncClientBuild()
      )) return 'uncertain';
    } catch {
      // Sem o consumidor por campo, o caminho legado poderia recriar o campo
      // aposentado ao enviar a parcial inteira. Preserve a operação para retry.
      return 'uncertain';
    }
  }

  if (!templateTombstoneExists) {
    if (!checklistId && checklistTombstoneExists) return 'uncertain';
    return 'allow';
  }

  const localChecklist = checklistId
    ? await readLocalSyncRelation(checklistsTable, checklistId)
    : null;
  const checklistTemplateId = String(
    operationTemplateId || localChecklist?.template_id || ''
  ).trim();
  if (!checklistTemplateId) return 'uncertain';
  if (isTombstoneBarrierActive(barriers, templatesTable, checklistTemplateId)) {
    return 'blocked';
  }

  for (const campoId of campoIds) {
    const localField = await readLocalSyncRelation(fieldsTable, campoId);
    if (!localField && isPartialChecklistUpdate) {
      // A fila usa o ID visual do snapshot. Campo original aposentado não invalida
      // essa referência histórica; a RPC V2 continua responsável pelo seu contrato.
      const companyId = getCurrentSyncCompanyId();
      const snapshotId = String(localChecklist?.snapshot_id ?? '').trim();
      if (!companyId || !snapshotId || localChecklist?.empresa_id !== companyId) {
        return 'uncertain';
      }
      const snapshotField = await readLocalSyncRelation(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME,
        campoId
      );
      if (snapshotField?.empresa_id !== companyId || snapshotField?.snapshot_id !== snapshotId) {
        return 'uncertain';
      }
      const snapshot = await readLocalSyncRelation(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOTS.NAME,
        snapshotId
      );
      if (snapshot?.empresa_id !== companyId || snapshot?.checklist_id !== checklistId ||
        snapshot?.template_id !== checklistTemplateId) {
        return 'uncertain';
      }
      continue;
    }
    const fieldTemplateId = String(localField?.template_id ?? '').trim();
    if (!fieldTemplateId) return 'uncertain';
    if (isTombstoneBarrierActive(barriers, templatesTable, fieldTemplateId)) {
      return 'blocked';
    }
  }

  return 'allow';
};

const isAuthorizedRecoveryPull = (context: RecoveryCanonicalPullContext): boolean => (
  IS_CHECKLIST_SYNC_RECOVERY_CANARY &&
  isRecoveryReceiptVerifiedInProcess(context.receipt, {
    identity: context.identity,
    recoveryId: context.recoveryId,
    manifestSha512: context.manifestSha512,
  })
);

const pullRemoteChangesInternal = async (
  options: SyncOptions = {},
  recoveryContext: RecoveryCanonicalPullContext | null = null
): Promise<SyncStatus> => {
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    SyncLogger.Pull.warn('[RECOVERY] Pull remoto bloqueado pela quarentena');
    return recoveryDiagnosticSyncStatus();
  }

  if (recoveryContext && !isAuthorizedRecoveryPull(recoveryContext)) {
    SyncLogger.Pull.warn('[RECOVERY] Pull canonico recusado por receipt nao verificado');
    return {
      success: false,
      message: 'Pull de recuperacao nao autorizado.',
      timestamp: new Date(),
      details: { reason: 'recovery_receipt_not_verified' },
    };
  }

  if (isRecoveryRuntimeBlocked() && !recoveryContext) {
    SyncLogger.Pull.warn('[RECOVERY] Pull remoto bloqueado pelo gate runtime');
    return recoveryGateSyncStatus();
  }

  const startTime = Date.now();
  const traceLabel = options.tracePrefix ?? '';
  const tracePrefix = traceLabel ? `${traceLabel} ` : '';
  const pullLogger = {
    info: (message: string, ...args: any[]) => SyncLogger.Pull.info(`${tracePrefix}${message}`, ...args),
    warn: (message: string, ...args: any[]) => SyncLogger.Pull.warn(`${tracePrefix}${message}`, ...args),
    error: (message: string, ...args: any[]) => SyncLogger.Pull.error(`${tracePrefix}${message}`, ...args),
  };

  // Verificar pré-requisitos
  const prerequisites = checkSyncPrerequisites();
  if (!prerequisites.allMet) {
    const errorMessage = 'Não é possível obter dados remotos: pré-requisitos não atendidos';
    reportUnrecoveredSyncFailure({ code: prerequisites.networkConnected ? 'prerequisites_not_met' : 'offline', message: errorMessage, details: JSON.stringify(prerequisites) }, 'prerequisites', prerequisites.networkConnected);
    pullLogger.warn(errorMessage);
    return {
      success: false,
      message: errorMessage,
      timestamp: new Date(),
      details: prerequisites
    };
  }

  // Atualizar estado global
  const managedBySyncAll = options.managedBySyncAll ?? false;
  const shouldManageState = !managedBySyncAll;
  const pullOverallProgress = (progress: number) => managedBySyncAll
    ? 30 + Math.floor(progress * 0.6)
    : progress;

  if (shouldManageState) {
    syncActions.setSyncRunning(true);
  }
  syncActions.setSyncStatus('idle');
  syncActions.updateProgress('pull', 0, pullOverallProgress(0));

  try {
    // Em web, navigator.onLine pode ficar "true" mesmo sem alcance real ao Supabase.
    // Aqui validamos conectividade efetiva antes de iniciar o pull.
    if (Platform.OS === 'web') {
      const hasEffectiveConnection = await checkConnection();
      if (!hasEffectiveConnection) {
        const connectivityMessage = `${tracePrefix}Conectividade com servidor indisponível no momento. Pull cancelado.`;
        pullLogger.warn(connectivityMessage);
        captureSentryMessageWithScope('Pull cancelado por conectividade indisponível', {
          level: 'warning',
          context: getSyncSentryBaseContext({
            flow: 'sync',
            stage: 'pull_connectivity_check',
            tracePrefix: traceLabel || null,
            loggerMessage: connectivityMessage,
          }),
          tags: {
            flow: 'sync',
            stage: 'pull',
            reason: 'connectivity_check_failed',
          },
          fingerprint: ['sync', 'pull_connectivity_check_failed'],
          dedupeKey: 'sync:pull_connectivity_check_failed',
          dedupeWindowMs: 30000,
        });

        if (shouldManageState) {
          syncActions.setSyncRunning(false);
        }
        syncActions.setSyncStatus('error');
        syncActions.setSyncError(connectivityMessage);

        return {
          success: false,
          message: connectivityMessage,
          timestamp: new Date(),
          details: {
            reason: 'connectivity_check_failed',
          },
        };
      }
    }

    // Obter o ID do usuário atual
    const userId = globalState.user.user.get()?.id;
    if (!userId) {
      const errorMessage = 'ID do usuário não encontrado para sincronização';
      pullLogger.error(errorMessage);
      return {
        success: false,
        message: errorMessage,
        timestamp: new Date(),
        details: { error: 'Usuário não identificado' }
      };
    }

    // Tentar obter o ID da empresa do usuário
    let empresaId: string | null = null;

    // Verificar se o app requer empresa antes de tentar obter o empresaId
    if (APP_CONSTANTS.REGISTRATION.REQUIRES_COMPANY) {
      //pullLogger.info('App requer empresa, tentando obter empresa_id do usuário');
      try {
        const userProfile = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, userId);
        if (userProfile && userProfile.empresa_id) {
          empresaId = userProfile.empresa_id;
          //pullLogger.info(`ID da empresa do usuário obtido: ${empresaId}`);
        }
      } catch (error) {
        pullLogger.warn(`Não foi possível obter a empresa do usuário: ${error}`);
        // Continuamos sem o ID da empresa
      }
    } else {
      pullLogger.info('App não requer empresa, pulando obtenção de empresa_id');
    }

    empresaId = resolveRecoveryPullCompanyId({
      authenticatedUserId: userId,
      localCompanyId: empresaId,
      context: recoveryContext,
    });

    let localPullProtection = createEmptyLocalPullProtection();

    const refreshLocalPullProtection = async (): Promise<LocalPullProtection> => {
      localPullProtection = await loadLocalPullProtection();
      return localPullProtection;
    };

    if (!empresaId) {
      throw new Error('Empresa ausente para preflight de tombstones no pull remoto.');
    }

    const syncScope = { companyId: empresaId, userId };
    const forceFullSync = options.forceFullSync || false;
    const persistedLastSyncTimestamp = getLastSyncTimestamp(syncScope);
    const lastSyncTimestamp = forceFullSync ? null : persistedLastSyncTimestamp;
    const lastSyncLabel = lastSyncTimestamp ? new Date(lastSyncTimestamp).toISOString() : 'nunca sincronizado';
    const syncModeMessage = forceFullSync || !lastSyncTimestamp
      ? 'Realizando sincronização completa de dados (full sync)'
      : `Realizando sincronização incremental desde: ${new Date(lastSyncTimestamp).toISOString()}`;

    pullLogger.info(`Iniciando obtenção de dados remotos | Último timestamp: ${lastSyncLabel} | ${syncModeMessage}`);

    const tombstonePreflight = await pullCanaryTombstones({
      companyId: empresaId,
      userId,
      logInfo: pullLogger.info,
      mode: 'pull-only',
    });
    const tombstoneBarriers = tombstonePreflight.barriersByTable;

    // Determinar tabelas a sincronizar com base na configuração
    let tablesToSync: string[] = [];
    if (options.tables && options.tables.length > 0) {
      // Se houver uma lista específica de tabelas nas opções, usar essa lista
      tablesToSync = options.tables;
      pullLogger.info(`Usando tabelas especificadas nas opções: ${tablesToSync.join(', ')}`);
    } else {
      // Caso contrário, filtrar tabelas com base na configuração
      tablesToSync = Object.entries(APP_CONSTANTS.DATABASE.TABLES)
        .filter(([_, table]) => {
          // Verificar se a tabela tem uma configuração de sincronização válida
          const syncConfig = (table as any).SYNC_CONFIG as TableSyncConfig | undefined;
          if (!syncConfig || !syncConfig.enabled) {
            return false;
          }
          // pullEnabled default true
          return syncConfig.pullEnabled !== false;
        })
        .map(([_, table]) => table.NAME);

      const runtimeClientBuild = getSyncClientBuild();
      const historicalSnapshotsEnabled = recoveryContext
        ? true
        : empresaId
          ? await syncCanaryFeatureService.isEnabledOrFalse(
              empresaId,
              HISTORICAL_SNAPSHOTS_FEATURE,
              runtimeClientBuild,
              () => pullLogger.warn(
                'Feature historica indisponivel; pull normal continua sem tabelas de snapshot'
              )
            )
          : false;

      tablesToSync = includeHistoricalTablesWhenEnabled(tablesToSync, {
        templates: APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
        templateVersions: APP_CONSTANTS.DATABASE.TABLES.GUINCHO_TEMPLATE_SCHEMA_VERSIONS.NAME,
        checklists: APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
        snapshots: APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOTS.NAME,
        snapshotFields: APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME,
      }, historicalSnapshotsEnabled);

      pullLogger.info(`Tabelas habilitadas para sincronização: ${tablesToSync.join(', ')}`);
    }

    // Variáveis para estatísticas
    let totalItemsSynced = 0;
    let tablesProcessed = 0;
    let tableErrors = 0;
    let itemErrors = 0;
    let imageErrors = 0;
    let abortRemainingTables = false;
    let firstNetworkFailure: {
      tableName: string;
      pageIndex: number;
      error: NormalizedSyncTableError;
    } | null = null;
    // Estrutura auxiliar para consolidar o resultado da sincronização por tabela
    type TableSyncSummary = {
      table: string;
      filterTimestamp: string | null;
      itemsFound: number;
      created: number;
      updated: number;
      skipped: number;
      imagesProcessed: number;
      itemErrors: number;
      imageErrors: number;
      notes: string[];
      processed: boolean;
    };
    const tableSummaries: TableSyncSummary[] = [];

    // Para cada tabela, obter os dados mais recentes
    for (const tableName of tablesToSync) {
      const tableSummary: TableSyncSummary = {
        table: tableName,
        filterTimestamp: null,
        itemsFound: 0,
        created: 0,
        updated: 0,
        skipped: 0,
        imagesProcessed: 0,
        itemErrors: 0,
        imageErrors: 0,
        notes: [],
        processed: false,
      };
      tableSummaries.push(tableSummary);

      if (abortRemainingTables) {
        tableSummary.notes.push('pull interrompido por falha de rede anterior');
        continue;
      }

      // Encontrar configuração da tabela
      const tableConfig = Object.values(APP_CONSTANTS.DATABASE.TABLES)
        .find(table => table.NAME === tableName);

      if (!tableConfig) {
        pullLogger.warn(`Tabela ${tableName} não encontrada na configuração. Pulando.`);
        tableSummary.notes.push('não encontrada na configuração');
        continue;
      }

      // Obter a configuração de sincronização
      const syncConfig = (tableConfig as any).SYNC_CONFIG as TableSyncConfig | undefined;
      const orderColumn = getDefaultOrderColumn(tableConfig);
      const hasIdColumn = tableConfig.COLUMNS.some((column: any) => column.name === 'id');
      const isChecklistProtectedPullTable = checklistProtectedPullCollections().has(tableName);

      if (lastSyncTimestamp && !forceFullSync) {
        tableSummary.filterTimestamp = new Date(lastSyncTimestamp).toISOString();
      } else {
        tableSummary.filterTimestamp = forceFullSync ? null : lastSyncTimestamp ? new Date(lastSyncTimestamp).toISOString() : null;
      }

      const buildBaseQuery = (includeCount: boolean = false, pageCursorId?: string | null) => {
        let baseQuery: any = includeCount
          ? getSyncSupabaseClient().from(tableName).select('*', { count: 'exact' })
          : getSyncSupabaseClient().from(tableName).select('*');

        if (syncConfig) {
          if (syncConfig.filterByUser && syncConfig.userField && userId) {
            baseQuery = baseQuery.eq(syncConfig.userField, userId);
          }

          if (syncConfig.filterByCompany && syncConfig.companyField) {
            const shouldFilterByCompany = APP_CONSTANTS.REGISTRATION.REQUIRES_COMPANY || syncConfig.forceCompanyFilter;

            if (shouldFilterByCompany && empresaId) {
              baseQuery = baseQuery.eq(syncConfig.companyField, empresaId);
            } else if (!shouldFilterByCompany) {
              pullLogger.info(`Ignorando filtro por empresa para ${tableName} pois o app não requer empresa`);
            } else if (!empresaId) {
              pullLogger.warn(`Não foi possível aplicar filtro por empresa para ${tableName} pois empresaId não está disponível`);
            }
          }
        }

        if (tableSummary.filterTimestamp && !forceFullSync) {
          const timestampColumns = getIncrementalTimestampColumns(tableConfig);
          const hasCreatedAt = timestampColumns.includes('created_at');
          const hasUpdatedAt = timestampColumns.includes('updated_at');

          if (hasCreatedAt && hasUpdatedAt) {
            baseQuery = baseQuery.or(
              `created_at.gt.${tableSummary.filterTimestamp},updated_at.gt.${tableSummary.filterTimestamp}`
            );
          } else if (hasUpdatedAt) {
            baseQuery = baseQuery.gt('updated_at', tableSummary.filterTimestamp);
          } else if (hasCreatedAt) {
            baseQuery = baseQuery.gt('created_at', tableSummary.filterTimestamp);
          }
        }

        if (hasIdColumn) {
          baseQuery = baseQuery.order('id', { ascending: true, nullsFirst: false });
          if (pageCursorId) {
            baseQuery = baseQuery.gt('id', pageCursorId);
          }
        } else if (orderColumn) {
          baseQuery = baseQuery.order(orderColumn, { ascending: true, nullsFirst: false });
        }

        return baseQuery;
      };

      const baseContextLabel = traceLabel ? `${traceLabel} pull:${tableName}:select` : `pull:${tableName}:select`;

      const pageSize = getSupabasePullPageSize();
      let pageIndex = 0;
      let pageCursorId: string | null = null;
      let encounteredError = false;
      let encounteredData = false;
      let tableKnownTotalRows: number | null = null;
      let tableProcessedRows = 0;
      const supportsProtectedBootstrapInsert =
        tableName === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME ||
        tableName === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME;
      const firstSyncProtectedBootstrap =
        isChecklistProtectedPullTable &&
        supportsProtectedBootstrapInsert &&
        !persistedLastSyncTimestamp;
      const protectedBootstrapInsertEnabled =
        isChecklistProtectedPullTable &&
        supportsProtectedBootstrapInsert &&
        (
          (forceFullSync && options.protectedBootstrapRepair === true) ||
          firstSyncProtectedBootstrap
        );
      const bootstrapExistingRowsById = firstSyncProtectedBootstrap
        ? new Map(
            (await databaseService.listItems<Record<string, unknown> & { id?: string }>(tableName))
              .map(row => [String(row.id ?? '').trim(), row] as const)
              .filter(([id]) => Boolean(id))
          )
        : null;

      const updatePullProgressForCurrentTable = () => {
        const completedTables = tablesProcessed;
        const currentTableRatio = tableKnownTotalRows && tableKnownTotalRows > 0
          ? Math.min(1, tableProcessedRows / tableKnownTotalRows)
          : encounteredData
            ? Math.min(0.95, Math.max(0.05, tableProcessedRows / Math.max(pageSize, tableProcessedRows + pageSize)))
            : 0;

        const stageProgress = Math.min(
          100,
          Math.floor(((completedTables + currentTableRatio) / tablesToSync.length) * 100)
        );

        syncActions.updateProgress(
          'pull',
          stageProgress,
          pullOverallProgress(stageProgress)
        );
      };

      const sanitizePullPageData = async (
        pageData: any[],
        protection: LocalPullProtection = localPullProtection
      ): Promise<any[]> => {
        const tombstoneFilter = await filterTombstonedRowsWithLocalRelations(
          tableName,
          pageData,
          tombstoneBarriers,
          async (relationTable, relationId) => databaseService.readItem<Record<string, unknown>>(
            relationTable,
            relationId
          )
        );
        if (tombstoneFilter.blocked.length > 0) {
          tableSummary.skipped += tombstoneFilter.blocked.length;
          const note = `anti-ressurreicao bloqueou ${tombstoneFilter.blocked.length} registro(s)`;
          if (!tableSummary.notes.includes(note)) tableSummary.notes.push(note);
          pullLogger.warn(
            `Tabela ${tableName}: ${tombstoneFilter.blocked.length} registro(s) remoto(s) ignorado(s) por tombstone local`
          );
        }
        const protectedRows = isChecklistProtectedPullTable
          ? tombstoneFilter.accepted.filter(row => (
              isProtectedFromRemotePull(tableName, row, protection)
            ))
          : [];
        const pullAcceptedRows = isChecklistProtectedPullTable
          ? tombstoneFilter.accepted.filter(row => (
              !isProtectedFromRemotePull(tableName, row, protection)
            ))
          : tombstoneFilter.accepted;
        if (protectedRows.length > 0) {
          tableSummary.skipped += protectedRows.length;
          const note = `preservou ${protectedRows.length} registro(s) local(is) pendente(s) ou em conflito`;
          if (!tableSummary.notes.includes(note)) tableSummary.notes.push(note);
          pullLogger.info(
            `Tabela ${tableName}: ${protectedRows.length} registro(s) remoto(s) ignorado(s) para preservar estado local protegido`
          );
        }
        const normalizedRows = pullAcceptedRows.map(row => (
          normalizeChecklistSnapshotHistoricalRow(tableName, row)
        ));
        const sanitized = sanitizeLocalTableRows(tableName, normalizedRows);

        if (sanitized.droppedKeys.length > 0) {
          const droppedKeysMessage = sanitized.droppedKeys.join(', ');
          pullLogger.info(
            `Tabela ${tableName} recebeu coluna(s) remota(s) fora do schema local e elas foram ignoradas no pull: ${droppedKeysMessage}`
          );
          const note = `colunas remotas ignoradas: ${droppedKeysMessage}`;
          if (!tableSummary.notes.includes(note)) {
            tableSummary.notes.push(note);
          }
        }

        return sanitized.rows;
      };

      const processPageIndividually = async (pageData: any[]): Promise<any[]> => {
        const acceptedRows: any[] = [];

        for (let i = 0; i < pageData.length; i++) {
          const latestProtection = isChecklistProtectedPullTable
            ? await refreshLocalPullProtection()
            : createEmptyLocalPullProtection();
          const sanitizedPageData = await sanitizePullPageData([pageData[i]], latestProtection);
          const item = sanitizedPageData[0];
          if (!item) continue;

          try {
            const localItem = await databaseService.readItem(tableName, item.id);

            // A fila pode ter sido criada enquanto a leitura local estava em
            // andamento; revalidamos antes da escrita efetiva da linha.
            const protectionBeforeWrite = isChecklistProtectedPullTable
              ? await refreshLocalPullProtection()
              : createEmptyLocalPullProtection();
            if (
              isChecklistProtectedPullTable &&
              isProtectedFromRemotePull(tableName, item, protectionBeforeWrite)
            ) {
              tableSummary.skipped++;
              continue;
            }

            if (localItem) {
              const comparisonLogger: PullLoggerAdapter = {
                info: () => {},
                warn: (...args) => pullLogger.warn(...args),
              };
              const shouldUpdate = shouldUpdateLocalItem(localItem, item, comparisonLogger);

              if (shouldUpdate) {
                const updated = isChecklistProtectedPullTable
                  ? await databaseService.updateItemIfUnchanged(
                      tableName,
                      item.id,
                      item,
                      localItem as Record<string, unknown>,
                      true
                    )
                  : await databaseService.updateItem(tableName, item.id, item, {
                    skipPendingOperation: true,
                    source: 'remote',
                  });
                if (updated) {
                  tableSummary.updated++;
                } else {
                  tableSummary.skipped++;
                }
              } else {
                tableSummary.skipped++;
              }
            } else {
              try {
                await databaseService.createItem(tableName, item, {
                  skipPendingOperation: true,
                  source: 'remote',
                });
                tableSummary.created++;
              } catch (createError) {
                if (!isChecklistProtectedPullTable) throw createError;
                const latestLocalItem = await databaseService.readItem(tableName, item.id);
                if (!latestLocalItem) throw createError;
                const protectionAfterCreateRace = await refreshLocalPullProtection();
                if (isProtectedFromRemotePull(tableName, item, protectionAfterCreateRace)) {
                  tableSummary.skipped++;
                  continue;
                }
                const updated = await databaseService.updateItemIfUnchanged(
                  tableName,
                  item.id,
                  item,
                  latestLocalItem as Record<string, unknown>,
                  true
                );
                if (updated) {
                  tableSummary.updated++;
                } else {
                  tableSummary.skipped++;
                }
              }
            }

            acceptedRows.push(item);
            totalItemsSynced++;
            tableProcessedRows++;

            if (i % 10 === 0 || i === sanitizedPageData.length - 1) {
              updatePullProgressForCurrentTable();
            }
          } catch (itemError) {
            reportUnrecoveredSyncFailure(itemError, 'pull_item');
            encounteredError = true;
            itemErrors++;
            tableSummary.itemErrors++;
            if (!tableSummary.notes.includes('falha ao persistir item local')) {
              tableSummary.notes.push('falha ao persistir item local');
            }
            SyncLogger.Error.error(`Erro ao processar item ${item.id} da tabela ${tableName}:`, itemError);
          }
        }

        return acceptedRows;
      };

      const processPageWithBulkUpsert = async (pageData: any[]): Promise<any[]> => {
        const latestProtection = isChecklistProtectedPullTable
          ? await refreshLocalPullProtection()
          : createEmptyLocalPullProtection();
        const sanitizedPageData = await sanitizePullPageData(pageData, latestProtection);

        if (sanitizedPageData.length === 0) return [];

        await databaseService.bulkUpsert(tableName, sanitizedPageData, true);
        tableSummary.created += sanitizedPageData.length;
        totalItemsSynced += sanitizedPageData.length;
        tableProcessedRows += sanitizedPageData.length;

        if (!tableSummary.notes.includes('upsert local em lote aplicado')) {
          tableSummary.notes.push('upsert local em lote aplicado');
        }

        pullLogger.info(
          `Tabela ${tableName} página ${pageIndex} persistida com bulk upsert local (${pageData.length} registros)`
        );

        updatePullProgressForCurrentTable();
        return sanitizedPageData;
      };

      const processProtectedBootstrapPage = async (pageData: any[]): Promise<any[]> => {
        // Mantém dois snapshots por página: um no início do processamento e
        // outro imediatamente antes da escrita, preservando a barreira de corrida.
        await refreshLocalPullProtection();
        const protectionBeforeWrite = await refreshLocalPullProtection();
        const sanitizedPageData = await sanitizePullPageData(pageData, protectionBeforeWrite);
        if (sanitizedPageData.length === 0) return [];

        const preexistingRows = bootstrapExistingRowsById
          ? sanitizedPageData.filter(row => bootstrapExistingRowsById.has(String(row.id ?? '')))
          : [];
        const rowsForBulkInsert = bootstrapExistingRowsById
          ? sanitizedPageData.filter(row => !bootstrapExistingRowsById.has(String(row.id ?? '')))
          : sanitizedPageData;
        const inserted = rowsForBulkInsert.length > 0
          ? await databaseService.bulkInsertIfAbsent(tableName, rowsForBulkInsert)
          : 0;
        const preservedByRace = rowsForBulkInsert.length - inserted;
        tableSummary.created += inserted;
        tableSummary.skipped += preservedByRace;
        totalItemsSynced += inserted;
        tableProcessedRows += rowsForBulkInsert.length;

        const comparisonLogger: PullLoggerAdapter = {
          info: () => {},
          warn: (...args) => pullLogger.warn(...args),
        };
        const rowsNeedingReconciliation = bootstrapExistingRowsById
          ? preexistingRows.filter(row => {
              const localRow = bootstrapExistingRowsById.get(String(row.id ?? ''));
              return !localRow || shouldUpdateLocalItem(localRow, row, comparisonLogger);
            })
          : preexistingRows;
        const unchangedPreexistingRows = preexistingRows.length - rowsNeedingReconciliation.length;

        if (unchangedPreexistingRows > 0) {
          tableSummary.skipped += unchangedPreexistingRows;
          totalItemsSynced += unchangedPreexistingRows;
          tableProcessedRows += unchangedPreexistingRows;
        }
        if (rowsNeedingReconciliation.length > 0) {
          await processPageIndividually(rowsNeedingReconciliation);
        }

        if (!tableSummary.notes.includes('bootstrap local protegido em lote aplicado')) {
          tableSummary.notes.push('bootstrap local protegido em lote aplicado');
        }
        if (rowsNeedingReconciliation.length > 0) {
          const note = `reconciliou ${rowsNeedingReconciliation.length} linha(s) preexistente(s) durante bootstrap`;
          if (!tableSummary.notes.includes(note)) tableSummary.notes.push(note);
        }
        if (preservedByRace > 0) {
          const note = `preservou ${preservedByRace} colisão(ões) de corrida durante bootstrap`;
          if (!tableSummary.notes.includes(note)) tableSummary.notes.push(note);
        }

        pullLogger.info(
          `Tabela ${tableName} página ${pageIndex} persistida com insert-if-absent` +
          ` | recebidos=${pageData.length} | aceitos=${sanitizedPageData.length}` +
          ` | inseridos=${inserted} | preexistentes=${preexistingRows.length}` +
          ` | reconciliados=${rowsNeedingReconciliation.length}` +
          ` | preservados_por_corrida=${preservedByRace}`
        );
        updatePullProgressForCurrentTable();
        return sanitizedPageData;
      };

      while (true) {
        const pageContextLabel = `${baseContextLabel}#${pageIndex}`;
        // Solicitamos o count apenas na primeira página para enriquecer o feedback
        // de progresso sem adicionar uma consulta separada ao fluxo do pull.
        const basePageQuery = buildBaseQuery(pageIndex === 0, pageCursorId);
        const pageQuery = hasIdColumn
          ? basePageQuery.limit(pageSize)
          : basePageQuery.range(pageIndex * pageSize, ((pageIndex + 1) * pageSize) - 1);

        const { data, error, count } = await withNetworkTimeout<any>(
          () => pageQuery,
          pageContextLabel
        );

        if (error) {
          const normalizedError = normalizeSyncTableError(error);
          const errorSummary = buildSyncTableErrorSummary(normalizedError);
          const networkFailureDetected = isNetworkFailureError(normalizedError);

          SyncLogger.Error.error(
            `${tracePrefix}Erro ao obter dados da tabela ${tableName} (página ${pageIndex}): ${errorSummary}`,
            normalizedError.original
          );
          tableSummary.notes.push(`erro ao consultar dados: ${errorSummary}`);
          tableErrors++;

          captureSentryMessageWithScope('Erro ao consultar tabela durante pull', {
            level: 'error',
            context: getSyncSentryBaseContext({
              flow: 'sync',
              stage: 'pull_table_fetch',
              tracePrefix: traceLabel || null,
              tableName,
              pageIndex,
              networkFailureDetected,
              errorMessage: normalizedError.message,
              errorCode: normalizedError.code,
              errorHint: normalizedError.hint,
            }),
            tags: {
              flow: 'sync',
              stage: 'pull',
              reason: networkFailureDetected ? 'network_failure' : 'table_fetch_failure',
            },
            fingerprint: [
              'sync',
              'pull_table_fetch_error',
              networkFailureDetected ? 'network' : 'generic',
              normalizedError.message.slice(0, 120),
              normalizedError.code || 'no_code',
            ],
            dedupeKey: `sync:pull_table_fetch:${networkFailureDetected ? 'network' : 'generic'}:${normalizedError.signature.slice(0, 240)}`,
            dedupeWindowMs: 30000,
          });

          if (networkFailureDetected) {
            abortRemainingTables = true;
            firstNetworkFailure = firstNetworkFailure || {
              tableName,
              pageIndex,
              error: normalizedError,
            };
            pullLogger.warn(
              `Falha de rede detectada na tabela ${tableName} (página ${pageIndex}). Interrompendo pull das próximas tabelas.`
            );
          }

          encounteredError = true;
          break;
        }

        const pageData = data ?? [];

        if (pageIndex === 0 && typeof count === 'number' && count >= 0) {
          tableKnownTotalRows = count;
        }

        if (pageIndex === 0 && pageData.length === 0) {
          tableSummary.notes.push('nenhum dado novo');
          tableSummary.processed = true;
          break;
        }

        if (pageData.length === 0) {
          break;
        }

        encounteredData = true;
        tableSummary.processed = true;
        tableSummary.itemsFound += pageData.length;

        const shouldUseBulkLocalUpsert =
          !isChecklistProtectedPullTable &&
          !tableSummary.filterTimestamp &&
          pageData.length >= getLocalBulkUpsertThreshold();
        const shouldUseProtectedBootstrapInsert =
          protectedBootstrapInsertEnabled &&
          !tableSummary.filterTimestamp &&
          (
            options.protectedBootstrapRepair === true ||
            pageData.length >= getLocalBulkUpsertThreshold()
          );

        let rowsForPostProcessing: any[] = [];
        if (shouldUseProtectedBootstrapInsert) {
          try {
            rowsForPostProcessing = await processProtectedBootstrapPage(pageData);
          } catch (bulkInsertError) {
            if (options.protectedBootstrapRepair === true) {
              throw bulkInsertError;
            }
            pullLogger.warn(
              `Falha no insert-if-absent do bootstrap para ${tableName} na página ${pageIndex}. Retomando processamento item a item.`,
              bulkInsertError
            );
            rowsForPostProcessing = await processPageIndividually(pageData);
          }
        } else if (shouldUseBulkLocalUpsert) {
          try {
            rowsForPostProcessing = await processPageWithBulkUpsert(pageData);
          } catch (bulkError) {
            pullLogger.warn(
              `Falha ao aplicar bulk upsert local para ${tableName} na página ${pageIndex}. Retomando processamento item a item.`,
              bulkError
            );
            rowsForPostProcessing = await processPageIndividually(pageData);
          }
        } else {
          rowsForPostProcessing = await processPageIndividually(pageData);
        }

        if (
          tableName === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME &&
          rowsForPostProcessing.length > 0
        ) {
          try {
            const imageResult = await processMarcacaoImages(rowsForPostProcessing, tracePrefix);
            tableSummary.imagesProcessed += imageResult.success;
            tableSummary.imageErrors += imageResult.errors;
            imageErrors += imageResult.errors;
            if (
              imageResult.errors > 0 &&
              !tableSummary.notes.includes('falha ao materializar imagem local')
            ) {
              tableSummary.notes.push('falha ao materializar imagem local');
            }
          } catch (imageError) {
            imageErrors += rowsForPostProcessing.length;
            tableSummary.imageErrors += rowsForPostProcessing.length;
            if (!tableSummary.notes.includes('falha ao processar imagens locais')) {
              tableSummary.notes.push('falha ao processar imagens locais');
            }
            pullLogger.error(`Erro ao processar imagens de marcação da tabela ${tableName}:`, imageError);
          }
        }

        if (pageData.length < pageSize) {
          break;
        }

        if (hasIdColumn) {
          pageCursorId = pageData[pageData.length - 1]?.id || null;
          if (!pageCursorId) {
            throw new Error(`Paginação keyset sem id em ${tableName} na página ${pageIndex}`);
          }
        }

        pageIndex++;
      }

      if (encounteredError) {
        continue;
      }

      if (!encounteredData && tableSummary.notes.length === 0) {
        tableSummary.notes.push('nenhum dado novo');
        tableSummary.processed = true;
      }

      tablesProcessed++;

      const progressPercent = Math.floor((tablesProcessed / tablesToSync.length) * 100);
      syncActions.updateProgress('pull', progressPercent, pullOverallProgress(progressPercent));
    }

    if (!tableErrors && !itemErrors && tombstonePreflight.enabled
      && [retiredHistoryTables.templates, retiredHistoryTables.fields, retiredHistoryTables.parents, retiredHistoryTables.values]
        .every(table => tablesToSync.includes(table))) {
      const historyTombstones = await databaseService.listItems<LocalTombstoneRow>('sync_tombstones', { empresa_id: empresaId });
      const hasRetiredCatalog = historyTombstones.some(row => row.deletion_kind === 'retire'
        && (row.entity_type === retiredHistoryTables.templates || row.entity_type === retiredHistoryTables.fields));
      if (hasRetiredCatalog) {
        const historySession = await capturePendingSyncSession();
        if (!historySession || historySession.userId !== userId || historySession.empresaId !== empresaId) {
          throw new PendingSyncSessionChangedError();
        }
        const historyClient = createPendingSyncSupabaseClient(historySession);
        let historicalDeletes = new Map<string, Set<string>>();
        let historicalGuardTables: HistoricalInsertGuard['tables'] = [];
        const historicalGuardRows = new Map<string, HistoricalInsertGuard['rows'][number]>();
        const historicalCheckpointKey = `retired-history:v3:${encodeURIComponent(empresaId)}:${encodeURIComponent(userId)}`;
        const previousHistoricalCheckpoint = syncStorage.getString(historicalCheckpointKey);
        const restored = await new RetiredChecklistHistoryService({
          loadCheckpoint() {
            try {
              const state = JSON.parse(syncStorage.getString(historicalCheckpointKey) ?? 'null');
              return state && Number.isInteger(state.root) && state.root >= 0
                && Number.isInteger(state.parent) && state.parent >= 0 && Number.isInteger(state.offset) && state.offset >= 0
                && ['fields', 'values', 'images'].includes(state.phase) ? state : null;
            } catch { return null; }
          },
          saveCheckpoint(state) {
            if (!isPendingSyncSessionSnapshotStateCurrent(historySession)) throw new PendingSyncSessionChangedError();
            syncStorage.set(historicalCheckpointKey, JSON.stringify(state));
          },
          tombstones: async () => historyTombstones,
          snapshotsEnabled: tablesToSync.includes(historicalTables.snapshots),
          async read(table, id) {
            const row = await databaseService.readItem<Record<string, unknown>>(table, id);
            historicalGuardRows.set(`${table}:${id}`, { table, id, expected: row });
            return row;
          },
          async fetch(table, filters, offset, limit) {
            await assertPendingSyncSessionCurrent(historySession);
            let query = historyClient.from(table).select('*');
            for (const [key, value] of Object.entries(filters)) query = query.eq(key, value);
            const result = await withNetworkTimeout<any>(async () => await query.order('id').range(offset, offset + limit - 1), `historical:${table}`);
            await assertPendingSyncSessionCurrent(historySession);
            if (result.error) throw result.error;
            return result.data ?? [];
          },
          async refreshProtection(reuse = false) {
            await assertPendingSyncSessionCurrent(historySession);
            historicalGuardRows.clear();
            if (reuse) return;
            historicalGuardTables = [];
            // Captura antes de calcular a proteção; qualquer mudança posterior invalida o insert transacional.
            for (const table of ['pending_operations', 'pending_images', 'pending_deletions', 'sync_conflicts', 'sync_tombstones']) {
              historicalGuardTables.push({ table, expected: await databaseService.listItems<Record<string, unknown>>(table) });
            }
            await refreshLocalPullProtection();
            historicalDeletes = new Map();
            const rows = await databaseService.listItems<LocalTombstoneRow>('sync_tombstones', { empresa_id: empresaId });
            for (const row of rows) {
              if (row.deletion_kind !== 'delete' && row.entity_type !== historicalTables.parents) continue;
              const ids = historicalDeletes.get(row.entity_type) ?? new Set<string>();
              ids.add(row.entity_id);
              historicalDeletes.set(row.entity_type, ids);
            }
          },
          async allowed(table, row) {
            if ((table === historicalTables.parents && (row.retired_at != null
                || isTombstoneBarrierActive(tombstoneBarriers, table, row.id)))
              || !isPendingSyncSessionSnapshotStateCurrent(historySession)
              || row.empresa_id !== empresaId || isProtectedFromRemotePull(table, row, localPullProtection)) return false;
            return filterTombstonedRows(table, [row], tombstoneBarriers, { historical: true }).accepted.length === 1
              && filterTombstonedRows(table, [row], historicalDeletes, { historical: true }).accepted.length === 1;
          },
          async insertAbsent(table, row) {
            if (!isPendingSyncSessionSnapshotStateCurrent(historySession)) throw new PendingSyncSessionChangedError();
            return databaseService.bulkInsertIfAbsent(table, [normalizeChecklistSnapshotHistoricalRow(table, row)], {
              canApply: () => isPendingSyncSessionSnapshotStateCurrent(historySession),
              rows: [...historicalGuardRows.values()], tables: historicalGuardTables,
            });
          },
          async materializePhoto(row, revalidate) {
            return materializeHistoricalPhoto({ userId, companyId: empresaId!, checklistId: row.checklist_id }, row.valor, {
              read: id => databaseService.readItem(historicalPhotoCacheTable, id),
              download: () => downloadHistoricalMediaBase64(row.valor, NETWORK_TIMEOUT),
              onDownloadError(error) {
                imageErrors++;
                pullLogger.warn(`Foto histórica ${row.id} indisponível; será tentada em outro ciclo`, error);
              },
              revalidate,
              commit: cache => databaseService.bulkInsertIfAbsent(historicalPhotoCacheTable, [cache], {
                canApply: () => isPendingSyncSessionSnapshotStateCurrent(historySession),
                rows: [...historicalGuardRows.values()], tables: historicalGuardTables,
              }),
            });
          },
          async materializeImages(rows, revalidate) {
            for (const row of rows) {
              const cached = await materializeHistoricalImage(row, {
                readCache: () => databaseService.readItem(historicalImageCacheTable, row.id),
                async hasFile() {
                  if (Platform.OS === 'web') return false;
                  for (const extension of ['jpg', 'png']) {
                    const info = await FileSystem.getInfoAsync(`${FileSystem.documentDirectory}checklist-marcacao-bkg/${row.empresa_id}/${row.id}.${extension}`);
                    if (info.exists && !info.isDirectory && info.size > 0) return true;
                  }
                  return false;
                },
                download: () => downloadHistoricalMediaBase64(row.foto_url, NETWORK_TIMEOUT),
                onDownloadError(error) {
                  imageErrors++;
                  pullLogger.warn(`Imagem histórica ${row.id} indisponível; será tentada em outro ciclo`, error);
                },
                revalidate,
                commit: cache => databaseService.bulkInsertIfAbsent(historicalImageCacheTable, [cache], {
                  canApply: () => isPendingSyncSessionSnapshotStateCurrent(historySession),
                  rows: [...historicalGuardRows.values()], tables: historicalGuardTables,
                }),
              });
              if (!cached) return false;
            }
            return true;
          },
        }).run(empresaId);
        const savedHistoricalCheckpoint = syncStorage.getString(historicalCheckpointKey);
        const historicalResumePending = savedHistoricalCheckpoint
          && JSON.parse(savedHistoricalCheckpoint).completedAt == null;
        if (historicalResumePending && (restored > 0 || savedHistoricalCheckpoint !== previousHistoricalCheckpoint)) {
          void import('./syncScheduler').then(module => {
            if (isPendingSyncSessionSnapshotStateCurrent(historySession)) module.triggerSyncSoon('retired-history-resume', 5000);
          }).catch(error => pullLogger.warn('Retomada histórica aguardará próximo ciclo normal', error));
        }
        totalItemsSynced += restored;
        if (restored) pullLogger.info(`Histórico sob catálogo aposentado: ${restored} linha(s) ausente(s) recebida(s)`);
      }
    }

    if (!tableErrors && !itemErrors && (tablesToSync.includes(historicalTables.parents) || tablesToSync.includes(historicalTables.snapshots))) {
      const metadataSession = await capturePendingSyncSession();
      if (!metadataSession || metadataSession.userId !== userId || metadataSession.empresaId !== empresaId) throw new PendingSyncSessionChangedError();
      const metadataClient = createPendingSyncSupabaseClient(metadataSession);
      const checkpointKey = `canonical-metadata:v1:${encodeURIComponent(empresaId)}:${encodeURIComponent(userId)}`;
      let freshHistoricalBarriers = new Map<string, Set<string>>();
      let metadataProtection = createEmptyLocalPullProtection();
      let guardTables: HistoricalInsertGuard['tables'] = [];
      const guardRows = new Map<string, HistoricalInsertGuard['rows'][number]>();
      const canApply = () => isPendingSyncSessionSnapshotStateCurrent(metadataSession);
      const guard = (): HistoricalInsertGuard => ({ canApply, rows: [...guardRows.values()], tables: guardTables });
      const remember = async (table: string, id: string) => {
        await assertPendingSyncSessionCurrent(metadataSession);
        const row = await databaseService.readItem<Record<string, any>>(table, id);
        guardRows.set(`${table}:${id}`, { table, id, expected: row });
        return row;
      };
      const result = await pullCanonicalMetadata(empresaId, {
        enabled: async () => tablesToSync.includes(historicalTables.snapshots)
          && await syncCanaryFeatureService.isEnabled(empresaId!, HISTORICAL_SNAPSHOTS_FEATURE, getSyncClientBuild()),
        listParents: async () => [],
        async page(table, afterId, limit, scope) {
          await assertPendingSyncSessionCurrent(metadataSession);
          return databaseService.listItemsByIdPage(table, afterId, limit, scope);
        },
        load() {
          try {
            const state = JSON.parse(syncStorage.getString(checkpointKey) ?? 'null');
            return state && typeof state.parent === 'string' && typeof state.value === 'string'
              && typeof state.parentDone === 'boolean' && typeof state.valueDone === 'boolean' ? state : null;
          } catch { return null; }
        },
        save(state) {
          if (!canApply()) throw new PendingSyncSessionChangedError();
          syncStorage.set(checkpointKey, JSON.stringify(state));
        },
        now: Date.now,
        read: remember,
        async fetch(table, filters, offset, limit) {
          await assertPendingSyncSessionCurrent(metadataSession);
          let query = metadataClient.from(table).select('*');
          for (const [key, value] of Object.entries(filters)) query = query.eq(key, value);
          const result = await query.order('id').range(offset, offset + limit - 1);
          await assertPendingSyncSessionCurrent(metadataSession);
          if (result.error) throw result.error;
          return result.data ?? [];
        },
        async refreshProtection() {
          await assertPendingSyncSessionCurrent(metadataSession);
          guardRows.clear(); guardTables = [];
          for (const table of ['pending_operations', 'pending_images', 'pending_deletions', 'sync_conflicts', 'sync_tombstones']) {
            guardTables.push({ table, expected: await databaseService.listItems<Record<string, unknown>>(table) });
          }
          metadataProtection = await loadLocalPullProtection(true);
          freshHistoricalBarriers = new Map();
          for (const row of (guardTables.find(entry => entry.table === 'sync_tombstones')!.expected as unknown as LocalTombstoneRow[])) {
            if (row.empresa_id !== empresaId || (row.deletion_kind === 'retire' && row.entity_type !== historicalTables.parents)) continue;
            const ids = freshHistoricalBarriers.get(row.entity_type) ?? new Set<string>();
            ids.add(row.entity_id); freshHistoricalBarriers.set(row.entity_type, ids);
          }
        },
        async allowed(table, row) {
          if (!canApply() || row.empresa_id !== empresaId || isProtectedFromRemotePull(table, row, metadataProtection)) return false;
          if (table === historicalTables.parents && (row.retired_at != null
            || isTombstoneBarrierActive(tombstoneBarriers, table, row.id))) return false;
          if (row.template_id && isProtectedFromRemotePull(retiredHistoryTables.templates, { id: row.template_id }, metadataProtection)) return false;
          if (table === historicalTables.fields && row.original_campo_id) {
            const original = { id: row.original_campo_id };
            if (isProtectedFromRemotePull(retiredHistoryTables.fields, original, metadataProtection)
              || filterTombstonedRows(retiredHistoryTables.fields, [original], freshHistoricalBarriers, { historical: true }).accepted.length !== 1
              || filterTombstonedRows(retiredHistoryTables.fields, [original], tombstoneBarriers, { historical: true }).accepted.length !== 1) return false;
          }
          const historical = true;
          if (filterTombstonedRows(table, [row], tombstoneBarriers, { historical }).accepted.length !== 1
            || filterTombstonedRows(table, [row], freshHistoricalBarriers, { historical }).accepted.length !== 1) return false;
          const current = await remember(table, row.id);
          if (!current) return table !== historicalTables.parents && table !== retiredHistoryTables.values;
          return Object.keys(row).every(key => stableStringify(current[key] ?? null) === stableStringify(row[key] ?? null));
        },
        async insert(table, row) {
          const inserted = await databaseService.bulkInsertIfAbsent(table, [row], guard());
          await remember(table, row.id);
          return inserted;
        },
        link: (id, snapshotId, expected) => databaseService.updateItemIfUnchanged(historicalTables.parents, id, { snapshot_id: snapshotId }, expected, true, canApply, guard()),
        patch: (table, expected, updates) => databaseService.updateItemIfUnchanged(table, expected.id, updates, expected, true, canApply, guard()),
      });
      totalItemsSynced += result.changed;
      if (result.changed || (result.incomplete && result.progressed)) {
        // Carregamento tardio mantém a dependência circular fora da inicialização dos serviços.
        const { triggerSyncSoon } = require('./syncScheduler') as typeof import('./syncScheduler');
        if (canApply()) triggerSyncSoon('canonical-metadata-progress', 5000);
      }
      if (result.changed) pullLogger.info(`Metadados canônicos ausentes recebidos: ${result.changed}`);
    }

    const blockingPullErrors = tableErrors + itemErrors;
    const totalPullErrors = blockingPullErrors + imageErrors;
    if (blockingPullErrors > 0) {
      const failedTables = tableSummaries
        .filter(summary => summary.notes.some(note => (
          note.startsWith('erro ao consultar dados') ||
          note.startsWith('falha ao persistir item local')
        )))
        .map(summary => summary.table);

      const networkFailureLabel = firstNetworkFailure
        ? ` Falha de rede principal em ${firstNetworkFailure.tableName} (página ${firstNetworkFailure.pageIndex}).`
        : '';
      const errorMessage =
        `${tracePrefix}Pull concluído com falha: ${blockingPullErrors} erro(s) em ` +
        `${failedTables.length} tabela(s).` +
        ` Tabelas com erro: ${failedTables.join(', ') || 'não identificadas'}.` +
        networkFailureLabel;

      pullLogger.warn(errorMessage);

      captureSentryMessageWithScope('Pull finalizado com falhas de tabela', {
        level: 'error',
        context: getSyncSentryBaseContext({
          flow: 'sync',
          stage: 'pull_summary_failure',
          tracePrefix: traceLabel || null,
          tableErrors,
          itemErrors,
          imageErrors,
          totalPullErrors,
          failedTables,
          firstNetworkFailure: firstNetworkFailure
            ? {
              tableName: firstNetworkFailure.tableName,
              pageIndex: firstNetworkFailure.pageIndex,
              errorMessage: firstNetworkFailure.error.message,
              errorCode: firstNetworkFailure.error.code,
            }
            : null,
        }),
        tags: {
          flow: 'sync',
          stage: 'pull',
          result: 'error',
          reason: firstNetworkFailure ? 'network_failure' : 'table_fetch_failure',
        },
        fingerprint: [
          'sync',
          'pull_failed_summary',
          firstNetworkFailure ? 'network' : 'generic',
        ],
        dedupeKey: `sync:pull_failed_summary:${firstNetworkFailure ? 'network' : 'generic'}`,
        dedupeWindowMs: 30000,
      });

      if (shouldManageState) {
        syncActions.setSyncRunning(false);
      }
      syncActions.setSyncStatus('error');
      syncActions.setSyncError(errorMessage);

      return {
        success: false,
        message: errorMessage,
        timestamp: new Date(),
        details: {
          duration: Date.now() - startTime,
          totalItemsSynced,
          tablesProcessed,
          tablesToSync,
          tableErrors,
          itemErrors,
          imageErrors,
          totalPullErrors,
          failedTables,
          firstNetworkFailure: firstNetworkFailure
            ? {
              tableName: firstNetworkFailure.tableName,
              pageIndex: firstNetworkFailure.pageIndex,
              errorMessage: firstNetworkFailure.error.message,
              errorCode: firstNetworkFailure.error.code,
              errorHint: firstNetworkFailure.error.hint,
            }
            : null,
        },
      };
    }

    if (activePendingSyncExecution && (
      tablesToSync.includes(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME)
      || tablesToSync.includes(APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME)
    )) {
      await refreshPulledIdentity(activePendingSyncExecution.snapshot);
    }

    // Salvar o timestamp da sincronização atual
    saveLastSyncTimestamp(startTime, syncScope);

    // Finalizar
    const endTime = Date.now();
    const duration = endTime - startTime;

    const tablesOverview = tableSummaries.map(summary => {
      const detailParts: string[] = [];
      if (!summary.processed) {
        detailParts.push('processo não executado');
      } else if (summary.itemsFound === 0) {
        detailParts.push('nenhum dado novo');
      } else {
        detailParts.push(`${summary.itemsFound} registros`);
        const changeDetails: string[] = [];
        if (summary.created) {
          changeDetails.push(`${summary.created} novos`);
        }
        if (summary.updated) {
          changeDetails.push(`${summary.updated} atualizados`);
        }
        if (summary.skipped) {
          changeDetails.push(`${summary.skipped} sem mudanças`);
        }
        if (changeDetails.length) {
          detailParts.push(`(${changeDetails.join(', ')})`);
        }
      }
      if (summary.imagesProcessed) {
        detailParts.push(`${summary.imagesProcessed} imagens processadas`);
      }
      if (summary.notes.length) {
        detailParts.push(summary.notes.join('; '));
      }
      const detailSuffix = detailParts.join(' | ') || 'nenhum processamento';
      return `${summary.table} -> ${detailSuffix}`;
    }).join('; ');

    pullLogger.info(`Sincronização concluída em ${duration}ms | Timestamp base: ${lastSyncLabel} | Total sincronizado: ${totalItemsSynced} | Visão por tabela: ${tablesOverview || 'sem tabelas processadas'}`);

    syncActions.setSyncStatus('success');
    syncActions.updateProgress('pull', 100, pullOverallProgress(100));
    if (shouldManageState) {
      syncActions.setSyncRunning(false);
    }

    if (shouldRefreshPullViews(
      totalItemsSynced,
      tombstonePreflight.applied,
      tombstonePreflight.duplicates
    )) {
      realtimeTrigger.counter.set(realtimeTrigger.counter.get() + 1);
    }

    return {
      success: true,
      message: `Sincronização concluída com sucesso. ${totalItemsSynced} itens sincronizados em ${tablesToSync.length} tabelas.`,
      timestamp: new Date(),
      details: {
        duration,
        totalItemsSynced,
        tablesProcessed,
        tablesToSync,
        tombstonePreflight: tombstonePreflight.enabled ? 'applied' : 'feature_off',
        imageErrors,
        totalPullErrors,
      }
    };
  } catch (error) {
    reportUnrecoveredSyncFailure(error, 'pull_remote_changes');
    const errorMessage = `Erro durante sincronização de dados remotos: ${error}`;
    SyncLogger.Error.warn(errorMessage);
    captureSentryExceptionWithScope(error, {
      context: getSyncSentryBaseContext({
        flow: 'sync',
        stage: 'pull_remote_changes',
        forceFullSync: options.forceFullSync || false,
        tracePrefix: traceLabel || null,
        loggerMessage: errorMessage,
      }),
      tags: {
        flow: 'sync',
        stage: 'pull',
      },
      fingerprint: ['sync', 'pull_exception'],
      dedupeKey: 'sync:pull_exception',
      dedupeWindowMs: 30000,
    });

    if (shouldManageState) {
      syncActions.setSyncRunning(false);
    }
    syncActions.setSyncStatus('error');
    syncActions.setSyncError(errorMessage);

    return {
      success: false,
      message: errorMessage,
      timestamp: new Date(),
      details: { error }
    };
  }
};

export const pullRemoteChanges = (options: SyncOptions = {}): Promise<SyncStatus> =>
  options.managedBySyncAll ? pullRemoteChangesInternal(options, null)
    : runReservedSync(() => pullRemoteChangesInternal(options, null), 'pull');

export const pullRemoteChangesForRecovery = (
  context: RecoveryCanonicalPullContext
): Promise<SyncStatus> => new RecoveryCanonicalPullService(
  isAuthorizedRecoveryPull,
  (options, verifiedContext) => pullRemoteChangesInternal(options, verifiedContext)
).execute(context);

export type { RecoveryCanonicalPullContext } from './sync/RecoveryCanonicalPullService';

/**
 * Verifica se o item local deve ser atualizado com base no item remoto
 * 
 * @param localItem Item armazenado localmente
 * @param remoteItem Item obtido do servidor
 * @returns Booleano indicando se o item local deve ser atualizado
 */
type PullLoggerAdapter = {
  info: (message: string, ...args: any[]) => void;
  warn?: (message: string, ...args: any[]) => void;
};

const shouldUpdateLocalItem = (
  localItem: any,
  remoteItem: any,
  logger: PullLoggerAdapter = {
    info: (...args) => SyncLogger.Pull.info(...args),
    warn: (...args) => SyncLogger.Pull.warn(...args),
  }
): boolean => {
  // Se não tiver data de atualização, presume que deve atualizar
  if (!localItem.updated_at || !remoteItem.updated_at) {
    logger.info('Não há data de atualização para o item local ou remoto, presumindo que deve atualizar');
    return true;
  }

  // Converter strings para objetos Date
  let localDate = localItem.updated_at;
  let remoteDate = remoteItem.updated_at;

  // Se as datas estiverem em formato de string, converter para Date
  if (typeof localDate === 'string') {
    localDate = new Date(localDate);
  } else if (typeof localDate === 'number') {
    localDate = new Date(localDate);
  }

  if (typeof remoteDate === 'string') {
    remoteDate = new Date(remoteDate);
  } else if (typeof remoteDate === 'number') {
    remoteDate = new Date(remoteDate);
  }

  logger.info(`Comparando datas para o item local: ${localDate} e remoto: ${remoteDate}`);
  // Verifique se as datas são objetos Date válidos
  if (!(localDate instanceof Date) || isNaN(localDate.getTime()) ||
    !(remoteDate instanceof Date) || isNaN(remoteDate.getTime())) {
    if (logger.warn) {
      logger.warn('Datas inválidas ao comparar itens:', { localDate, remoteDate });
    } else {
      SyncLogger.Conflict.warn('Datas inválidas ao comparar itens:', { localDate, remoteDate });
    }
    return true; // Se houver problemas com as datas, atualize para garantir
  }

  logger.info(`A versão remota é mais recente que a local: ${remoteDate > localDate}`);
  // Atualizar se a versão remota for mais recente
  return remoteDate > localDate;
};

/**
 * Processa as imagens de marcação baixadas da tabela GUINCHO_MARCACAO_IMAGENS
 * Salva as imagens remotas localmente para uso offline
 */
type MarcacaoImageProcessResult = {
  processed: number;
  success: number;
  errors: number;
};

const processMarcacaoImages = async (
  marcacaoItems: any[],
  tracePrefix: string,
): Promise<MarcacaoImageProcessResult> => {
  const result: MarcacaoImageProcessResult = {
    processed: 0,
    success: 0,
    errors: 0,
  };
  const logInfo = (message: string, ...args: any[]) =>
    SyncLogger.Pull.info(`${tracePrefix}${message}`, ...args);
  const logWarn = (message: string, ...args: any[]) =>
    SyncLogger.Pull.warn(`${tracePrefix}${message}`, ...args);
  const logError = (message: string, ...args: any[]) =>
    SyncLogger.Pull.error(`${tracePrefix}${message}`, ...args);

  logInfo(`Iniciando processamento de ${marcacaoItems.length} imagens de marcação`);

  for (const marcacao of marcacaoItems) {
    result.processed++;
    const { id, foto_url, empresa_id, template_id, campo_id } = marcacao;

    try {
      if (!foto_url || typeof foto_url !== 'string') {
        result.errors++;
        logWarn(`Imagem de marcação ${id} não possui foto_url válida`);
        continue;
      }

      if (!foto_url.startsWith('http')) {
        result.success++;
        logInfo(`Imagem de marcação ${id} já é local: ${foto_url}`);
        continue;
      }

      if (Platform.OS === 'web') {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), NETWORK_TIMEOUT);
        let base64: string;
        try {
          const response = await fetch(foto_url, { signal: controller.signal });
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          const blob = await response.blob();
          const reader = new FileReader();
          base64 = await new Promise<string>((resolve, reject) => {
            reader.onerror = () => reject(reader.error);
            reader.onload = () => resolve((reader.result as string).split(',')[1]);
            reader.readAsDataURL(blob);
          });
        } finally {
          clearTimeout(timeoutId);
        }

        const localImageData = {
          id,
          base64,
          empresa_id,
          template_id,
          campo_id,
          foto_url: id,
          created_at: Date.now(),
          updated_at: Date.now(),
        };
        const existing = await databaseService.readItem<any>(
          APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACAO_BKG_IMAGES.NAME,
          id,
        );

        if (existing) {
          await databaseService.updateItem(
            APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACAO_BKG_IMAGES.NAME,
            id,
            localImageData,
            true,
          );
        } else {
          await databaseService.createItem(
            APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACAO_BKG_IMAGES.NAME,
            localImageData,
            true,
          );
        }
      } else {
        const dirPath = `${FileSystem.documentDirectory}checklist-marcacao-bkg/${empresa_id}/`;
        const fileUri = `${dirPath}${id}.jpg`;
        const dirInfo = await FileSystem.getInfoAsync(dirPath);
        if (!dirInfo.exists) {
          await FileSystem.makeDirectoryAsync(dirPath, { intermediates: true });
        }
        await downloadRemoteImageToFile(foto_url, fileUri, FileSystem);
      }

      result.success++;
      logInfo(`Imagem de marcação ${id} salva localmente`);
    } catch (itemError: any) {
      result.errors++;
      if (itemError?.name === 'AbortError') {
        logWarn(`Timeout ao baixar imagem de marcação ${id}`);
      } else {
        logError(`Erro ao processar imagem de marcação ${id}:`, itemError);
      }
    }
  }

  logInfo(
    `Processamento de imagens de marcação concluído ` +
    `(sucesso=${result.success}, erros=${result.errors})`,
  );
  return result;
};

/**
 * Envia alterações locais pendentes para o Supabase
 * 
 * @param options Opções de sincronização
 * @returns Promise que resolve para um objeto com status e detalhes da operação
 */
const runReservedSync = async (run: () => Promise<SyncStatus>, stage?: 'push' | 'pull'): Promise<SyncStatus> => {
  const release = reserveSyncExecution();
  if (!release) return {
    success: false, message: 'Sincronização em andamento', timestamp: new Date(),
    details: { reason: 'busy' },
  };
  let execution: PendingSyncExecution | null = null;
  let cycle: Parameters<typeof beginSyncDiagnosticCycle>[0] | null = null;
  let endCycle: (() => void) | null = null;
  let completedResult: SyncStatus | null = null;
  let preparationCurrent = true;
  try {
    const sessionSnapshot = await withNetworkTimeout(async () => {
      // No primeiro ciclo o perfil local pode ainda não estar hidratado.
      const currentUserId = globalState.user.user.get()?.id?.trim();
      const profileBeforeHydration = globalState.user.profile.get();
      const profileUserId = profileBeforeHydration?.id;
      const profileCompanyId = profileBeforeHydration?.empresa_id;
      if (currentUserId && !profileCompanyId) {
        await getUserProfile(currentUserId, {
          // O bootstrap popula o cache; esta leitura possui prazo e pode expirar.
          cacheRemote: false,
          shouldApply: () => preparationCurrent &&
            globalState.user.user.get()?.id?.trim() === currentUserId &&
            globalState.user.profile.get() === profileBeforeHydration &&
            globalState.user.profile.get()?.id === profileUserId &&
            globalState.user.profile.get()?.empresa_id === profileCompanyId,
        });
      }
      if (!preparationCurrent) return null;
      const snapshot = await capturePendingSyncSession();
      if (snapshot) await assertPendingSyncSessionCurrent(snapshot);
      return snapshot;
    }, 'preparação da sessão de sincronização');
    if (!sessionSnapshot) {
      return {
        success: false,
        message: 'Sessão autenticada e empresa não puderam ser confirmadas; pendências preservadas.',
        timestamp: new Date(),
        details: { reason: 'pending_sync_session_unverified' },
      };
    }
    // A preparação pode responder depois do timeout, mas somente o await
    // vencedor publica a execução e inicia um ciclo que possa consumir filas.
    execution = {
      snapshot: sessionSnapshot,
      client: createPendingSyncSupabaseClient(sessionSnapshot),
    };
    activePendingSyncExecution = execution;
    cycle = { companyId: sessionSnapshot.empresaId,
      userId: sessionSnapshot.userId, traceId: uuidv4(), startedAt: Date.now() };
    endCycle = beginSyncDiagnosticCycle(cycle);
    const result = await run();
    completedResult = result;
    if (stage && (result.success || (result.details as any)?.errorCount > 0 || (result.details as any)?.totalPullErrors > 0 || (result.details as any)?.error)) {
      reportSyncDiagnosticResult({ ...cycle, success: result.success, error: result.message ?? 'Falha de sincronização', stage });
    }
    return result;
  } catch (error) {
    if (endCycle) throw error;
    SyncLogger.Main.warn('Preparação da sessão não confirmada; pendências preservadas.');
    return {
      success: false,
      message: 'Sessão autenticada e empresa não puderam ser confirmadas; pendências preservadas.',
      timestamp: new Date(),
      details: { reason: 'pending_sync_session_unverified' },
    };
  } finally {
    preparationCurrent = false;
    endCycle?.();
    if (execution && activePendingSyncExecution === execution) activePendingSyncExecution = null;
    release();

    // O observador de coleta é chamado deliberadamente depois da liberação da
    // lease. Ele nunca dispara syncAll e não pode atrasar a tela de retorno.
    if (!stage && cycle && completedResult && isActualPostUpdateSyncResult(completedResult)) {
      const metadata = getClientInstallationPresenceMetadata();
      if (metadata && cycle.companyId && cycle.userId) {
        const observation: PostUpdateSyncObservation = {
          companyId: cycle.companyId,
          userId: cycle.userId,
          traceId: String(completedResult.details?.traceId ?? cycle.traceId),
          startedAt: new Date(cycle.startedAt).toISOString(),
          finishedAt: new Date().toISOString(),
          outcome: completedResult.details?.finalStatus === 'partial'
            ? 'partial'
            : completedResult.details?.finalStatus === 'error' || !completedResult.success
              ? 'error'
              : 'success',
          result: completedResult,
          appVersion: metadata.appVersion,
          nativeBuild: metadata.nativeBuild,
          compatibilityBuild: metadata.compatibilityBuild,
          platform: metadata.platform,
          observedBuild: metadata.platform === 'web'
            ? metadata.compatibilityBuild
            : metadata.nativeBuild ?? metadata.compatibilityBuild,
        };
        postUpdateSyncObservers.forEach(observer => {
          try { void Promise.resolve(observer(observation)).catch(() => undefined); } catch { /* observador isolado */ }
        });
      }
    }
  }
};

export const pushLocalChanges = (options: SyncOptions = {}): Promise<SyncStatus> =>
  options.managedBySyncAll && activePendingSyncExecution
    ? pushLocalChangesInternal(options)
    : runReservedSync(() => pushLocalChangesInternal(options), 'push');

export const syncAll = (options: SyncOptions = {}): Promise<SyncStatus> =>
  runReservedSync(() => syncAllInternal(options));

const pushLocalChangesInternal = async (options: SyncOptions = {}): Promise<SyncStatus> => {
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    SyncLogger.Push.warn('[RECOVERY] Push remoto bloqueado pela quarentena');
    return recoveryDiagnosticSyncStatus();
  }

  if (isRecoveryRuntimeBlocked()) {
    SyncLogger.Push.warn('[RECOVERY] Push remoto bloqueado pelo gate runtime');
    return recoveryGateSyncStatus();
  }

  const startTime = Date.now();
  const traceLabel = options.tracePrefix ?? '';
  const tracePrefix = traceLabel ? `${traceLabel} ` : '';
  const logPushInfo = (message: string, ...args: any[]) => SyncLogger.Push.info(`${tracePrefix}${message}`, ...args);
  const logPushWarn = (message: string, ...args: any[]) => SyncLogger.Push.warn(`${tracePrefix}${message}`, ...args);
  const logPushError = (message: string, ...args: any[]) => SyncLogger.Push.error(`${tracePrefix}${message}`, ...args);
  logPushInfo('Iniciando envio de alterações locais para o servidor');

  // Verificar pré-requisitos
  const prerequisites = checkSyncPrerequisites();
  if (!prerequisites.allMet) {
    const errorMessage = 'Não é possível enviar alterações locais: pré-requisitos não atendidos';
    reportUnrecoveredSyncFailure({ code: prerequisites.networkConnected ? 'prerequisites_not_met' : 'offline', message: errorMessage, details: JSON.stringify(prerequisites) }, 'prerequisites', prerequisites.networkConnected);
    logPushWarn(errorMessage);
    return {
      success: false,
      message: errorMessage,
      timestamp: new Date(),
      details: prerequisites
    };
  }

  const managedBySyncAll = options.managedBySyncAll ?? false;
  const shouldManageState = !managedBySyncAll;
  const sessionSnapshot = activePendingSyncExecution?.snapshot;
  if (!sessionSnapshot) {
    return {
      success: false,
      message: 'Sessão de processamento de pendências não validada; fila preservada.',
      timestamp: new Date(),
      details: { reason: 'pending_sync_session_unverified' },
    };
  }

  try {
    await assertPendingSyncSessionCurrent(sessionSnapshot);
    // Atualizar estado global
    if (shouldManageState) {
      syncActions.setSyncRunning(true);
    }
    syncActions.setSyncStatus('idle');
    if (shouldManageState) {
      syncActions.updateProgress('push', 0, 0); // Estamos na etapa de push, começando com 0% completo
    }

    // Obter operações pendentes do banco de dados local
    logPushInfo('Obtendo operações pendentes do banco local');
    const cancellations = await processAutomaticChecklistCancellations(sessionSnapshot);
    if (cancellations.failed > 0) {
      logPushWarn(`${cancellations.failed} cancelamento(s) legado(s) não confirmado(s); filas preservadas`);
    }
    await prepareChecklistCancellations(sessionSnapshot.empresaId);
    await prepareArchivedPartialReplays({
      companyId: sessionSnapshot.empresaId,
      clientBuild: getSyncClientBuild(),
      client: createPendingSyncSupabaseClient(sessionSnapshot),
      database: databaseService,
      assertSession: () => assertPendingSyncSessionCurrent(sessionSnapshot),
      isSessionCurrent: () => isPendingSyncSessionSnapshotStateCurrent(sessionSnapshot),
      onPreserved: reason => SyncLogger.Main.warn('Replay arquivado preservado', { reason }),
    });
    const storedPendingOperations = await databaseService.getPendingOperations();
    await assertPendingSyncSessionCurrent(sessionSnapshot);
    const pendingOperations: PendingOperation[] = [];
    let blockedByOwnerOrCompany = 0;
    for (const operation of storedPendingOperations) {
      await assertPendingSyncSessionCurrent(sessionSnapshot);
      if (!await isChecklistCancellationBlocked(sessionSnapshot.empresaId, operation) &&
          await isPendingOperationEligibleForSession(operation, sessionSnapshot)) {
        pendingOperations.push(operation);
      } else {
        blockedByOwnerOrCompany++;
      }
    }
    await assertPendingSyncSessionCurrent(sessionSnapshot);

    if (pendingOperations.length === 0) {
      const { errors: mediaAckErrors, deferred: mediaAckDeferred } = await finalizePendingChecklistMedia(sessionSnapshot);
      logPushInfo(
        blockedByOwnerOrCompany > 0
          ? `Nenhuma das ${blockedByOwnerOrCompany} pendências foi comprovada para a sessão atual; filas preservadas`
          : 'Não há operações pendentes para sincronizar'
      );
      options.progressReporter?.({
        context: 'operations',
        processed: 0,
        success: 0,
        errors: 0,
        total: 0,
      });
      if (shouldManageState) {
        syncActions.updateProgress('push', 100, 30); // 100% do push concluído, 30% do total
      }
      return {
        success: mediaAckErrors === 0,
        message: mediaAckErrors > 0
          ? `${mediaAckErrors} mídia(s) ainda sem ACK exato; filas preservadas`
          : mediaAckDeferred > 0
          ? `${mediaAckDeferred} mídia(s) aguardando preparação ou confirmação; ACK adiado e filas preservadas`
          : blockedByOwnerOrCompany > 0
          ? 'Nenhuma operação pendente elegível para a sessão atual; filas preservadas'
          : 'Não há operações pendentes para sincronizar',
        timestamp: new Date(),
        details: { blockedByOwnerOrCompany, mediaAckErrors, mediaAckDeferred, deferredCount: 0, errorCount: mediaAckErrors },
      };
    }

    let tombstoneBarriers = options.tombstoneBarriers;

    const pendingOpsRequireTombstone =
      hasPendingOperationsRequiringChecklistTombstone(pendingOperations);

    if ((!managedBySyncAll || !tombstoneBarriers) && pendingOpsRequireTombstone) {
      const { companyId, userId } = requireTombstonePreflightScope();
      logPushInfo(
        managedBySyncAll
          ? 'TOMBSTONE_PREFLIGHT revalidando imediatamente antes do push'
          : 'TOMBSTONE_PREFLIGHT iniciado antes do push direto'
      );
      const tombstonePreflight = await pullCanaryTombstones({
        companyId,
        userId,
        logInfo: (message, ...args) => logPushInfo(message, ...args),
        mode: managedBySyncAll ? 'coordinated-write' : 'remote-write',
      });
      if (managedBySyncAll && !tombstonePreflight.enabled) {
        logPushWarn('PUSH protegido adiado; pendências preservadas para o próximo ciclo elegível');
        return tombstoneFeatureOffPushStatus();
      }
      tombstoneBarriers = tombstonePreflight.barriersByTable;
      logPushInfo('TOMBSTONE_PREFLIGHT concluído antes do push');
    } else if (pendingOpsRequireTombstone) {
      const { companyId } = requireTombstonePreflightScope();
      let featureLookupFailed = false;
      const tombstoneFeatureEnabled = managedBySyncAll
        ? await syncCanaryFeatureService.isEnabledOrFalse(
            companyId,
            SYNC_TOMBSTONES_FEATURE,
            getSyncClientBuild(),
            () => {
              featureLookupFailed = true;
            }
          )
        : await syncCanaryFeatureService.isEnabled(
            companyId,
            SYNC_TOMBSTONES_FEATURE,
            getSyncClientBuild()
          );
      if (!tombstoneFeatureEnabled) {
        if (managedBySyncAll && !featureLookupFailed) {
          logPushWarn('PUSH protegido adiado; pendências preservadas para o próximo ciclo elegível');
          return tombstoneFeatureOffPushStatus();
        }
        throw new Error('Feed de tombstones indisponivel para esta empresa/build.');
      }
    }

    logPushInfo(`Encontradas ${pendingOperations.length} operações pendentes para sincronizar`);

    // Configurar para rastrear progresso
    let processedCount = 0;
    let successCount = 0;
    let errorCount = 0;
    let deferredCount = 0;
    let terminalConflictCount = 0;

    // Agrupar operações por coleção para processar em blocos
    const operationsByCollection: Record<string, typeof pendingOperations> = {};

    pendingOperations.forEach(op => {
      if (!operationsByCollection[op.collection]) {
        operationsByCollection[op.collection] = [];
      }
      operationsByCollection[op.collection].push(op);
    });

    // Ordenação de coleções
    // Regra geral (default):
    // 1) guincho_templates_checklist (pai de campos)
    // 2) guincho_campos_template (pai de marcacao_imagens)
    // 3) guincho_marcacao_imagens (depende de campos/template)
    // 4) guincho_checklists (independente dos acima neste contexto)
    // 5) guincho_valores_checklist (filho de checklists)
    // Para operações de DELETE especificamente, queremos processar valores ANTES de checklists
    // para minimizar no-ops (apesar de o Supabase ter CASCADE, fica mais coerente com o fluxo local).
    const preferredOrderDefault = [
      APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME,
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
    ];
    const preferredOrderForDelete = [
      APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME,
      // Inverter apenas estes dois para operações de delete
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
    ];

    // Coleções com pelo menos uma operação delete
    const hasDeleteOps: Record<string, boolean> = Object.fromEntries(
      Object.entries(operationsByCollection).map(([col, ops]) => [
        col,
        ops.some(op => op.operation === 'delete')
      ])
    );

    const collections = Object.keys(operationsByCollection)
      .sort((a, b) => {
        const aHasDel = !!hasDeleteOps[a];
        const bHasDel = !!hasDeleteOps[b];

        // Coleções com DELETE vêm antes das demais
        const bucketA = aHasDel ? 0 : 1;
        const bucketB = bHasDel ? 0 : 1;
        if (bucketA !== bucketB) return bucketA - bucketB;

        // Dentro do mesmo bucket, usar a ordem apropriada
        const order = aHasDel ? preferredOrderForDelete : preferredOrderDefault;
        const ia = order.indexOf(a);
        const ib = order.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      });
    logPushInfo(`Operações agrupadas em ${collections.length} coleções (ordenadas): ${collections.join(', ')}`);

    // Processar cada coleção sequencialmente
    let sessionChangedDuringPush = false;
    for (let i = 0; i < collections.length; i++) {
      const collection = collections[i];
      let operations = operationsByCollection[collection];

      // Ordenar operações por timestamp ascendente para preservar ordem de criação
      operations.sort((a, b) => {
        const ta = (a as any).timestamp ?? 0;
        const tb = (b as any).timestamp ?? 0;
        return ta - tb;
      });
      const firstTs = operations[0] ? new Date((operations[0] as any).timestamp || 0).toISOString() : 'n/a';
      const lastTs = operations[operations.length - 1] ? new Date((operations[operations.length - 1] as any).timestamp || 0).toISOString() : 'n/a';
      logPushInfo(`Processando ${operations.length} operações na coleção "${collection}" (${i + 1}/${collections.length}) [ordem por timestamp asc: first=${firstTs}, last=${lastTs}]`);

      // Processar cada operação na ordem
      for (let j = 0; j < operations.length; j++) {
        const operation = operations[j];

        try {
          await assertPendingSyncSessionCurrent(sessionSnapshot);
          if (await isChecklistCancellationBlocked(sessionSnapshot.empresaId, operation)) continue;
          logPushInfo(`Processando operação ${operation.operation} para o item ${operation.itemId} na coleção ${operation.collection}`);

          // Processar com base no tipo de operação
          let result = false;
          let pendingHandledByV2 = false;
          let pendingChangedDuringV2 = false;
          let terminalConflict = false;
          let partialDeferred = false;
          let partialReason: ChecklistPartialUpdateReason | undefined;

          const tombstoneDecision = await resolvePendingOperationTombstoneDecision(
            operation,
            tombstoneBarriers
          );

          if (tombstoneDecision === 'blocked') {
            logPushWarn(
              `Operação ${operation.id} bloqueada por tombstone local aplicado no preflight; classificando conflito terminal`
            );
            const reconciliation = await reconcilePendingOperationBlockedByTombstone(
              operation,
              tombstoneBarriers,
              logPushInfo,
              logPushWarn
            );
            if (reconciliation === 'accounted') {
              result = true;
              terminalConflict = true;
              terminalConflictCount++;
            }
          } else if (tombstoneDecision === 'uncertain') {
            if (await isValueDeleteWaitingForParentRetirement(operation, pendingOperations, sessionSnapshot)) {
              partialDeferred = true;
              partialReason = 'parent_retirement_waiting';
            } else {
              logPushWarn(
                `Operação ${operation.id} sem prova local suficiente da relação ancestral; mantendo pendência sem push`
              );
            }
          } else {
            switch (operation.operation) {
              case 'create':
                {
                  const createResult = await processCreateOperation(collection, operation as CreateOp);
                  result = createResult === 'accounted' || createResult === true;
                  pendingHandledByV2 = createResult === 'accounted';
                }
                break;
              case 'update':
                result = await processUpdateOperation(collection, operation as UpdateOp);
                break;
              case 'delete':
                result = await processDeleteOperation(collection, operation as DeleteOp);
                break;
              case 'checklist_partial_update':
                {
                  const partialResult = await processChecklistPartialUpdate(operation);
                  result = partialResult.success;
                  pendingHandledByV2 = partialResult.pendingHandledByV2;
                  pendingChangedDuringV2 = Boolean(partialResult.pendingChanged);
                  partialDeferred = partialResult.deferred === true;
                  partialReason = partialResult.reason;
                }
                break;
              case 'checklist_delete':
                result = await processChecklistDelete(operation);
                break;
              default:
                throw new Error(`Tipo de operação desconhecido: ${operation.operation}`);
            }
          }

          if (result) {
            let shouldRemovePending = terminalConflict || pendingHandledByV2
              ? false
              : await shouldRemovePendingOperationAfterSuccess(
                  operation,
                  logPushInfo,
                  logPushWarn
                );

            if (shouldRemovePending) {
              const pendingSnapshot = buildPendingOperationSnapshot(operation);
              if (!pendingSnapshot) {
                logPushWarn(
                  `Operação ${operation.id} sem snapshot completo no ACK; mantendo pendência por segurança`
                );
              } else {
                // Remover operação pendente somente se ela ainda corresponde ao
                // payload que foi efetivamente confirmado no servidor.
                const removed = await databaseService.removePendingOperationIfUnchanged(
                  operation.id,
                  pendingSnapshotToExpectedRaw(pendingSnapshot),
                  () => isPendingSyncSessionSnapshotStateCurrent(sessionSnapshot)
                );
                if (!removed) {
                  shouldRemovePending = false;
                  logPushWarn(
                    `Operação ${operation.id} mudou antes do ACK local; mantendo pendência atual`
                  );
                }
              }
            }

            successCount++;
            logPushInfo(
              terminalConflict
                ? `Operação ${operation.id} classificada como conflito terminal tombstoned_entity; payload preservado em evidencia local`
                : pendingHandledByV2
                ? pendingChangedDuringV2
                  ? `Operacao ${operation.id} processada pelo sync v2 e preservada porque mudou durante o envio`
                  : `Operacao ${operation.id} finalizada pelo sync v2 com evidencia local`
                : shouldRemovePending
                  ? `Operação ${operation.id} processada com sucesso e removida do banco local`
                  : `Operação ${operation.id} processada com sucesso, mas mantida localmente porque recebeu alterações mais novas`
            );
          } else if (partialDeferred) {
            deferredCount++;
            logPushInfo(`Operação ${operation.id} adiada; reason=${partialReason}; pendência preservada`);
          } else {
            errorCount++;
            logPushWarn(`Operação ${operation.id} não aplicada integralmente; reason=${partialReason ?? 'processing_failed'}; consultar pendências e conflitos locais`);
          }
        } catch (error) {
          errorCount++;
          if (error instanceof PendingSyncSessionChangedError) {
            sessionChangedDuringPush = true;
            logPushWarn('Sessão mudou durante o push; operações restantes preservadas.');
          }
          reportUnrecoveredSyncFailure(error, 'push_operation');
          logPushError(`Erro ao processar operação ${operation.id}:`, error);
        }

        // Atualizar progresso
        processedCount++;
        const stageProgress = Math.floor((processedCount / pendingOperations.length) * 100);
        // Total de 30% para push (0-30%)
        const overallProgress = Math.floor((processedCount / pendingOperations.length) * 30);

        if (shouldManageState) {
          syncActions.updateProgress('push', stageProgress, overallProgress);
        }

        logPushInfo(`Progresso: ${processedCount}/${pendingOperations.length} (${stageProgress}%)`);

        // Notifica o orquestrador (syncAll/Home) sobre o avanço do push
        options.progressReporter?.({
          context: 'operations',
          processed: processedCount,
          success: successCount,
          errors: errorCount,
          total: pendingOperations.length,
        });

        if (sessionChangedDuringPush) break;
      }
      if (sessionChangedDuringPush) break;
    }

    const { errors: mediaAckErrors, deferred: mediaAckDeferred } = sessionChangedDuringPush
      ? { errors: 0, deferred: 0 } : await finalizePendingChecklistMedia(sessionSnapshot);
    if (!sessionChangedDuringPush) {
      options.progressReporter?.({ context: 'operations', processed: processedCount, success: successCount,
        errors: errorCount, total: pendingOperations.length });
    }
    errorCount += mediaAckErrors;
    // Finalizar e retornar status
    const elapsedTime = Date.now() - startTime;

    if (errorCount === 0) {
      logPushInfo(`Sincronização push concluída com sucesso em ${elapsedTime}ms. ${successCount} operações processadas.`);
      return {
        success: true,
        message: `${successCount} operações processadas com sucesso${deferredCount > 0 ? `; ${deferredCount} operação(ões) adiada(s), com pendências preservadas` : ''}${mediaAckDeferred > 0 ? `; ${mediaAckDeferred} mídia(s) aguardando preparação ou confirmação; ACK adiado` : ''}`,
        timestamp: new Date(),
        details: {
          processedCount,
          successCount,
          errorCount,
          deferredCount,
          mediaAckErrors,
          mediaAckDeferred,
          terminalConflictCount,
          elapsedTime
        }
      };
    } else {
      logPushWarn(`Sincronização push concluída parcialmente em ${elapsedTime}ms. ${successCount} sucessos, ${errorCount} erros.`);
      captureSentryMessageWithScope('Push local concluído com erros', {
        level: 'warning',
        context: getSyncSentryBaseContext({
          flow: 'sync',
          stage: 'push_local_changes',
          processedCount,
          successCount,
          errorCount,
          elapsedTime,
          tracePrefix: traceLabel || null,
        }),
        tags: {
          flow: 'sync',
          stage: 'push',
          result: 'partial',
        },
        fingerprint: ['sync', 'push_partial'],
        dedupeKey: 'sync:push_partial',
        dedupeWindowMs: 60000,
      });
      return {
        success: false,
        message: `Sincronização parcial: ${successCount} sucessos, ${errorCount} erros${deferredCount > 0 ? `; ${deferredCount} operação(ões) adiada(s)` : ''}`,
        timestamp: new Date(),
        details: {
          processedCount,
          successCount,
          errorCount,
          deferredCount,
          mediaAckErrors,
          mediaAckDeferred,
          terminalConflictCount,
          elapsedTime
        }
      };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
    reportUnrecoveredSyncFailure(error, 'push_local_changes');
    const pushChangesErrorMessage = 'Erro durante push de alterações locais';
    logPushWarn(`${pushChangesErrorMessage}:`, error);
    captureSentryExceptionWithScope(error, {
      context: getSyncSentryBaseContext({
        flow: 'sync',
        stage: 'push_local_changes',
        tracePrefix: traceLabel || null,
        loggerMessage: pushChangesErrorMessage,
      }),
      tags: {
        flow: 'sync',
        stage: 'push',
      },
      fingerprint: ['sync', 'push_exception'],
      dedupeKey: 'sync:push_exception',
      dedupeWindowMs: 30000,
    });

    // Atualizar estado global em caso de erro
    syncActions.setSyncError(errorMessage);

    return {
      success: false,
      message: `Erro durante push: ${errorMessage}`,
      timestamp: new Date(),
      details: { error }
    };
  } finally {
    if (!managedBySyncAll) {
      syncActions.setSyncRunning(false);
    }
  }
};

/**
 * Processa uma operação de criação no Supabase
 * 
 * @param collection Nome da coleção/tabela
 * @param operation Operação pendente a ser processada
 * @returns Boolean indicando sucesso
 */
const acknowledgeExistingChecklistCreate = async (
  operation: CreateOp, payload: Record<string, any>, remote: Record<string, any>
): Promise<boolean> => {
  const snapshot = buildPendingOperationSnapshot(operation);
  const scope = requireTombstonePreflightScope();
  if (!snapshot || payload.id !== operation.itemId || payload.empresa_id !== scope.companyId) return false;
  const serialized = serializePendingOperationData(operation.data);
  const hash = sha512Hex(serialized ?? '');
  const id = `accounted:create:${operation.id}:${hash.slice(0, 24)}`;
  return new ChecklistCreateAcknowledgementService({
    enabled: companyId => syncCanaryFeatureService.isEnabled(companyId, 'checklist_snapshots_v1', getSyncClientBuild()),
    readLocal: () => databaseService.readItem(operation.collection, operation.itemId),
    async readSnapshot(snapshotId, companyId, remoteRead) {
      if (!remoteRead) return databaseService.readItem(historicalTables.snapshots, snapshotId);
      const result = await getSyncSupabaseClient().from(historicalTables.snapshots).select('*').eq('id', snapshotId).eq('empresa_id', companyId).maybeSingle();
      if (result.error) throw result.error;
      return result.data;
    },
    unchanged: () => pendingSnapshotStillMatches(operation),
    async archive() {
      const row = {
        id, empresa_id: scope.companyId, user_id: scope.userId, operation: operation.operation,
        collection: operation.collection, item_id: operation.itemId, data: serialized,
        original_timestamp: operation.timestamp, accounting_state: 'superseded_by_remote_entity',
        reason: 'checklist_create_already_represented', receipt_id: null, payload_sha512: hash,
      };
      const existing = await databaseService.readItem<Record<string, any>>('accounted_pending_operations', id);
      if (existing) {
        if (Object.entries(row).some(([key, value]) => existing[key] !== value)) throw new Error('Evidência de CREATE diverge do arquivo existente.');
      } else {
        const now = Date.now();
        await databaseService.createItem('accounted_pending_operations', { ...row, accounted_at: now, created_at: now, updated_at: now }, true);
      }
    },
    remove: () => databaseService.removePendingOperationIfUnchanged(
      operation.id,
      pendingSnapshotToExpectedRaw(snapshot),
      () => Boolean(activePendingSyncExecution && isPendingSyncSessionSnapshotStateCurrent(activePendingSyncExecution.snapshot))
    ),
  }).process(payload, remote);
};

const resolveHistoricalCreatePayload = async (payload: Record<string, any>) => {
  const scope = requireTombstonePreflightScope();
  return resolveHistoricalValueCreateReference(payload, scope, {
    enabled: () => syncCanaryFeatureService.isEnabled(scope.companyId, HISTORICAL_SNAPSHOTS_FEATURE, getSyncClientBuild()),
    async fetch(table, filters) {
      let query = getSyncSupabaseClient().from(table).select('*');
      for (const [key, value] of Object.entries(filters)) query = query.eq(key, value);
      const result = await query.limit(2);
      if (result.error) throw result.error;
      return result.data ?? [];
    },
  });
};

const processCreateOperation = async (
  collection: string,
  operation: CreateOp
): Promise<boolean | 'accounted'> => {
  try {
    SyncLogger.OpCreate.info(`Processando criação na coleção ${collection} do item ${operation.itemId}`);

    if (!operation.data) {
      throw new Error('Dados ausentes para operação de criação');
    }

    // Garantir que os dados estejam em formato de objeto
    // Se for uma string JSON, fazer o parse
    let parsedData;
    if (typeof operation.data === 'string') {
      try {
        SyncLogger.OpCreate.info('Dados recebidos como string, convertendo para objeto');
        parsedData = JSON.parse(operation.data);
      } catch (parseError) {
        SyncLogger.OpCreate.error('Erro ao fazer parse dos dados:', parseError);
        throw new Error('Formato de dados inválido');
      }
    } else {
      SyncLogger.OpCreate.info('Dados recebidos como objeto, mantendo como está');
      parsedData = operation.data;
    }

    // Verificar se há configuração de sincronização para esta tabela
    const tableConfig = getTableSyncConfig(collection);
    if (!tableConfig || !tableConfig.enabled || tableConfig.pushEnabled === false) {
      SyncLogger.OpConfig.warn(`Tabela ${collection} não está configurada para sincronização ou está desabilitada`);
      return false;
    }

    // Preparar dados para inserção (agora com garantia de ser um objeto)
    let dataToInsert = { ...parsedData };
    const isChecklistCollection = collection === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME;
    let exactValueCreate = false;
    const isValueCreate = collection === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME;
    if (isValueCreate) {
      const resolved = await resolveHistoricalCreatePayload(dataToInsert);
      if (!resolved) return false;
      dataToInsert = resolved.payload;
      exactValueCreate = resolved.historical || getSyncClientBuild() >= 124;
      if (exactValueCreate && (dataToInsert.id !== operation.itemId
        || !await pendingSnapshotStillMatches(operation))) return false;
    }

    // Adicionar campos de timestamp se não existirem
    const operationTime = new Date(operation.timestamp);
    const stableOperationTimestamp = Number.isFinite(operationTime.getTime())
      ? operationTime.toISOString()
      : new Date().toISOString();
    if (!dataToInsert.created_at) {
      dataToInsert.created_at = stableOperationTimestamp;
    }
    if (!dataToInsert.updated_at) {
      dataToInsert.updated_at = stableOperationTimestamp;
    }

    if (isChecklistCollection) {
      const sessionSnapshot = activePendingSyncExecution?.snapshot;
      const relay = Boolean(sessionSnapshot && getSyncClientBuild() >= 124);
      if (relay && !sessionSnapshot) throw new PendingSyncSessionChangedError();
      if (relay) {
        const snapshotResult = await processChecklistSnapshotCreate(operation, dataToInsert);
        if (snapshotResult !== null) return snapshotResult;

        // Checklist simples só usa esta rota quando a feature de snapshots está
        // desligada. A RPC valida empresa e destino sem substituir a origem.
        const normalizedChecklist = normalizeDateFieldsForSupabase(dataToInsert);
        await assertPendingSyncSessionCurrent(sessionSnapshot!);
        const { data, error } = await getSyncSupabaseClient().rpc('transmit_checklist_create_v1', {
          p_checklist: normalizedChecklist,
          p_snapshot_id: null,
          p_fields: null,
          p_client_build: getSyncClientBuild(),
          p_operation_id: operation.id,
          p_claimed_origin_user_id: operation.user_id ?? null,
        });
        if (error) throw error;
        await assertPendingSyncSessionCurrent(sessionSnapshot!);
        return isChecklistRelayReceiptValid(
          data,
          operation,
          normalizedChecklist,
          sessionSnapshot!.empresaId,
        );
      }

      const { data: existingChecklist, error: existingChecklistError } = await getSyncSupabaseClient()
        .from(collection)
        .select('*')
        .eq('id', operation.itemId)
        .eq('empresa_id', dataToInsert.empresa_id)
        .maybeSingle();

      if (existingChecklistError) {
        SyncLogger.OpCreate.error(`Erro ao verificar existência do checklist em ${collection}:`, existingChecklistError);
        return false;
      }

      if (existingChecklist?.updated_at) {
        const localDate = new Date(dataToInsert.updated_at);
        const remoteDate = new Date(existingChecklist.updated_at);
        if (remoteDate > localDate) {
          if (await acknowledgeExistingChecklistCreate(operation, parsedData, existingChecklist)) return 'accounted';
          SyncLogger.OpCreate.warn('Checklist remoto é mais recente; sem prova completa de criação já representada, preservando pendência local');
          return false;
        }
      }

      const snapshotResult = await processChecklistSnapshotCreate(operation, dataToInsert);
      if (snapshotResult !== null) return snapshotResult;
    }

    // Verificar se o item já existe no Supabase
    let existingQuery = exactValueCreate
      ? getSyncSupabaseClient().from(collection).select('*')
      : getSyncSupabaseClient().from(collection).select('id, updated_at');
    if (exactValueCreate) {
      existingQuery = existingQuery.eq('empresa_id', dataToInsert.empresa_id)
        .eq('checklist_id', dataToInsert.checklist_id);
    }
    const { data: existingItem, error: checkError } = await existingQuery
      .eq('id', operation.itemId)
      .maybeSingle();

    if (checkError) {
      SyncLogger.OpCreate.error(`Erro ao verificar existência do item em ${collection}:`, checkError);
      if (exactValueCreate) return false;
    }

    if (existingItem) {
      if (exactValueCreate) {
        return historicalValueCreateMatches(normalizeDateFieldsForSupabase(dataToInsert), existingItem);
      }
      SyncLogger.OpCreate.warn(`Item ${operation.itemId} já existe no Supabase, tratando como atualização`);

      // Verificar se os dados locais são mais recentes
      let localDate = new Date(dataToInsert.updated_at);
      let remoteDate = new Date(existingItem.updated_at);

      // Se o registro remoto for mais recente, não devemos sobrescrever
      if (remoteDate > localDate) {
        SyncLogger.OpCreate.warn('Item remoto é mais recente; preservando pendência local para resolução explícita');
        return false;
      }

      // Normalizar campos de data para formato ISO
      const normalizedData = normalizeDateFieldsForSupabase(dataToInsert);
      SyncLogger.OpCreate.info('Dados normalizados para envio ao Supabase (atualização)');

      // Remover created_at para não sobrescrever o timestamp original do servidor
      if (normalizedData.created_at) {
        SyncLogger.OpCreate.info(`Removendo campo created_at da operação de atualização de item existente para preservar valor original`);
        delete normalizedData.created_at;
      }

      // Proteger numeração sequencial gerada no servidor
      if (isChecklistCollection) {
        if ('sequencial_numero' in normalizedData) {
          SyncLogger.OpCreate.info('Removendo sequencial_numero do update de item existente para preservar valor gerado pelo trigger');
          delete normalizedData.sequencial_numero;
        }
        if ('sequencial_codigo' in normalizedData) {
          SyncLogger.OpCreate.info('Removendo sequencial_codigo do update de item existente para preservar valor gerado pelo trigger');
          delete normalizedData.sequencial_codigo;
        }
      }

      // Garantir updated_at monotônico para checklists
      if (isChecklistCollection) {
        const rawUpdatedAt = (dataToInsert as any).updated_at;
        const parsedUpdatedAt = typeof rawUpdatedAt === 'number' ? rawUpdatedAt : Date.parse(rawUpdatedAt);
        const nowMs = Date.now();
        const safeMs = Number.isFinite(parsedUpdatedAt) ? Math.max(nowMs, parsedUpdatedAt + 1) : nowMs;
        normalizedData.updated_at = new Date(safeMs).toISOString();
        SyncLogger.OpCreate.info(`Forçando updated_at no update de item existente do checklist para ${normalizedData.updated_at} para manter ordenação de sync`);
      }

      if (isChecklistCollection) await assertChecklistNotCancelled(String(dataToInsert.empresa_id), operation.itemId);
      // Realizar atualização ao invés de inserção
      const { data: updateData, error: updateError } = await getSyncSupabaseClient()
        .from(collection)
        .update(normalizedData)
        .eq('id', operation.itemId)
        .select();

      if (updateError) {
        SyncLogger.OpCreate.error(`Erro ao atualizar item existente em ${collection}:`, updateError);
        return false;
      }

      if (!Array.isArray(updateData) || updateData.length !== 1) {
        SyncLogger.OpCreate.warn(`Update de create existente sem linha afetada em ${collection}, mantendo pendencia.`);
        return false;
      }

      SyncLogger.OpCreate.info(`Item existente atualizado com sucesso em ${collection}, ID: ${operation.itemId}`);
      return true;
    }

    // Normalizar campos de data para formato ISO
    const normalizedData = normalizeDateFieldsForSupabase(dataToInsert);
    SyncLogger.OpCreate.info('Dados normalizados para envio ao Supabase (inserção)');

    // Registrar timestamp da operação para fins de auditoria
    const operationTimestamp = new Date().toISOString();
    SyncLogger.OpCreate.info(`Timestamp da operação de inserção: ${operationTimestamp}`);

    // Adicionar informação de log sobre o item sendo inserido
    SyncLogger.OpCreate.info(`Tentando inserir novo item em ${collection} com ID: ${operation.itemId} às ${operationTimestamp}`);
    if (isChecklistCollection) await assertChecklistNotCancelled(String(dataToInsert.empresa_id), operation.itemId);
    // Se não existir, realizar inserção normal
    const { data, error } = await getSyncSupabaseClient()
      .from(collection)
      .insert(normalizedData)
      .select();

    if (error) {
      if (exactValueCreate) {
        SyncLogger.OpCreate.error('CREATE de resposta recusado; preservando pendência', { code: error.code });
        return false;
      }
      // Se ocorrer erro de chave duplicada mesmo com nossa verificação, tratar como caso especial
      if (error.code === '23505') {
        SyncLogger.OpCreate.warn(`Conflito de chave duplicada mesmo após verificação em ${collection}, tentando atualizar`);

        // Remover created_at para não sobrescrever o timestamp original do servidor
        if (normalizedData.created_at) {
          SyncLogger.OpCreate.info(`Removendo campo created_at da operação de atualização de fallback para preservar valor original`);
          delete normalizedData.created_at;
        }

        // Proteger numeração sequencial gerada no servidor
        if (isChecklistCollection) {
          if ('sequencial_numero' in normalizedData) {
            SyncLogger.OpCreate.info('Removendo sequencial_numero do update de fallback para preservar valor gerado pelo trigger');
            delete normalizedData.sequencial_numero;
          }
          if ('sequencial_codigo' in normalizedData) {
            SyncLogger.OpCreate.info('Removendo sequencial_codigo do update de fallback para preservar valor gerado pelo trigger');
            delete normalizedData.sequencial_codigo;
          }

          const rawUpdatedAt = (dataToInsert as any).updated_at;
          const parsedUpdatedAt = typeof rawUpdatedAt === 'number' ? rawUpdatedAt : Date.parse(rawUpdatedAt);
          const nowMs = Date.now();
          const safeMs = Number.isFinite(parsedUpdatedAt) ? Math.max(nowMs, parsedUpdatedAt + 1) : nowMs;
          normalizedData.updated_at = new Date(safeMs).toISOString();
          SyncLogger.OpCreate.info(`Forçando updated_at no update de fallback do checklist para ${normalizedData.updated_at} para manter ordenação de sync`);
        }

        // Registrar timestamp da operação de atualização
        const updateTimestamp = new Date().toISOString();
        SyncLogger.OpCreate.info(`Timestamp da operação de atualização: ${updateTimestamp}`);

        // Tentar atualizar como último recurso
        const { data: fallbackData, error: fallbackError } = await getSyncSupabaseClient()
          .from(collection)
          .update(normalizedData)
          .eq('id', operation.itemId)
          .select();

        if (fallbackError) {
          SyncLogger.OpCreate.error(`Também falhou ao tentar atualizar item em ${collection}:`, fallbackError);
          return false;
        }

        if (!Array.isArray(fallbackData) || fallbackData.length !== 1) {
          SyncLogger.OpCreate.warn(`Update de fallback sem linha afetada em ${collection}, mantendo pendencia.`);
          return false;
        }

        SyncLogger.OpCreate.info(`Item atualizado com sucesso (após conflito) em ${collection}, ID: ${operation.itemId}`);
        return true;
      }

      SyncLogger.OpCreate.error(`Erro ao inserir em ${collection}:`, error);
      return false;
    }

    if (exactValueCreate && (!Array.isArray(data) || data.length !== 1
      || !historicalValueCreateMatches(normalizedData, data[0]))) return false;
    SyncLogger.OpCreate.info(`Item criado com sucesso em ${collection}, ID: ${operation.itemId}`);
    return true;
  } catch (error) {
    reportUnrecoveredSyncFailure(error, 'push_create');
    SyncLogger.OpCreate.error(`Erro ao processar operação de criação para ${collection}:`, error);
    return false;
  }
};

type SatisfiedFieldUpdateReadback = 'satisfied' | 'different' | 'remote_newer' | 'unverifiable';

const canonicalComparableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonicalComparableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, canonicalComparableValue(entry)]));
  }
  return value;
};

const readSatisfiedChecklistFieldUpdate = async (
  collection: string,
  itemId: string,
  payload: Record<string, any>,
): Promise<SatisfiedFieldUpdateReadback> => {
  const fieldsTable = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME;
  const snapshot = activePendingSyncExecution?.snapshot;
  if (collection !== fieldsTable || !snapshot || payload.empresa_id !== snapshot.empresaId ||
      (payload.id !== undefined && payload.id !== itemId)) return 'different';

  const businessEntries = Object.entries(payload).filter(([key]) =>
    !['id', 'empresa_id', 'created_at', 'updated_at'].includes(key)
  );
  if (businessEntries.length === 0) return 'different';

  await assertPendingSyncSessionCurrent(snapshot);
  const { data, error } = await getSyncSupabaseClient()
    .from(collection)
    .select('*')
    .eq('id', itemId)
    .maybeSingle();
  await assertPendingSyncSessionCurrent(snapshot);
  if (error) return 'unverifiable';
  if (!data) return 'different';
  if (data.id !== itemId || data.empresa_id !== snapshot.empresaId) return 'unverifiable';

  const exact = businessEntries.every(([key, expected]) =>
    JSON.stringify(canonicalComparableValue(data[key])) ===
      JSON.stringify(canonicalComparableValue(expected))
  );
  if (exact) return 'satisfied';
  const remoteUpdatedAt = Date.parse(String(data.updated_at ?? ''));
  const localUpdatedAt = Date.parse(String(payload.updated_at ?? ''));
  if ((data.retired_at != null && payload.retired_at == null) ||
      (Number.isFinite(remoteUpdatedAt) && Number.isFinite(localUpdatedAt) &&
        remoteUpdatedAt > localUpdatedAt)) return 'remote_newer';
  return 'different';
};

/**
 * Processa uma operação de atualização no Supabase
 * 
 * @param collection Nome da coleção/tabela
 * @param operation Operação pendente a ser processada
 * @returns Boolean indicando sucesso
 */
const processUpdateOperation = async (
  collection: string,
  operation: UpdateOp
): Promise<boolean> => {
  try {
    SyncLogger.OpUpdate.info(`Processando atualização na coleção ${collection} do item ${operation.itemId}`);

    if (!operation.data) {
      throw new Error('Dados ausentes para operação de atualização');
    }

    // Garantir que os dados estejam em formato de objeto
    // Se for uma string JSON, fazer o parse
    let parsedData;
    if (typeof operation.data === 'string') {
      try {
        SyncLogger.OpUpdate.info('Dados recebidos como string, convertendo para objeto');
        parsedData = JSON.parse(operation.data);
      } catch (parseError) {
        SyncLogger.OpUpdate.error('Erro ao fazer parse dos dados:', parseError);
        throw new Error('Formato de dados inválido');
      }
    } else {
      parsedData = operation.data;
    }

    // Verificar se há configuração de sincronização para esta tabela
    const tableConfig = getTableSyncConfig(collection);
    if (!tableConfig || !tableConfig.enabled || tableConfig.pushEnabled === false) {
      SyncLogger.OpConfig.warn(`Tabela ${collection} não está configurada para sincronização ou está desabilitada`);
      return false;
    }

    // Preparar dados para atualização (agora com garantia de ser um objeto)
    const dataToUpdate = { ...parsedData };

    // Proteção: não sobrescrever numeração sequencial do checklist com valores nulos/defasados
    const isChecklistUpdate = collection === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME;
    if (isChecklistUpdate) {
      delete (dataToUpdate as any).sequencial_numero;
      delete (dataToUpdate as any).sequencial_codigo;     
    }

    // Remover o campo created_at para não sobrescrever o valor original no servidor
    if (dataToUpdate.created_at) {
      SyncLogger.OpUpdate.info(`Removendo campo created_at da operação de atualização para preservar valor original`);
      delete dataToUpdate.created_at;
    }

    // Garantir updated_at monotônico para checklists (evita regravar com timestamp antigo e bloquear PULL/realtime)
    if (isChecklistUpdate) {
      const rawUpdatedAt = (dataToUpdate as any).updated_at;
      const parsedUpdatedAt = typeof rawUpdatedAt === 'number' ? rawUpdatedAt : Date.parse(rawUpdatedAt);
      const nowMs = Date.now();
      const safeMs = Number.isFinite(parsedUpdatedAt) ? Math.max(nowMs, parsedUpdatedAt + 1) : nowMs;
      (dataToUpdate as any).updated_at = new Date(safeMs).toISOString();
      SyncLogger.OpUpdate.info(`Forçando updated_at do checklist para ${dataToUpdate.updated_at} para manter ordenação de sync`);
    } else {
      // Preservar o timestamp original do updated_at da edição local, em vez de criar um novo
      if (!dataToUpdate.updated_at) {
        // Só definimos um novo timestamp se não existir
        dataToUpdate.updated_at = new Date().toISOString();
        SyncLogger.OpUpdate.info(`Definindo novo timestamp updated_at: ${dataToUpdate.updated_at}`);
      } else {
        SyncLogger.OpUpdate.info(`Preservando timestamp updated_at original da edição local: ${dataToUpdate.updated_at}`);
      }
    }

    // Normalizar campos de data para formato ISO
    const normalizedData = normalizeDateFieldsForSupabase(dataToUpdate);
    SyncLogger.OpUpdate.info('Dados normalizados para envio ao Supabase');

    // Uma definição já restaurada no catálogo canônico deve apenas ser lida e
    // reconhecida. Reaplicar o payload antigo poderia retroceder timestamps.
    const fieldReadback = await readSatisfiedChecklistFieldUpdate(
      collection,
      operation.itemId,
      normalizedData as Record<string, any>,
    );
    if (fieldReadback === 'satisfied') {
      SyncLogger.OpUpdate.info(`Definição do campo já corresponde ao servidor; sem replay: ${operation.itemId}`);
      return true;
    }
    if (fieldReadback === 'unverifiable') {
      SyncLogger.OpUpdate.warn(`Readback do campo sem escopo verificável; mantendo pendência: ${operation.itemId}`);
      return false;
    }
    if (fieldReadback === 'remote_newer') {
      SyncLogger.OpUpdate.warn(`Campo remoto divergiu após a edição local; mantendo pendência: ${operation.itemId}`);
      return false;
    }

    // Registrar timestamp da operação para fins de auditoria
    const operationTimestamp = Date.now();
    SyncLogger.OpUpdate.info(`Timestamp da operação de atualização: ${operationTimestamp} (${new Date(operationTimestamp).toISOString()})`);

    // Realizar atualização no Supabase
    const { data, error } = await getSyncSupabaseClient()
      .from(collection)
      .update(normalizedData)
      .eq('id', operation.itemId)
      .select();

    SyncLogger.OpUpdate.info(`Resultado da operação de atualização: linhas=${Array.isArray(data) ? data.length : 0}`);

    if (error) {
      SyncLogger.OpUpdate.error(`Erro ao atualizar em ${collection}:`, error);
      return false;
    }

    if (!Array.isArray(data) || data.length !== 1) {
      SyncLogger.OpUpdate.warn(`Atualizacao sem linha afetada em ${collection}, mantendo pendencia.`);
      return false;
    }

    SyncLogger.OpUpdate.info(`Item atualizado com sucesso em ${collection}, ID: ${operation.itemId}`);
    return true;
  } catch (error) {
    reportUnrecoveredSyncFailure(error, 'push_update');
    SyncLogger.OpUpdate.error(`Erro ao processar operação de atualização para ${collection}:`, error);
    return false;
  }
};

/**
 * Processa uma operação de exclusão no Supabase
 * 
 * @param collection Nome da coleção/tabela
 * @param operation Operação pendente a ser processada
 * @returns Boolean indicando sucesso
 */
const processDeleteOperation = async (
  collection: string,
  operation: DeleteOp
): Promise<boolean> => {
  try {
    SyncLogger.OpDelete.info(`Processando exclusão na coleção ${collection} do item ${operation.itemId}`);

    // Verificar se há configuração de sincronização para esta tabela
    const tableConfig = getTableSyncConfig(collection);
    if (!tableConfig || !tableConfig.enabled || tableConfig.pushEnabled === false) {
      SyncLogger.OpConfig.warn(`Tabela ${collection} não está configurada para sincronização ou está desabilitada`);
      return false;
    }

    const userId = globalState.user.user.get()?.id;
    const userProfile = userId
      ? await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, userId)
      : null;
    const companyId = userProfile?.empresa_id;
    const tombstoneEnabled = companyId
      ? await syncCanaryFeatureService.isEnabled(
          String(companyId),
          SYNC_TOMBSTONES_FEATURE,
          getSyncClientBuild()
        )
      : false;

    if (tombstoneEnabled) {
      const existingLocalTombstone = await databaseService.listItems<any>('sync_tombstones', {
        entity_type: collection,
        entity_id: operation.itemId,
      });
      const expectedDeletionKind = [
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
        APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
      ].includes(collection) ? 'retire' : 'delete';
      if (existingLocalTombstone.some(row => row.empresa_id === companyId &&
          row.entity_type === collection && row.entity_id === operation.itemId &&
          row.deletion_kind === expectedDeletionKind)) {
        SyncLogger.OpDelete.info(`Delete em ${collection} já aplicado via tombstone local; operação concluída idempotentemente`);
        return true;
      }

      if (collection === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME) {
        const valuesTable = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME;
        const guard = await verifyFieldDeleteDependencies({
          fieldId: operation.itemId,
          companyId: String(companyId),
          deleteOperationId: operation.id,
          fieldTable: collection,
          checklistTable: APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
          valuesTable,
        }, {
          pending: () => databaseService.getPendingOperations(),
          localValues: (fieldId, scope) => databaseService.listItems<any>(valuesTable, {
            empresa_id: scope,
            campo_id: fieldId,
          }),
        });
        if (!guard.safe) {
          SyncLogger.OpDelete.warn(`Delete de campo adiado ate tratamento das dependencias: ${guard.reason}`);
          return false;
        }
      }

      const deletePayload = typeof operation.data === 'string' ? JSON.parse(operation.data) : operation.data;
      const partnerRevision = deletePayload?.expected_partner_revision;
      const { data, error } = await getSyncSupabaseClient().rpc(partnerRevision !== undefined ? 'delete_sync_entity_partner_v1' : 'delete_sync_entity_v2', {
        p_entity_type: collection,
        p_entity_id: operation.itemId,
        p_idempotency_key: `sync-delete:${operation.id}`.slice(0, 128),
        ...(partnerRevision !== undefined ? { p_expected_partner_revision: partnerRevision } : {}),
        p_client_build: getSyncClientBuild(),
      });
      const sequence = Number(data?.sequence);
      if (error || !data || data.entity_type !== collection ||
          data.entity_id !== operation.itemId ||
          data.deletion_kind !== expectedDeletionKind ||
          !Number.isSafeInteger(sequence) || sequence <= 0 ||
          typeof data.idempotent !== 'boolean') {
        SyncLogger.OpDelete.error(`Erro ao excluir com tombstone em ${collection}:`, error);
        return false;
      }
      SyncLogger.OpDelete.info(`Item excluido/aposentado com tombstone em ${collection}, ID: ${operation.itemId}`);
      return true;
    }

    SyncLogger.OpDelete.warn(
      `Delete remoto bloqueado em ${collection}: feed de tombstones nao habilitado.`
    );
    return false;
  } catch (error) {
    reportUnrecoveredSyncFailure(error, 'push_delete');
    SyncLogger.OpDelete.error(`Erro ao processar operação de exclusão para ${collection}:`, error);
    return false;
  }
};

/**
 * Obtém a configuração de sincronização para uma tabela específica
 * 
 * @param tableName Nome da tabela
 * @returns Configuração da tabela ou undefined se não existir
 */
const getTableSyncConfig = (tableName: string): TableSyncConfig | undefined => {
  // Procura a tabela nas configurações constantes
  for (const tableKey in APP_CONSTANTS.DATABASE.TABLES) {
    // Garante que a propriedade existe no objeto (corrige erro de lint)
    if (Object.prototype.hasOwnProperty.call(APP_CONSTANTS.DATABASE.TABLES, tableKey)) {
      const table = APP_CONSTANTS.DATABASE.TABLES[tableKey as keyof typeof APP_CONSTANTS.DATABASE.TABLES];

      if (table.NAME === tableName && table.SYNC_CONFIG) {
        return table.SYNC_CONFIG;
      }
    }
  }

  return undefined;
};

/**
 * Gera mensagem adequada com base no status da sincronização
 */
const getMessage = (
  status: 'success' | 'partial' | 'error',
  pushStats: { processedCount: number; successCount: number; errorCount: number; deferredCount?: number; mediaAckDeferred?: number }
): string => {
  const waiting = Number(pushStats.deferredCount ?? 0) + Number(pushStats.mediaAckDeferred ?? 0);
  const waitingMessage = waiting > 0 ? ` ${waiting} pendência(s) aguardando envio ou confirmação.` : '';
  switch (status) {
    case 'success':
      if (waiting > 0) return `${pushStats.successCount} alterações enviadas.${waitingMessage}`;
      return `Sincronização concluída com sucesso. ${pushStats.successCount} alterações enviadas.`;
    case 'partial':
      return `Sincronização parcial. ${pushStats.successCount} alterações enviadas, ${pushStats.errorCount} erros.${waitingMessage}`;
    case 'error':
      return 'Falha na sincronização. Verifique a conexão e tente novamente.';
    default:
      return 'Sincronização concluída.';
  }
};

async function logPendings() {
  const { operations: ops, images: imgs, deletions: dels } =
    await loadLocalPendingSnapshot({ rawOperations: true });
  SyncLogger.Main.info(`>>> Estado das pendências → ops: ${ops.length}, imgs: ${imgs.length}, dels: ${dels.length}`);
}

const refreshSubscriptionAfterPull = async (tracePrefix: string) => {
  try {
    const profile = globalState.user.profile.get();
    const empresaId = profile?.empresa_id || globalState.company.data.get()?.id;

    if (!empresaId) {
      SyncLogger.Main.info(`${tracePrefix} Sem empresa_id para atualizar assinatura após sincronização`);
      return;
    }

    SyncLogger.Main.info(`${tracePrefix} Atualizando assinatura após pull para empresa ${empresaId}`);
    const subscriptionStatus = await checkSubscriptionStatus(empresaId);
    userActions.setSubscriptionStatus(subscriptionStatus);
  } catch (error) {
    SyncLogger.Main.warn(`${tracePrefix} Falha ao atualizar assinatura após pull:`, error);
  }
};

/**
 * Executa o processo completo de sincronização (push e pull)
 * 
 * @param options Opções de sincronização
 * @returns Promise que resolve para um objeto com status e detalhes da operação
 */
const syncAllInternal = async (options: SyncOptions = {}): Promise<SyncStatus> => {
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    SyncLogger.Main.warn('[RECOVERY] Sincronizacao completa bloqueada pela quarentena');
    return recoveryDiagnosticSyncStatus();
  }

  const diagnosticIdentity = { companyId: globalState.user.profile.get()?.empresa_id ?? '', userId: globalState.user.user.get()?.id ?? '' };
  const startTime = Date.now();
  const traceId = getActiveSyncTraceId() ?? uuidv4();
  const tracePrefix = `[trace:${traceId}]`;
  SyncLogger.Main.info(`${tracePrefix} Iniciando processo completo de sincronização`);
  addSentryBreadcrumb({
    category: 'sync',
    message: 'syncAll:start',
    level: 'info',
    data: {
      traceId,
      skipPush: Boolean(options.skipPush),
      skipPull: Boolean(options.skipPull),
    },
  });

  if (globalState.sync.isRunning.get()) {
    SyncLogger.Main.info(`${tracePrefix} SyncAll solicitado enquanto outra sincronização está em andamento. Ignorando solicitação duplicada.`);
    return {
      success: false,
      message: 'Sincronização em andamento',
      timestamp: new Date(),
      details: { reason: 'busy', traceId }
    };
  }

  // Verificar pré-requisitos
  const prerequisites = checkSyncPrerequisites();
  if (!prerequisites.allMet) {
    const errorMessage = 'Não é possível iniciar sincronização: pré-requisitos não atendidos';
    reportUnrecoveredSyncFailure({ code: prerequisites.networkConnected ? 'prerequisites_not_met' : 'offline', message: errorMessage, details: JSON.stringify(prerequisites) }, 'prerequisites', prerequisites.networkConnected);
    SyncLogger.Main.warn(`${tracePrefix} ${errorMessage}`);
    return {
      success: false,
      message: errorMessage,
      timestamp: new Date(),
      details: { ...prerequisites, traceId }
    };
  }

  await refreshStructuralSyncFeatureDecisions();

  if (isRecoveryRuntimeBlocked()) {
    SyncLogger.Main.warn('[RECOVERY] Sincronizacao completa bloqueada pelo gate runtime');
    return recoveryGateSyncStatus();
  }

  let shouldFinalizeRunning = false;

  try {
    // Incrementar contagem de tentativas
    syncActions.incrementAttempts();
    const currentAttempts = globalState.sync.attempts.get();
    SyncLogger.Main.info(`${tracePrefix} Tentativa de sincronização #${currentAttempts}`);

    // Atualizar estado global
    syncActions.setSyncRunning(true);
    shouldFinalizeRunning = true;
    syncActions.setSyncStatus('idle');
    syncActions.updateProgress('prerequisites', 100, 0); // Pré-requisitos verificados (100%)

    const {
      operations: initialPendingOps,
      images: initialPendingImages,
      deletions: initialPendingDeletions,
    } = await loadLocalPendingSnapshot();
    const pendingSessionSnapshot = activePendingSyncExecution?.snapshot;
    if (!pendingSessionSnapshot) throw new PendingSyncSessionChangedError();
    await assertPendingSyncSessionCurrent(pendingSessionSnapshot);

    await prepareChecklistCancellations(pendingSessionSnapshot.empresaId);

    const progressTotals: Record<SyncProgressContext, number> = {
      images: initialPendingImages.length,
      deletions: initialPendingDeletions.length,
      operations: initialPendingOps.length,
    };

    const progressState: Record<SyncProgressContext, { processed: number; success: number; errors: number }> = {
      images: { processed: 0, success: 0, errors: 0 },
      deletions: { processed: 0, success: 0, errors: 0 },
      operations: { processed: 0, success: 0, errors: 0 },
    };

    const totalPendencias = progressTotals.images + progressTotals.deletions + progressTotals.operations;
    SyncLogger.Main.info(`${tracePrefix} Pendências detectadas → imagens=${progressTotals.images}, deleções=${progressTotals.deletions}, operações=${progressTotals.operations}, total=${totalPendencias}`);

    const computeContribution = (processed: number, total: number): number => {
      if (total === 0) {
        return 10;
      }
      return Math.min(10, Math.round((processed / total) * 10));
    };

    const publishPendingSnapshot = () => {
      syncActions.setPendingSnapshot({
        totals: {
          images: progressTotals.images,
          deletions: progressTotals.deletions,
          operations: progressTotals.operations,
          total: totalPendencias,
        },
        processed: {
          images: { ...progressState.images },
          deletions: { ...progressState.deletions },
          operations: { ...progressState.operations },
        },
      });
    };

    const updatePushProgress = () => {
      const processedTotal =
        progressState.images.processed +
        progressState.deletions.processed +
        progressState.operations.processed;

      const stageProgress = totalPendencias === 0
        ? 100
        : Math.min(100, Math.round((processedTotal / totalPendencias) * 100));

      const overallPush =
        computeContribution(progressState.images.processed, progressTotals.images) +
        computeContribution(progressState.deletions.processed, progressTotals.deletions) +
        computeContribution(progressState.operations.processed, progressTotals.operations);

      const cappedOverall = Math.min(30, overallPush);
      syncActions.updateProgress('push', stageProgress, cappedOverall);
      publishPendingSnapshot();
      SyncLogger.Progress.info(`${tracePrefix} Progresso PUSH → stage=${stageProgress}% | overall=${cappedOverall} (img ${progressState.images.processed}/${progressTotals.images}, del ${progressState.deletions.processed}/${progressTotals.deletions}, ops ${progressState.operations.processed}/${progressTotals.operations})`);
    };

    const handleProgressUpdate = (payload: SyncProgressPayload) => {
      const { context, processed, success, errors, total } = payload;
      progressState[context].processed = processed;
      progressState[context].success = success;
      progressState[context].errors = errors;
      progressTotals[context] = total;
      updatePushProgress();
    };

    const handleProcessProgress = (progress: PendingProcessProgress) => {
      handleProgressUpdate({
        context: progress.context as SyncProgressContext,
        processed: progress.processed,
        success: progress.success,
        errors: progress.errors,
        total: progress.total,
      });
    };

    updatePushProgress();

    const requiresTombstonePreflight =
      hasPendingMediaRequiringChecklistTombstone(initialPendingImages, initialPendingDeletions) ||
      (!options.skipPush && hasPendingOperationsRequiringChecklistTombstone(initialPendingOps));

    let tombstoneFeatureBlockedPush = false;
    if (requiresTombstonePreflight) {
      const { companyId, userId } = requireTombstonePreflightScope();
      SyncLogger.Main.info(`${tracePrefix} TOMBSTONE_PREFLIGHT iniciado antes de mídia, deleções e push`);
      const tombstonePreflight = await pullCanaryTombstones({
        companyId,
        userId,
        logInfo: (message, ...args) => SyncLogger.Main.info(`${tracePrefix} ${message}`, ...args),
        mode: 'coordinated-write',
      });
      tombstoneFeatureBlockedPush = !tombstonePreflight.enabled;
      SyncLogger.Main.info(`${tracePrefix} TOMBSTONE_PREFLIGHT concluído`);
    }

    const {
      images: currentPendingImages,
      deletions: currentPendingDeletions,
    } = await loadLocalPendingMedia();
    const cancellationRows = await prepareChecklistCancellations(pendingSessionSnapshot.empresaId);
    const cancelledIds = new Set(cancellationRows.map(row => row.item_id));
    const preflightImageIds = new Set(currentPendingImages.filter((item: any) => !cancelledIds.has(item.item_id)).map((item: any) => String(item.id)));
    const preflightDeletionIds = new Set(currentPendingDeletions.filter((item: any) => !cancelledIds.has(item.itemId ?? item.item_id)).map((item: any) => String(item.id)));
    let mediaTombstoneBarriers: Map<string, Set<string>> | undefined;
    if (
      !tombstoneFeatureBlockedPush &&
      hasPendingMediaRequiringChecklistTombstone(currentPendingImages, currentPendingDeletions)
    ) {
      const { companyId, userId } = requireTombstonePreflightScope();
      SyncLogger.Main.info(`${tracePrefix} TOMBSTONE_PREFLIGHT revalidando imediatamente antes de mídia/deleções`);
      const mediaTombstonePreflight = await pullCanaryTombstones({
        companyId,
        userId,
        logInfo: (message, ...args) => SyncLogger.Main.info(`${tracePrefix} ${message}`, ...args),
        mode: 'coordinated-write',
      });
      tombstoneFeatureBlockedPush = !mediaTombstonePreflight.enabled;
      if (!tombstoneFeatureBlockedPush) {
        mediaTombstoneBarriers = mediaTombstonePreflight.barriersByTable;
      }
      SyncLogger.Main.info(`${tracePrefix} TOMBSTONE_PREFLIGHT concluído antes de mídia/deleções`);
    }

    let imagesProcessResult = {
      total: progressTotals.images,
      processed: 0,
      success: 0,
      errors: 0,
    };

    let deletionsProcessResult: PendingProcessResult = {
      total: progressTotals.deletions,
      processed: 0,
      success: 0,
      errors: 0,
    };

    // Processar imagens pendentes antes da sincronização principal
    try {
      if (tombstoneFeatureBlockedPush) {
        SyncLogger.Main.warn(
          `${tracePrefix} Mídia, deleções e push protegidos foram adiados; o pull continuará com a feature de tombstones desligada`
        );
      } else if (!options.skipPendingMedia) {
        //SyncLogger.Main.info(`${tracePrefix} >>> Mostrando logpendings antes de processPendingImages`);
        await logPendings();
        SyncLogger.Main.info(`${tracePrefix} Processando imagens pendentes antes da sincronização principal`);
        await debugPause('SyncService antes de processPendingImages');
        imagesProcessResult = await processPendingImages({
          onProgress: handleProcessProgress,
          allowedPendingIds: preflightImageIds,
          tombstoneBarriers: mediaTombstoneBarriers,
          sessionSnapshot: pendingSessionSnapshot,
        });
        SyncLogger.Main.info(`${tracePrefix} Processamento de imagens pendentes concluído (sucesso=${imagesProcessResult.success}, erros=${imagesProcessResult.errors})`);
        SyncLogger.Main.info(`${tracePrefix} >>> Mostrando logpendings após processPendingImages`);
        await logPendings();
        const userId = globalState.user.user.get()?.id;
        if (userId) {
          await getUserProfile(userId);
        }

        //SyncLogger.Main.info(`${tracePrefix} >>> Mostrando logpendings antes de processPendingDeletions`);
        await logPendings();
        SyncLogger.Main.info(`${tracePrefix} Processando deleções pendentes antes da sincronização principal`);
        await debugPause('SyncService antes de processPendingDeletions');
        try {
        deletionsProcessResult = await processPendingDeletions({
          onProgress: handleProcessProgress,
          allowedPendingIds: preflightDeletionIds,
          tombstoneBarriers: mediaTombstoneBarriers,
          sessionSnapshot: pendingSessionSnapshot,
        });
        } catch (cleanupError) {
          deletionsProcessResult.errors = Math.max(deletionsProcessResult.errors, 1);
          progressState.deletions.errors = Math.max(progressState.deletions.errors, 1);
          SyncLogger.Error.warn(`${tracePrefix} Limpeza adiada; envio lógico preservado`, cleanupError);
        }
        try {
          const retention = await reconcileChecklistMediaRetention(pendingSessionSnapshot);
          if (retention.held.length) SyncLogger.Error.warn('Intenções de mídia preservadas para revisão:', retention.held);
        }
        catch (error) { SyncLogger.Error.warn(`${tracePrefix} Retenção não confirmada; evidência local preservada.`, error); }
        SyncLogger.Main.info(`${tracePrefix} Processamento de deleções pendentes concluído (sucesso=${deletionsProcessResult.success}, erros=${deletionsProcessResult.errors})`);
        SyncLogger.Main.info(`${tracePrefix} >>> Mostrando logpendings após processPendingDeletions`);
        await logPendings();
      } else {
        SyncLogger.Main.info(`${tracePrefix} Mídia e deleções aguardam o ciclo normal após o pull de preparação`);
      }
    } catch (imageError) {
      const pendingMediaErrorMessage = `${tracePrefix} Erro ao processar imagens/deleções pendentes`;
      SyncLogger.Error.warn(`${pendingMediaErrorMessage}:`, imageError);
      captureSentryExceptionWithScope(imageError, {
        level: 'warning',
        context: getSyncSentryBaseContext({
          flow: 'sync',
          stage: 'pending_media_preprocess',
          traceId,
          loggerMessage: pendingMediaErrorMessage,
        }),
        tags: {
          flow: 'sync',
          stage: 'pending_media',
          severity: 'recoverable',
        },
        fingerprint: ['sync', 'pending_media_preprocess_exception'],
        dedupeKey: 'sync:pending_media_preprocess_exception',
        dedupeWindowMs: 30000,
      });
      imagesProcessResult.errors = Math.max(imagesProcessResult.errors, 1);
      progressState.images.errors = Math.max(progressState.images.errors, 1);
    }

    // Resultados de cada etapa
    let pushResult: SyncStatus | null = null;
    let pullResult: SyncStatus | null = null;
    let overallSuccess = !tombstoneFeatureBlockedPush;
    const pendingMediaBlockedPush =
      imagesProcessResult.errors > 0 ||
      progressState.images.errors > 0;

    // Etapa 1: Push (se não for pulada)
    if (!options.skipPush) {
      if (tombstoneFeatureBlockedPush) {
        overallSuccess = false;
        pushResult = tombstoneFeatureOffPushStatus();
        SyncLogger.Main.warn(
          `${tracePrefix} PUSH protegido adiado; pendências preservadas e etapa PULL mantida`
        );
      } else if (pendingMediaBlockedPush) {
        overallSuccess = false;
        pushResult = {
          success: false,
          message: 'Push bloqueado por falha no processamento de mídia/deleções pendentes',
          timestamp: new Date(),
          details: {
            reason: 'pending_media_failed',
            imageErrors: imagesProcessResult.errors,
            deletionErrors: deletionsProcessResult.errors,
          },
        };
        SyncLogger.Main.warn(`${tracePrefix} PUSH bloqueado porque mídia/deleções pendentes tiveram erro`);
      } else {
        //SyncLogger.Main.info(`${tracePrefix} >>> Mostrando logpendings antes de pushLocalChanges`);
        await logPendings();
        SyncLogger.Main.info(`${tracePrefix} Iniciando etapa PUSH`);
        await debugPause('SyncService antes de pushLocalChanges');
        pushResult = await pushLocalChanges({
          ...options,
          skipPull: true,
          managedBySyncAll: true,
          progressReporter: handleProgressUpdate,
          tracePrefix,
        });
        SyncLogger.Main.info(`${tracePrefix} >>> Mostrando logpendings após pushLocalChanges`);
        await logPendings();
        SyncLogger.Main.info(`${tracePrefix} Resultado do PUSH: ${pushResult.success ? 'sucesso' : 'falha'}`);
      }

      // Falha parcial do push também impede declarar o ciclo saudável.
      if (!pushResult.success) {
        overallSuccess = false;
        SyncLogger.Main.warn(`${tracePrefix} PUSH terminou com falhas, definindo resultado geral como falha`);
      }
    } else {
      SyncLogger.Main.info(`${tracePrefix} Etapa PUSH pulada conforme opções`);
      // Atualizar progresso para refletir que pulamos o push
      syncActions.updateProgress('push', 100, 30);
    }

    SyncLogger.Main.info(
      `${tracePrefix} Estatísticas PUSH → imagens: ${progressState.images.success}/${progressTotals.images} (erros=${progressState.images.errors}), ` +
      `deleções: ${progressState.deletions.success}/${progressTotals.deletions} (erros=${progressState.deletions.errors}), ` +
      `operações: ${progressState.operations.success}/${progressTotals.operations} (erros=${progressState.operations.errors})`
    );

    // Etapa 2: Pull (se não for pulada)
    if (!options.skipPull) {
      //SyncLogger.Main.info(`${tracePrefix} >>> Mostrando logpendings antes de pullRemoteChanges`);
      await logPendings();
      SyncLogger.Main.info(`${tracePrefix} Iniciando etapa PULL com opções:`, options);
      await debugPause('SyncService antes de pullRemoteChanges');
      pullResult = await pullRemoteChanges({
        ...options,
        managedBySyncAll: true,
        tracePrefix,
      });
      SyncLogger.Main.info(`${tracePrefix} >>> Mostrando logpendings após pullRemoteChanges`);
      await logPendings();
      SyncLogger.Main.info(`${tracePrefix} Resultado do PULL: ${pullResult.success ? 'sucesso' : 'falha'}`);
      await debugPause('SyncService após pullRemoteChanges');
      if (pullResult.success) {
        await refreshSubscriptionAfterPull(tracePrefix);
      }

      // Se falhou completamente, marca o resultado geral como falha
      if (!pullResult.success) {
        overallSuccess = false;
        SyncLogger.Main.warn(`${tracePrefix} PULL falhou, definindo resultado geral como falha`);
      }
    } else {
      SyncLogger.Main.info(`${tracePrefix} Etapa PULL pulada conforme opções`);
      // Atualizar progresso para refletir que pulamos o pull
      syncActions.updateProgress('pull', 100, 90);
    }

    // Etapa 3: Limpeza e finalização
    syncActions.updateProgress('cleanup', 0, 90);

    const deferredCleanup = await databaseService.listItems<any>('deferred_media_cleanup', {
      user_id: pendingSessionSnapshot.userId, empresa_id: pendingSessionSnapshot.empresaId,
    });
    const cleanupDeferred = (deletionsProcessResult.deferred ?? 0) + deferredCleanup.length;
    const cleanupPending = cleanupDeferred > 0 || deletionsProcessResult.errors > 0;
    // Êxito lógico e limpeza física são resultados distintos; a intenção nunca recebe ACK fictício.
    // Retenção esperada não provoca backoff do envio lógico no scheduler.

    // Calcular o status de sucesso geral
    let finalStatus: 'success' | 'partial' | 'error' = 'error';
    const syncAllFailedMessage = `${tracePrefix} Sincronização falhou completamente`;

    if (overallSuccess) {
      finalStatus = 'success';
      SyncLogger.Main.info(`${tracePrefix} Sincronização concluída com sucesso`);
    } else if (
      (pushResult && pushResult.success) ||
      (pullResult && pullResult.success) ||
      (pushResult && pushResult.details && pushResult.details.successCount > 0)
    ) {
      finalStatus = 'partial';
      SyncLogger.Main.warn(`${tracePrefix} Sincronização concluída parcialmente`);
    } else {
      SyncLogger.Main.warn(syncAllFailedMessage);
    }

    // Atualizar estado global com o resultado final
    syncActions.setSyncStatus(finalStatus);

    if (pullResult?.success !== true) {
      SyncLogger.Main.info(
        `${tracePrefix} Timestamp de sincronização não atualizado: pull não concluído com sucesso`
      );
    }

    // Finalizar processo de limpeza
    syncActions.updateProgress('cleanup', 100, 100);

    // Calcular estatísticas
    const elapsedTime = Date.now() - startTime;
    const pushStats = pushResult?.details || {
      processedCount: 0,
      successCount: 0,
      errorCount: 0
    };

    const totalProcessed =
      progressState.images.processed +
      progressState.deletions.processed +
      progressState.operations.processed;
    const totalErrors =
      progressState.images.errors +
      progressState.deletions.errors +
      progressState.operations.errors +
      Number(pushResult?.details?.mediaAckErrors ?? 0);

    SyncLogger.Main.info(
      `${tracePrefix} Sincronização finalizada em ${elapsedTime}ms com status: ${finalStatus} (processados=${totalProcessed}/${totalPendencias}, erros=${totalErrors})`
    );
    addSentryBreadcrumb({
      category: 'sync',
      message: 'syncAll:end',
      level: finalStatus === 'error' ? 'error' : finalStatus === 'partial' ? 'warning' : 'info',
      data: {
        traceId,
        finalStatus,
        elapsedTime,
        totalProcessed,
        totalPendencias,
        totalErrors,
      },
    });

    if (finalStatus === 'error') {
      captureSentryMessageWithScope(syncAllFailedMessage, {
        level: 'error',
        context: getSyncSentryBaseContext({
          flow: 'sync',
          stage: 'sync_all',
          traceId,
          finalStatus,
          totalProcessed,
          totalPendencias,
          totalErrors,
          loggerMessage: syncAllFailedMessage,
        }),
        tags: {
          flow: 'sync',
          stage: 'sync_all',
          result: 'error',
        },
        fingerprint: ['sync', 'sync_all_error'],
        dedupeKey: 'sync:sync_all_error',
        dedupeWindowMs: 60000,
      });
    } else if (finalStatus === 'partial' && totalErrors > 0) {
      captureSentryMessageWithScope('Sincronização completa concluída parcialmente', {
        level: 'warning',
        context: getSyncSentryBaseContext({
          flow: 'sync',
          stage: 'sync_all',
          traceId,
          finalStatus,
          totalProcessed,
          totalPendencias,
          totalErrors,
        }),
        tags: {
          flow: 'sync',
          stage: 'sync_all',
          result: 'partial',
        },
        fingerprint: ['sync', 'sync_all_partial'],
        dedupeKey: 'sync:sync_all_partial',
        dedupeWindowMs: 60000,
      });
    }

    if (finalStatus === 'success') {
      syncActions.resetAttempts();
    }
    if (finalStatus === 'success' || finalStatus === 'partial') {
      supportDiagnosticSyncHooks.forEach(hook => hook(finalStatus));
    }
    if (finalStatus === 'success' || finalStatus === 'error' || totalErrors > 0 || pullResult?.success === false) {
      reportSyncDiagnosticResult({ ...diagnosticIdentity, traceId, startedAt: startTime,
        success: finalStatus === 'success', error: [pushResult?.message, pullResult?.message].filter(Boolean).join(' | ') });
    }
    clientInstallationSyncHooks.forEach(hook => hook(finalStatus));

    // Retornar resultado final
    return {
      success: finalStatus === 'success',
      message: cleanupPending && finalStatus === 'success'
        ? 'Dados sincronizados; limpeza de arquivos permanece adiada.' : getMessage(finalStatus, {
        processedCount: pushStats.processedCount || 0,
        successCount: pushStats.successCount || 0,
        errorCount: pushStats.errorCount || 0,
        deferredCount: pushStats.deferredCount || 0,
        mediaAckDeferred: pushStats.mediaAckDeferred || 0,
      }),
      timestamp: new Date(),
      details: {
        elapsedTime,
        pushResult: pushResult?.details,
        pullResult: pullResult?.details,
        finalStatus,
        traceId,
        pendingTotals: {
          images: progressTotals.images,
          deletions: progressTotals.deletions,
          operations: progressTotals.operations,
          total: totalPendencias,
        },
        pendingProcessed: {
          images: progressState.images,
          deletions: progressState.deletions,
          operations: progressState.operations,
        },
        imagesProcessResult,
        deletionsProcessResult,
        mediaCleanup: { deferred: cleanupDeferred, errors: deletionsProcessResult.errors, status: cleanupPending ? 'pending' : 'none' },
      }
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
    reportUnrecoveredSyncFailure(error, 'sync_all');
    const syncAllExceptionMessage = `${tracePrefix} Erro durante sincronização`;
    SyncLogger.Error.warn(`${syncAllExceptionMessage}:`, error);
    captureSentryExceptionWithScope(error, {
      context: getSyncSentryBaseContext({
        flow: 'sync',
        stage: 'sync_all',
        traceId,
        loggerMessage: syncAllExceptionMessage,
      }),
      tags: {
        flow: 'sync',
        stage: 'sync_all',
      },
      fingerprint: ['sync', 'sync_all_exception'],
      dedupeKey: 'sync:sync_all_exception',
      dedupeWindowMs: 30000,
    });

    // Atualizar estado global em caso de erro
    syncActions.setSyncError(errorMessage);
    syncActions.setSyncStatus('error');
    reportSyncDiagnosticResult({ ...diagnosticIdentity, traceId, startedAt: startTime, success: false, error: errorMessage });
    clientInstallationSyncHooks.forEach(hook => hook('error'));

    return {
      success: false,
      message: `Erro durante sincronização: ${errorMessage}`,
      timestamp: new Date(),
      details: { error, traceId }
    };
  } finally {
    if (shouldFinalizeRunning) {
      syncActions.setSyncRunning(false);
    }
  }
};

// Helpers de checklist (após helpers de CRUD, antes de getTableSyncConfig)

// 1. Atualização parcial do rascunho (corrigido para usar a tabela e colunas corretas, incluindo empresa_id e created_at)
interface ChecklistPartialUpdateProcessResult {
  success: boolean;
  pendingHandledByV2: boolean;
  pendingChanged?: boolean;
  deferred?: boolean;
  reason?: ChecklistPartialUpdateReason;
}

type ChecklistPartialUpdateReason = 'media_url_waiting' | 'request_superseded' | 'pending_changed' |
  'pending_snapshot_invalid' | 'media_identity_unverified' | 'media_reference_unverified' |
  'media_succession_unverified' | 'payload_invalid' | 'field_reference_unverified' |
  'rpc_request_failed' | 'rpc_outcome_invalid' | 'rpc_rejected' | 'conflict_unresolved' |
  'capability_unavailable' | 'historical_create_pending' | 'parent_retirement_waiting' | 'processing_failed';

const terminalEvidenceId = (
  prefix: 'accounted' | 'conflict',
  operationId: string,
  outcome: ChecklistValueV2Outcome
): string => `${prefix}:${operationId}:${outcome.fieldId}:${outcome.payloadSha512.slice(0, 24)}`;

type PendingTombstoneReconciliation = 'accounted' | 'changed';

const findExactLocalTombstoneForPendingOperation = async (
  operation: PendingOperation,
  companyId: string
): Promise<LocalTombstoneRow | null> => {
  const itemId = String(operation.itemId ?? '');
  if (!operation.collection || !itemId) return null;

  const tombstones = await databaseService.listItems<LocalTombstoneRow>('sync_tombstones', {
    empresa_id: companyId,
    entity_type: operation.collection,
    entity_id: itemId,
  });

  const exact = tombstones.filter(row => (
    row.empresa_id === companyId &&
    row.entity_type === operation.collection &&
    row.entity_id === itemId
  ));

  if (exact.length === 0) return null;
  return [...exact].sort((left, right) => (
    right.sequence - left.sequence || right.id.localeCompare(left.id)
  ))[0];
};

const confirmExactTombstoneAppliedToLocalTarget = async (
  tombstone: LocalTombstoneRow
): Promise<boolean> => {
  const target = await databaseService.readItem<Record<string, unknown>>(
    tombstone.entity_type,
    tombstone.entity_id
  );

  if (!target) return true;
  if (target.empresa_id !== tombstone.empresa_id) {
    throw new Error('Alvo local do tombstone pertence a outra empresa ou nao tem escopo valido.');
  }

  if (tombstone.deletion_kind === 'retire') {
    if (target.retired_at === tombstone.deleted_at) return true;
    return databaseService.updateItemIfUnchanged(
      tombstone.entity_type,
      tombstone.entity_id,
      { retired_at: tombstone.deleted_at },
      target,
      true
    );
  }

  return databaseService.deleteItemIfUnchanged(
    tombstone.entity_type,
    tombstone.entity_id,
    target,
    true
  );
};

const reconcilePendingOperationBlockedByTombstone = async (
  operation: PendingOperation,
  tombstoneBarriers: Map<string, Set<string>> | undefined,
  logPushInfo: (message: string, ...args: any[]) => void,
  logPushWarn: (message: string, ...args: any[]) => void
): Promise<PendingTombstoneReconciliation> => {
  const pendingSnapshot = buildPendingOperationSnapshot(operation);
  if (!pendingSnapshot || !await pendingSnapshotStillMatches(operation)) {
    logPushWarn(
      `Operação ${operation.id} mudou antes da classificação por tombstone; mantendo o payload mais novo`
    );
    return 'changed';
  }

  const { companyId, userId } = requireTombstonePreflightScope();
  const payload = serializePendingOperationData(operation.data);
  const payloadSha512 = sha512Hex(payload ?? '');
  const conflictId = `conflict:tombstone:${operation.id}:${payloadSha512.slice(0, 24)}`;
  const archiveId = `accounted:tombstone:${operation.id}:${payloadSha512.slice(0, 24)}`;
  const operationData = parsePullProtectionData(operation.data);
  const itemId = String(operation.itemId ?? '');
  const exactTombstone = await findExactLocalTombstoneForPendingOperation(operation, companyId);

  const existingConflict = await databaseService.readItem<any>('sync_conflicts', conflictId);
  if (existingConflict) {
    if (
      existingConflict.empresa_id !== companyId ||
      existingConflict.pending_operation_id !== operation.id ||
      existingConflict.reason !== 'tombstoned_entity' ||
      existingConflict.payload !== payload ||
      existingConflict.payload_sha512 !== payloadSha512
    ) {
      throw new Error('Conflito local de tombstone diverge da evidencia existente.');
    }
  } else {
    await databaseService.createItem('sync_conflicts', {
      id: conflictId,
      empresa_id: companyId,
      user_id: userId,
      pending_operation_id: operation.id,
      collection: operation.collection,
      item_id: itemId,
      field_id: operationData?.campo_id ?? operationData?.campoId ?? null,
      snapshot_field_id: operationData?.snapshot_field_id ?? operationData?.snapshotFieldId ?? null,
      reason: 'tombstoned_entity',
      payload,
      payload_sha512: payloadSha512,
      recovery_id: null,
      status: 'blocked',
      created_at: Date.now(),
      updated_at: Date.now(),
      resolved_at: null,
    }, true);
  }

  const existingArchive = await databaseService.readItem<any>(
    'accounted_pending_operations',
    archiveId
  );
  if (existingArchive) {
    if (
      existingArchive.empresa_id !== companyId ||
      existingArchive.operation !== operation.operation ||
      existingArchive.collection !== operation.collection ||
      existingArchive.item_id !== itemId ||
      existingArchive.accounting_state !== 'conflict_materialized' ||
      existingArchive.reason !== 'tombstoned_entity' ||
      existingArchive.data !== payload ||
      existingArchive.payload_sha512 !== payloadSha512
    ) {
      throw new Error('Arquivo terminal de tombstone diverge da evidencia existente.');
    }
  } else {
    await databaseService.createItem('accounted_pending_operations', {
      id: archiveId,
      empresa_id: companyId,
      user_id: userId,
      operation: operation.operation,
      collection: operation.collection,
      item_id: itemId,
      data: payload,
      original_timestamp: operation.timestamp,
      accounting_state: 'conflict_materialized',
      reason: 'tombstoned_entity',
      receipt_id: null,
      payload_sha512: payloadSha512,
      accounted_at: Date.now(),
      created_at: Date.now(),
      updated_at: Date.now(),
    }, true);
  }

  if (exactTombstone) {
    const localConverged = await confirmExactTombstoneAppliedToLocalTarget(exactTombstone);
    if (!localConverged) {
      logPushWarn(
        `Tombstone local ${exactTombstone.id} ainda nao convergiu por nova corrida; mantendo pendencia ${operation.id}`
      );
      return 'changed';
    }
  }

  const revalidatedDecision = await resolvePendingOperationTombstoneDecision(
    operation,
    tombstoneBarriers
  );
  if (revalidatedDecision !== 'blocked') {
    logPushWarn(
      `Barreira de tombstone mudou durante a classificação da operação ${operation.id}; mantendo pendência para nova avaliação`
    );
    return 'changed';
  }

  // A evidência pode ser criada antes de uma nova edição local; nesse caso,
  // ela fica preservada e somente o payload antigo sai da fila.
  const shouldRemovePending = await shouldRemovePendingOperationAfterSuccess(
    operation,
    logPushInfo,
    logPushWarn
  );
  if (!shouldRemovePending) return 'changed';

  if (!await databaseService.removePendingOperationIfUnchanged(
    operation.id,
    pendingSnapshotToExpectedRaw(pendingSnapshot),
    () => Boolean(activePendingSyncExecution && isPendingSyncSessionSnapshotStateCurrent(activePendingSyncExecution.snapshot))
  )) {
    throw new Error('Falha ao retirar operação tombstonada após classificação terminal.');
  }

  return 'accounted';
};

const ensureTerminalEvidence = async (input: {
  operation: PendingOperation;
  companyId: string;
  userId: string | null;
  outcome: ChecklistValueV2Outcome;
  mediaObservationValue?: ChecklistValueV2LocalRow;
  mediaAppliedPending?: Record<string, any>;
  mediaAppliedObservation?: string | null;
  mediaAppliedObservationBasis?: Record<string, unknown>;
}): Promise<void> => {
  const now = Date.now();
  const { operation, companyId, userId, outcome } = input;

  if (outcome.accountingState === 'conflict_materialized') {
    const conflictId = terminalEvidenceId('conflict', operation.id, outcome);
    const existingConflict = await databaseService.readItem<any>('sync_conflicts', conflictId);
    if (existingConflict) {
      if (
        existingConflict.empresa_id !== companyId ||
        existingConflict.pending_operation_id !== operation.id ||
        existingConflict.payload_sha512 !== outcome.payloadSha512
      ) {
        throw new Error('Conflito local idempotente diverge da evidencia existente.');
      }
    } else {
      await databaseService.createItem('sync_conflicts', {
        id: conflictId,
        empresa_id: companyId,
        user_id: userId,
        pending_operation_id: operation.id,
        collection: operation.collection,
        item_id: operation.itemId,
        field_id: outcome.payload.campo_id,
        snapshot_field_id: outcome.payload.snapshot_field_id,
        reason: outcome.reason ?? 'server_conflict',
        payload: JSON.stringify(outcome.payload),
        payload_sha512: outcome.payloadSha512,
        recovery_id: null,
        status: 'open',
        created_at: now,
        updated_at: now,
        resolved_at: null,
      }, true);
    }
  }

  const archiveId = terminalEvidenceId('accounted', operation.id, outcome);
  const existingArchive = await databaseService.readItem<any>(
    'accounted_pending_operations',
    archiveId
  );
  if (existingArchive) {
    if (
      existingArchive.empresa_id !== companyId ||
      existingArchive.accounting_state !== outcome.accountingState ||
      existingArchive.payload_sha512 !== outcome.payloadSha512
    ) {
      throw new Error('Arquivo local idempotente diverge da evidencia existente.');
    }
    return;
  }

  await databaseService.createItem('accounted_pending_operations', {
    id: archiveId,
    empresa_id: companyId,
    user_id: userId,
    operation: operation.operation,
    collection: operation.collection,
    item_id: operation.itemId,
    data: JSON.stringify({
      field_id: outcome.fieldId,
      payload: outcome.payload,
      idempotency_key: outcome.idempotencyKey,
      ...(input.mediaAppliedPending ? { media_applied_ack: { version: 1, pending: input.mediaAppliedPending, expected_observation: input.mediaAppliedObservation, observation_basis: input.mediaAppliedObservationBasis, operation: { id: operation.id, timestamp: operation.timestamp, data: operation.data } } } : {}),
      ...(input.mediaObservationValue ? { media_observation_ack: {
        version: 1, original_value: input.mediaObservationValue,
      } } : {}),
    }),
    original_timestamp: operation.timestamp,
    accounting_state: outcome.accountingState,
    reason: outcome.reason,
    receipt_id: null,
    payload_sha512: outcome.payloadSha512,
    accounted_at: now,
    created_at: now,
    updated_at: now,
  }, true);
};

const pendingSnapshotStillMatches = async (operation: PendingOperation): Promise<boolean> => {
  const current = await databaseService.readItem<any>('pending_operations', operation.id);
  const before = buildPendingOperationSnapshot(operation);
  const after = current ? buildPendingOperationSnapshot(current) : null;
  return Boolean(before && after && !didPendingOperationChange(before, after));
};

/** Prova somente a espera pelo pai; nunca libera DELETE remoto da resposta histórica. */
const isValueDeleteWaitingForParentRetirement = async (
  operation: PendingOperation,
  pendingOperations: PendingOperation[],
  session: PendingSyncSessionSnapshot
): Promise<boolean> => {
  const valuesTable = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME;
  const parentsTable = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME;
  if (operation.operation !== 'delete' || operation.collection !== valuesTable) return false;
  const payload = parsePendingOperationPayload(operation);
  if (!payload || payload.id !== operation.itemId || payload.empresa_id !== session.empresaId
    || operation.empresa_id !== session.empresaId || !operation.user_id
    || typeof payload.checklist_id !== 'string' || !payload.checklist_id.trim()) return false;

  const checklistId = payload.checklist_id;
  const parents = pendingOperations.filter(candidate => {
    if (candidate.operation !== 'delete' || candidate.collection !== parentsTable
      || candidate.itemId !== checklistId || candidate.empresa_id !== session.empresaId
      || candidate.user_id !== operation.user_id) return false;
    const parent = parsePendingOperationPayload(candidate);
    return parent?.id === checklistId && parent.empresa_id === session.empresaId
      && typeof parent.template_id === 'string' && Boolean(parent.template_id.trim());
  });
  if (parents.length !== 1) return false;
  const parent = parents[0];
  if (![operation, parent].every(candidate => {
    const snapshot = buildPendingOperationSnapshot(candidate);
    return snapshot && snapshot.timestamp !== null && Number.isFinite(snapshot.timestamp);
  })) return false;

  // Leituras com falha devem continuar sendo erro, não prova de ausência.
  if (await databaseService.readItem(valuesTable, operation.itemId)
    || await databaseService.readItem(parentsTable, checklistId)) return false;
  if (!await pendingSnapshotStillMatches(operation) || !await pendingSnapshotStillMatches(parent)) return false;
  await assertPendingSyncSessionCurrent(session);
  return true;
};

type ChecklistMediaPartialInspection = {
  deferred: boolean;
  deferredFieldIds: Set<string>;
  expectedRemoteUpdatedAtByField: Record<string, string | null>;
  createOnlyFieldIds: Set<string>;
  blockedReason?: ChecklistPartialUpdateReason;
};

const changedChecklistPendingReason = async (operation: PendingOperation): Promise<ChecklistPartialUpdateReason> => {
  const session = activePendingSyncExecution?.snapshot;
  if (!session) return 'pending_snapshot_invalid';
  await assertPendingSyncSessionCurrent(session);
  const current = await databaseService.readItem<any>('pending_operations', operation.id);
  const before = buildPendingOperationSnapshot(operation);
  const after = buildPendingOperationSnapshot(current);
  if (!before || !after || !after.id || before.timestamp === null || !Number.isFinite(before.timestamp) ||
      !Number.isFinite(after.timestamp) || after.timestamp === null || !after.user_id ||
      after.id !== before.id || after.operation !== before.operation || after.collection !== before.collection ||
      after.itemId !== before.itemId || after.user_id !== before.user_id || after.empresa_id !== before.empresa_id ||
      after.empresa_id !== session.empresaId || !didPendingOperationChange(before, after)) return 'pending_snapshot_invalid';
  let answers: Record<string, unknown>;
  try { answers = typeof current.data === 'string' ? JSON.parse(current.data) : current.data; }
  catch { return 'pending_snapshot_invalid'; }
  if (!answers || typeof answers !== 'object' || Array.isArray(answers) || !Object.keys(answers).length) return 'pending_snapshot_invalid';
  const rows = await databaseService.listItems<any>(operation.collection, { checklist_id: operation.itemId });
  await assertPendingSyncSessionCurrent(session);
  const latest = buildPendingOperationSnapshot(await databaseService.readItem<any>('pending_operations', operation.id));
  if (!latest || didPendingOperationChange(after, latest)) return 'pending_snapshot_invalid';
  const compatible = Object.entries(answers).every(([fieldId, answer]) => {
    const matches = rows.filter(row => row.empresa_id === session.empresaId && row.checklist_id === operation.itemId &&
      (row.campo_id === fieldId || row.snapshot_field_id === fieldId));
    return matches.length === 1 && checklistAnswerMatchesRow(answer, matches[0]);
  });
  return compatible ? 'request_superseded' : 'pending_snapshot_invalid';
};

const isLocalChecklistMediaReference = (value: unknown): value is string => typeof value === 'string' && (
  /^(file|content|blob|temp):/i.test(value) ||
  /^data:/i.test(value) ||
  /^(?:[\/\\]|[A-Za-z]:[\/\\]|\.\.?[\/\\])/.test(value) ||
  /^checklist-(photos|signatures|marcacoes|marcacao-bkg)\//i.test(value)
);

const NON_MEDIA_CHECKLIST_FIELD_TYPES = new Set([
  'text',
  'texto',
  'number',
  'mensagem pronta',
  'timestamp with time zone',
  'dropdown1',
  'dropdown2',
  'dropdown3',
  'cep1',
  'cep2',
  'cep3',
  'cep4',
  'cep5',
  'cep6',
]);

const isKnownNonMediaChecklistFieldType = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  return NON_MEDIA_CHECKLIST_FIELD_TYPES.has(normalized) || normalized.startsWith('dropdown');
};

type ChecklistCurrentFieldTypeReference = {
  id: string;
  empresa_id?: string;
  template_id?: string;
  retired_at?: string | null;
  tipo?: unknown;
};

const resolveChecklistPartialFieldType = (input: {
  fieldId: string;
  checklistId: string;
  checklist: any;
  currentFields: ReadonlyArray<ChecklistCurrentFieldTypeReference>;
  localValues: ReadonlyArray<ChecklistValueV2LocalRow & Record<string, any>>;
  snapshotFields: ReadonlyArray<ChecklistSnapshotFieldReference>;
}): unknown => {
  const companyId = String(input.checklist.empresa_id ?? '');
  const currentMatches = input.currentFields.filter(field =>
    String(field.id) === input.fieldId &&
    field.empresa_id === companyId &&
    field.template_id === input.checklist.template_id &&
    !field.retired_at
  );
  const directSnapshotMatches = input.snapshotFields.filter(field => field.id === input.fieldId);

  if (currentMatches.length > 0 || directSnapshotMatches.length > 0) {
    if (currentMatches.length + directSnapshotMatches.length !== 1) return null;
    if (currentMatches.length === 1) return currentMatches[0].tipo;

    const reference = directSnapshotMatches[0];
    const localMatches = input.localValues.filter(row =>
      row.empresa_id === companyId &&
      row.checklist_id === input.checklistId &&
      row.snapshot_field_id === reference.id &&
      (!reference.originalFieldId || row.campo_id === reference.originalFieldId)
    );
    return localMatches.length === 1 ? reference.tipo : null;
  }

  const originalSnapshotMatches = input.snapshotFields.filter(field =>
    field.originalFieldId === input.fieldId
  );
  if (originalSnapshotMatches.length !== 1) return null;

  const reference = originalSnapshotMatches[0];
  const localMatches = input.localValues.filter(row =>
    row.empresa_id === companyId &&
    row.checklist_id === input.checklistId &&
    row.snapshot_field_id === reference.id &&
    row.campo_id === input.fieldId
  );
  return localMatches.length === 1 ? reference.tipo : null;
};

const normalizeChecklistMediaOrigin = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const hasValidChecklistMediaOrigin = (value: unknown): boolean =>
  value === null || value === undefined || typeof value === 'string';

const inspectChecklistMediaPartial = async (input: {
  operation: PendingOperation;
  checklist: any;
  answers: Record<string, unknown>;
  localValues: ReadonlyArray<ChecklistValueV2LocalRow & Record<string, any>>;
  snapshotFields: ReadonlyArray<ChecklistSnapshotFieldReference>;
  currentFields: ReadonlyArray<ChecklistCurrentFieldTypeReference>;
  currentFieldIds: ReadonlySet<string>;
  mediaPendings?: ReadonlyArray<Record<string, any>>;
}): Promise<ChecklistMediaPartialInspection> => {
  const session = activePendingSyncExecution?.snapshot;
  if (!session) throw new PendingSyncSessionChangedError();
  const companyId = String(input.checklist.empresa_id ?? '');
  const mediaPendings = input.mediaPendings ?? await databaseService.listItems<any>('pending_images', {
    item_id: input.operation.itemId,
  });
  const expectedRemoteUpdatedAtByField: Record<string, string | null> = {};
  const deferredFieldIds = new Set<string>();
  const createOnlyFieldIds = new Set<string>();
  let blockedReason: ChecklistPartialUpdateReason | undefined;

  for (const [fieldId, answer] of Object.entries(input.answers)) {
    let answerValue = checklistAnswerValue(answer);
    const aliases = new Set([fieldId]);
    if (!input.currentFieldIds.has(fieldId)) {
      const snapshotById = input.snapshotFields.find(reference => reference.id === fieldId);
      if (snapshotById?.originalFieldId && input.localValues.some(row =>
        row.empresa_id === companyId && row.checklist_id === input.operation.itemId &&
        row.snapshot_field_id === snapshotById.id && row.campo_id === snapshotById.originalFieldId)) {
        aliases.add(snapshotById.originalFieldId);
      } else {
        const snapshotByOriginal = input.snapshotFields.filter(reference => reference.originalFieldId === fieldId);
        if (snapshotByOriginal.length === 1 && input.localValues.some(row =>
          row.empresa_id === companyId && row.checklist_id === input.operation.itemId &&
          row.snapshot_field_id === snapshotByOriginal[0].id && row.campo_id === fieldId)) {
          aliases.add(snapshotByOriginal[0].id);
        }
      }
    }

    if (isAnswerPatch(answer) && !Object.prototype.hasOwnProperty.call(answer, 'valor')) {
      const candidates = input.localValues.filter(row => row.empresa_id === companyId &&
        row.checklist_id === input.operation.itemId &&
        ((row.snapshot_field_id && aliases.has(row.snapshot_field_id)) || (row.campo_id && aliases.has(row.campo_id))));
      if (candidates.length !== 1) { deferredFieldIds.add(fieldId); blockedReason = 'media_identity_unverified'; continue; }
      answerValue = candidates[0].valor;
    }

    const linked = mediaPendings.filter(row => row.item_type === 'CHECKLIST' &&
      row.item_id === input.operation.itemId && aliases.has(String(row.campo_id ?? '')));
    if (linked.length === 0) {
      const fieldType = resolveChecklistPartialFieldType({
          fieldId,
          checklistId: input.operation.itemId,
          checklist: input.checklist,
          currentFields: input.currentFields,
          localValues: input.localValues,
          snapshotFields: input.snapshotFields,
        });
      const normalizedType = typeof fieldType === 'string'
        ? fieldType.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : '';
      const isMediaType = ['foto', 'photo', 'marcacao', 'mark', 'assinatura', 'signature'].includes(normalizedType);
      if ((isLocalChecklistMediaReference(answerValue) && !isKnownNonMediaChecklistFieldType(fieldType)) ||
        (isMediaType && typeof answerValue === 'string' && answerValue !== '' && !/^https?:\/\//i.test(answerValue) && !parsePartnerStorageReference(answerValue))) {
        deferredFieldIds.add(fieldId);
        blockedReason = 'media_reference_unverified';
      }
      continue;
    }

    // Um item só avança quando a única fila ligada à identidade foi preparada
    // pela mesma origem e o payload já contém exatamente sua URL pública.
    if (linked.length !== 1) { deferredFieldIds.add(fieldId); blockedReason = 'media_identity_unverified'; continue; }
    const pending = linked[0];
    const operationUserId = normalizeChecklistMediaOrigin(input.operation.user_id);
    const operationCompanyId = normalizeChecklistMediaOrigin(input.operation.empresa_id);
    const pendingUserId = normalizeChecklistMediaOrigin(pending.user_id);
    const pendingCompanyId = normalizeChecklistMediaOrigin(pending.empresa_id);
    const sameOrigin = hasValidChecklistMediaOrigin(input.operation.user_id) &&
      hasValidChecklistMediaOrigin(input.operation.empresa_id) &&
      hasValidChecklistMediaOrigin(pending.user_id) &&
      hasValidChecklistMediaOrigin(pending.empresa_id) &&
      operationUserId === pendingUserId &&
      operationCompanyId === pendingCompanyId &&
      session.empresaId === companyId &&
      (!operationCompanyId || operationCompanyId === companyId) &&
      (!pendingCompanyId || pendingCompanyId === companyId);
    if (!sameOrigin) { deferredFieldIds.add(fieldId); blockedReason = 'media_identity_unverified'; continue; }

    const expectedPublicUrl = pending.bucket && pending.path
      ? getSyncSupabaseClient().storage.from(pending.bucket).getPublicUrl(pending.path).data?.publicUrl
      : null;
    const prepared = typeof expectedPublicUrl === 'string' &&
      pending.prepared_public_url === expectedPublicUrl;
    if (!prepared || answerValue !== expectedPublicUrl) {
      deferredFieldIds.add(fieldId);
      const localMatches = input.localValues.filter(row => row.empresa_id === companyId && row.checklist_id === input.operation.itemId &&
        ((row.campo_id && aliases.has(row.campo_id)) || (row.snapshot_field_id && aliases.has(row.snapshot_field_id))));
      if (typeof expectedPublicUrl !== 'string' || !/^https?:\/\//i.test(expectedPublicUrl) ||
          (pending.prepared_public_url != null && pending.prepared_public_url !== expectedPublicUrl) ||
          !isLocalChecklistMediaReference(pending.local_uri) || localMatches.length !== 1 ||
          !checklistAnswerMatchesRow(answer, localMatches[0]) ||
          ![pending.local_uri, pending.prepared_public_url].includes(localMatches[0].valor)) blockedReason = 'media_identity_unverified';
      continue;
    }
    if (pending.baseline_captured === 1) {
      expectedRemoteUpdatedAtByField[fieldId] = pending.expected_remote_updated_at ?? null;
    } else {
      // Sem prova da versão anterior, nunca adotar a versão lida agora como
      // autorização para sobrescrever outra foto ou nota remota.
      createOnlyFieldIds.add(fieldId);
    }
  }

  return { deferred: deferredFieldIds.size > 0, deferredFieldIds, expectedRemoteUpdatedAtByField, createOnlyFieldIds, blockedReason };
};

/** Confirma a antecessora pelo pedido persistido; leitura remota isolada nunca renova o CAS. */
async function reconcileChecklistMediaSuccessions(snapshot: PendingSyncSessionSnapshot, checklistId: string): Promise<Set<string>> {
  const blocked = new Set<string>();
  const images = await databaseService.listItems<any>('pending_images', { item_id: checklistId });
  let archives = await databaseService.listItems<any>('accounted_pending_operations', { item_id: checklistId });
  for (const pending of images) {
    if (pending.user_id !== snapshot.userId || pending.empresa_id !== snapshot.empresaId) continue;
    const links = archives.map(archive => ({ archive, link: readChecklistMediaSuccession(archive, pending) })).filter(item => item.link);
    if (!links.length) continue;
    if (links.length !== 1) { blocked.add(pending.id); continue; }
    const { archive, link } = links[0];
    const value = await databaseService.readItem<any>('guincho_valores_checklist', link!.value.id);
    const parent = await databaseService.readItem<any>('guincho_checklists', checklistId);
    const tombstones = await databaseService.listItems<any>('sync_tombstones', { empresa_id: snapshot.empresaId });
    const operations = await databaseService.listItems<any>('pending_operations');
    const conflicts = await databaseService.listItems<any>('sync_conflicts', { empresa_id: snapshot.empresaId });
    const ids = [checklistId, pending.campo_id, value?.id, value?.campo_id, value?.snapshot_field_id, parent?.template_id].filter(Boolean);
    if (!value || value.empresa_id !== snapshot.empresaId || value.checklist_id !== checklistId ||
        (value.campo_id ?? null) !== (link!.value.campo_id ?? null) ||
        (value.snapshot_field_id ?? null) !== (link!.value.snapshot_field_id ?? null) ||
        (value.valor !== pending.local_uri && value.valor !== pending.prepared_public_url) ||
        !parent || parent.empresa_id !== snapshot.empresaId || parent.retired_at ||
        tombstones.some(row => ids.includes(row.entity_id)) || hasMediaCancellation(conflicts, pending) ||
        operations.some(row => row.operation === 'delete' && ids.includes(row.item_id))) {
      blocked.add(pending.id); continue;
    }
    let evidence = archives.find(row => confirmsChecklistMediaPredecessor(link!, row));
    const knownConflict = archives.some(row => row.accounting_state === 'conflict_materialized' && row.payload_sha512 === link!.payloadSha512);
    if (knownConflict) continue;
    if (!evidence) {
      blocked.add(pending.id);
      await assertPendingSyncSessionCurrent(snapshot);
      const current = await databaseService.readItem<any>('pending_images', pending.id);
      const currentValue = await databaseService.readItem<any>('guincho_valores_checklist', value.id);
      const currentParent = await databaseService.readItem<any>('guincho_checklists', checklistId);
      const currentArchive = await databaseService.readItem<any>('accounted_pending_operations', archive.id);
      const currentTombstones = await databaseService.listItems<any>('sync_tombstones', { empresa_id: snapshot.empresaId });
      const currentOperations = await databaseService.listItems<any>('pending_operations');
      const currentConflicts = await databaseService.listItems<any>('sync_conflicts', { empresa_id: snapshot.empresaId });
      if (!sameMediaRow(current, pending) || !sameMediaRow(currentValue, value) || !sameMediaRow(currentParent, parent) ||
          !sameMediaRow(currentArchive, archive) || currentTombstones.some(row => ids.includes(row.entity_id)) ||
          currentOperations.some(row => row.operation === 'delete' && ids.includes(row.item_id)) ||
          hasMediaCancellation(currentConflicts, pending)) continue;
      await assertPendingSyncSessionCurrent(snapshot);
      const client = createPendingSyncSupabaseClient(snapshot);
      const response = await client.rpc(link!.payload.expected_partner_revision !== undefined ? 'upsert_checklist_values_partner_v1' : link!.payload.observacao !== undefined ? 'upsert_checklist_values_v3' : 'upsert_checklist_values_v2', {
        p_checklist_id: checklistId, p_values: [link!.payload], p_client_build: getSyncClientBuild(),
      });
      await assertPendingSyncSessionCurrent(snapshot);
      const terminal = Array.isArray(response.data) && response.data.length === 1 ? response.data[0] : null;
      if (response.error || terminal?.input_index !== 0 || terminal.value_id !== link!.value.id ||
          !['applied', 'historical', 'conflict'].includes(terminal.result) ||
          terminal.result !== 'conflict' && terminal.result !== (link!.payload.snapshot_field_id ? 'historical' : 'applied')) continue;
      const outcome: ChecklistValueV2Outcome = { fieldId: link!.fieldId, valueId: link!.value.id,
        payload: link!.payload, payloadSha512: link!.payloadSha512, idempotencyKey: link!.payload.idempotency_key,
        accountingState: terminal.result === 'conflict' ? 'conflict_materialized' : terminal.result === 'historical' ? 'historical_materialized' : 'server_applied',
        reason: terminal.reason, terminal: true };
      await ensureTerminalEvidence({ operation: { ...link!.operation, operation: 'checklist_partial_update',
        collection: 'guincho_valores_checklist', itemId: checklistId } as PendingOperation,
        companyId: snapshot.empresaId, userId: snapshot.userId, outcome, mediaAppliedPending: link!.predecessor, mediaAppliedObservation: link!.expectedObservation, mediaAppliedObservationBasis: link!.observationBasis });
      archives = await databaseService.listItems<any>('accounted_pending_operations', { item_id: checklistId });
      if (terminal.result === 'conflict') { blocked.delete(pending.id); continue; }
      evidence = archives.find(row => confirmsChecklistMediaPredecessor(link!, row));
    }
    if (!evidence) { blocked.add(pending.id); continue; }
    const predecessorPrepared = checklistValuesV2PreparedStore.get(link!.operation.id, link!.fieldId);
    if (predecessorPrepared && predecessorPrepared.payload.idempotency_key === link!.payload.idempotency_key &&
        mediaPayloadHash(predecessorPrepared.payload) === link!.payloadSha512) {
      checklistValuesV2PreparedStore.delete(link!.operation.id, link!.fieldId);
    }
    const client = createPendingSyncSupabaseClient(snapshot);
    const response = await client.from('guincho_valores_checklist').select('id,empresa_id,checklist_id,campo_id,snapshot_field_id,valor,observacao,updated_at')
      .eq('id', value.id).eq('empresa_id', snapshot.empresaId).eq('checklist_id', checklistId).maybeSingle();
    await assertPendingSyncSessionCurrent(snapshot);
    const remote = response.data;
    if (response.error || !remote || remote.id !== value.id || remote.empresa_id !== snapshot.empresaId ||
        remote.checklist_id !== checklistId || typeof remote.updated_at !== 'string' || !Number.isFinite(Date.parse(remote.updated_at)) ||
        (remote.snapshot_field_id ?? null) !== (link!.payload.snapshot_field_id ?? value.snapshot_field_id ?? null) ||
        (remote.campo_id ?? null) !== (link!.payload.campo_id ?? null)) { blocked.add(pending.id); continue; }
    blocked.delete(pending.id);
    // Outra imagem continua no resolvedor R4. A mesma URL só avança com a prova exata acima.
    if (remote.valor !== link!.payload.valor) continue;
    if ((remote.observacao ?? null) !== predecessorObservation(link!) && hasPendingMediaObservation(operations, value, pending)) continue;
    const successorPrepared = operations.filter(row => row.item_id === checklistId && row.operation === 'checklist_partial_update')
      .flatMap(operation => {
        let answers: Record<string, unknown>;
        try { answers = JSON.parse(operation.data); } catch { return []; }
        return Object.keys(answers).map(id => checklistValuesV2PreparedStore.get(operation.id, id));
      }).find(row => row?.payload.valor === pending.prepared_public_url);
    // A resposta desconhecida de B conserva seu lock; somente conflito terminal autoriza outra tentativa.
    if (successorPrepared && !archives.some(row => row.payload_sha512 === mediaPayloadHash(successorPrepared.payload) &&
        row.accounting_state === 'conflict_materialized')) continue;
    await databaseService.advanceChecklistMediaObservationBaseline({ pending, value,
      previousRemoteValue: remote.valor, previousRemoteUpdatedAt: link!.payload.expected_updated_at,
      remoteUpdatedAt: remote.updated_at, succession: { archive, evidence },
      canAcknowledge: () => isPendingSyncSessionSnapshotStateCurrent(snapshot),
    });
  }
  return blocked;
}

const processChecklistPartialUpdateV2 = async (
  operation: PendingOperation,
  checklist: any
): Promise<ChecklistPartialUpdateProcessResult> => {
  const sessionSnapshot = activePendingSyncExecution?.snapshot;
  if (!sessionSnapshot) throw new PendingSyncSessionChangedError();
  await assertPendingSyncSessionCurrent(sessionSnapshot);
  const answers = typeof operation.data === 'string'
    ? JSON.parse(operation.data)
    : operation.data;
  if (!answers || typeof answers !== 'object' || Array.isArray(answers)) {
    return { success: false, pendingHandledByV2: true, reason: 'payload_invalid' };
  }

  const companyId = String(checklist.empresa_id ?? '');
  const clientBuild = getSyncClientBuild();
  let currentFields = await databaseService.listItems<any>(
    APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
    { template_id: checklist.template_id }
  );
  const snapshots = await databaseService.listItems<any>(
    APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOTS.NAME,
    { checklist_id: operation.itemId }
  );
  await retryAcknowledgedChecklistMediaObservations(sessionSnapshot, operation.itemId);
  const blockedSuccessors = await reconcileChecklistMediaSuccessions(sessionSnapshot, operation.itemId);
  let localValues = await databaseService.listItems<any>(
    operation.collection,
    { checklist_id: operation.itemId }
  );
  const remoteResponse = await getSyncSupabaseClient()
    .from(operation.collection)
    .select('*')
    .eq('empresa_id', companyId)
    .eq('checklist_id', operation.itemId);
  if (remoteResponse.error) throw remoteResponse.error;
  if (!Array.isArray(remoteResponse.data)) throw new Error('Consulta de respostas remotas não confirmou uma lista válida.');

  const companySnapshots = snapshots.filter(snapshot => snapshot.empresa_id === companyId);
  const snapshotFieldsNested: any[][] = [];
  for (const snapshot of companySnapshots) {
    snapshotFieldsNested.push(await databaseService.listItems<any>(
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME,
      { snapshot_id: snapshot.id }
    ));
  }
  let snapshotFields: ChecklistSnapshotFieldReference[] = snapshotFieldsNested
    .flat()
    .filter(field => field.empresa_id === companyId)
    .map(field => ({
      id: String(field.id),
      originalFieldId: field.original_campo_id ? String(field.original_campo_id) : null,
      tipo: field.tipo,
    }));

  const remoteValues = (remoteResponse.data ?? []) as ChecklistValueV2LocalRow[];
  const localReferenceIds = new Set(snapshotFields.map(field => field.id));
  const missingReferenceIds = [...new Set(remoteValues
    .map(row => row.snapshot_field_id)
    .filter((id): id is string => Boolean(id) && !localReferenceIds.has(id!)))];
  if (missingReferenceIds.length > 0) {
    // Consulta delimitada antes do payload: não depende do cursor incremental nem altera o SQLite.
    const parents = await getSyncSupabaseClient()
      .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOTS.NAME)
      .select('id,empresa_id,checklist_id')
      .eq('empresa_id', companyId)
      .eq('checklist_id', operation.itemId);
    if (parents.error) throw parents.error;
    const parentRows = (parents.data ?? []) as Array<{
      id: string;
      empresa_id: string;
      checklist_id: string;
    }>;
    const parentIds = new Set(parentRows
      .filter(parent => parent.empresa_id === companyId && parent.checklist_id === operation.itemId)
      .map(parent => parent.id));
    const references = await getSyncSupabaseClient()
      .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME)
      .select('id,empresa_id,snapshot_id,original_campo_id,tipo')
      .eq('empresa_id', companyId)
      .in('id', missingReferenceIds);
    if (references.error) throw references.error;
    const referenceRows = (references.data ?? []) as Array<{
      id: string;
      empresa_id: string;
      snapshot_id: string;
      original_campo_id: string | null;
      tipo?: unknown;
    }>;
    const verifiedReferences = referenceRows.filter(field =>
      field.empresa_id === companyId && parentIds.has(field.snapshot_id));
    if (missingReferenceIds.some(id => !verifiedReferences.some(field => field.id === id))) {
      return { success: false, pendingHandledByV2: true, reason: 'field_reference_unverified' };
    }
    const fields = await getSyncSupabaseClient()
      .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME)
      .select('id,empresa_id,template_id,retired_at,tipo')
      .eq('empresa_id', companyId)
      .eq('template_id', checklist.template_id)
      .in('id', Object.keys(answers));
    if (fields.error) throw fields.error;
    const fieldRows = (fields.data ?? []) as Array<{
      id: string;
      empresa_id: string;
      template_id: string;
      retired_at: string | null;
      tipo?: unknown;
    }>;
    currentFields = fieldRows.filter(field =>
      field.empresa_id === companyId && field.template_id === checklist.template_id);
    snapshotFields = [...snapshotFields, ...verifiedReferences.map(field => ({
      id: String(field.id),
      originalFieldId: field.original_campo_id ? String(field.original_campo_id) : null,
      tipo: field.tipo,
    }))];
  }

  const currentFieldIds = new Set(
    currentFields
      .filter(field => field.empresa_id === companyId && !field.retired_at)
      .map(field => String(field.id))
  );
  const createOnlyFieldIds = /^archived-replay:[0-9a-f]{40}$/i.test(operation.id)
    ? new Set(Object.keys(answers))
    : new Set<string>();
  let legacyAuthorizedFieldIds = new Set<string>();

  // Um checklist sem snapshot pode transportar respostas físicas antigas
  // somente quando o backend liberar aquele campo para este pai/tenant.
  // O lookup local exige uma única linha física; a decisão de autorização e
  // o CAS final continuam no RPC, sem confiar em autor alegado pela fila.
  if (companySnapshots.length === 0 && Object.keys(answers).length > 0) {
    const snapshotFieldIds = new Set(snapshotFields.map(field => field.id));
    const snapshotOriginalIds = new Set(snapshotFields
      .map(field => field.originalFieldId)
      .filter((id): id is string => Boolean(id)));
    const localEvidence = localValues as Array<ChecklistValueV2LocalRow & {
      empresa_id?: string;
      checklist_id?: string;
      valor?: unknown;
    }>;
    const remoteEvidence = remoteValues as Array<ChecklistValueV2LocalRow & {
      empresa_id?: string;
      checklist_id?: string;
    }>;
    const eligibleLegacyFields = new Set<string>();

    for (const fieldId of Object.keys(answers)) {
      if (currentFieldIds.has(fieldId) || snapshotFieldIds.has(fieldId) || snapshotOriginalIds.has(fieldId)) continue;
      const localRows = localEvidence.filter(row => row.checklist_id === operation.itemId &&
        row.empresa_id === companyId && row.campo_id === fieldId && !row.snapshot_field_id);
      const remoteRows = remoteEvidence.filter(row => row.campo_id === fieldId);
      if (localRows.length !== 1 || remoteRows.length > 1 ||
          JSON.stringify(canonicalComparableValue(localRows[0]?.valor)) !==
            JSON.stringify(canonicalComparableValue(checklistAnswerValue(answers[fieldId]) ?? localRows[0]?.valor)) ||
          !checklistAnswerMatchesRow(answers[fieldId], localRows[0]) ||
          remoteRows.some(row => row.empresa_id !== companyId || row.checklist_id !== operation.itemId ||
            Boolean(row.snapshot_field_id))) continue;
      eligibleLegacyFields.add(fieldId);
    }

    if (eligibleLegacyFields.size > 0) {
      try {
        await assertPendingSyncSessionCurrent(sessionSnapshot);
        const { data, error } = await getSyncSupabaseClient().rpc('get_authorized_legacy_fields_v1', {
          p_checklist_id: operation.itemId,
          p_client_build: clientBuild,
        });
        await assertPendingSyncSessionCurrent(sessionSnapshot);
        if (!error && Array.isArray(data) && data.every(row =>
          row && typeof row === 'object' && !Array.isArray(row) && typeof row.campo_id === 'string'
        )) {
          const returnedIds = new Set<string>(data.map((row: { campo_id: string }) => row.campo_id));
          const tombstones = await databaseService.listItems<LocalTombstoneRow>(
            APP_CONSTANTS.DATABASE.TABLES.SYNC_TOMBSTONES.NAME,
            { empresa_id: companyId, entity_type: APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME }
          );
          const realDeletes = new Set(tombstones
            .filter(row => row.empresa_id === companyId &&
              row.entity_type === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME &&
              row.deletion_kind === 'delete')
            .map(row => row.entity_id));
          legacyAuthorizedFieldIds = new Set([...eligibleLegacyFields].filter(fieldId =>
            returnedIds.has(fieldId) && !realDeletes.has(fieldId)
          ));
        } else {
          SyncLogger.OpChecklist?.warn?.(
            `Referências físicas antigas não autorizadas pelo backend para ${operation.itemId}; mantendo somente esses campos pendentes`
          );
        }
      } catch (error) {
        if (error instanceof PendingSyncSessionChangedError) throw error;
        SyncLogger.OpChecklist?.warn?.(
          `Falha ao consultar autorização física antiga para ${operation.itemId}; mantendo somente esses campos pendentes`, error
        );
      }
    }
  }
  legacyAuthorizedFieldIds.forEach(fieldId => currentFieldIds.add(fieldId));

  let mediaSnapshotPendings = await databaseService.listItems<any>('pending_images', { item_id: operation.itemId });
  // Uma nota web pode criar outra PK enquanto a primeira imagem permanece offline.
  // Alinha somente esse caso antes do payload; o preflight, CAS e ACK seguem intactos.
  for (const pending of mediaSnapshotPendings) {
    const field = snapshotFieldsNested.flat().find(row => row.id === pending.campo_id);
    const snapshot = companySnapshots.find(row => row.id === field?.snapshot_id);
    if (!field || !snapshot || !Object.prototype.hasOwnProperty.call(answers, field.id) ||
        !isLocalChecklistMediaReference(pending.local_uri)) continue;
    const aliases = [field.id, field.original_campo_id];
    const matchingLocal = localValues.filter(row => aliases.includes(row.snapshot_field_id) || aliases.includes(row.campo_id));
    const matchingRemote = remoteValues.filter(row => aliases.includes(row.snapshot_field_id) || aliases.includes(row.campo_id));
    if (matchingLocal.length !== 1 || matchingRemote.length !== 1 || matchingLocal[0].id === matchingRemote[0].id) continue;
    const value = matchingLocal[0];
    const remote = matchingRemote[0];
    const publicUrl = pending.bucket && pending.path
      ? getSyncSupabaseClient().storage.from(pending.bucket).getPublicUrl(pending.path).data?.publicUrl : null;
    // Mídia ainda aguardando preparo ou com nota local conserva o adiamento
    // por campo existente; somente o recorte pronto pode exigir tracking de adoção.
    if (typeof publicUrl !== 'string' || pending.prepared_public_url !== publicUrl || value.valor !== publicUrl ||
        pending.baseline_captured !== 1 || pending.expected_remote_value !== null || pending.expected_remote_updated_at !== null ||
        remote.valor !== null || typeof remote.observacao !== 'string' || !remote.observacao ||
        (value.observacao ?? null) !== null || hasAnswerObservation(answers[field.id]) ||
        checklistAnswerValue(answers[field.id]) !== publicUrl) continue;
    const canCommit = () => isPendingSyncSessionSnapshotStateCurrent(sessionSnapshot) &&
      checklistValuesV2PreparedStore.canAdoptCanonicalMedia(operation.id, aliases, [value.id, remote.id]);
    if (!canCommit()) {
      await assertPendingSyncSessionCurrent(sessionSnapshot);
      return { success: false, pendingHandledByV2: true, reason: 'media_identity_unverified' };
    }
    if (!await pendingSnapshotStillMatches(operation)) continue;
    const rawOperation = await databaseService.readItem<any>('pending_operations', operation.id);
    const expected = buildPendingOperationSnapshot(operation);
    if (!rawOperation || !expected || !sameMediaRow(rawOperation, { id: operation.id, ...pendingSnapshotToExpectedRaw(expected) })) continue;
    await assertPendingSyncSessionCurrent(sessionSnapshot);
    const adopted = await databaseService.adoptChecklistMediaNoteCanonical({ operation: rawOperation, pending, value, remote,
      remoteValues, parent: checklist, snapshot, field, publicUrl, canCommit });
    if (!adopted) continue;
    await assertPendingSyncSessionCurrent(sessionSnapshot);
    localValues = await databaseService.listItems<any>(operation.collection, { checklist_id: operation.itemId });
    mediaSnapshotPendings = await databaseService.listItems<any>('pending_images', { item_id: operation.itemId });
    if (!await pendingSnapshotStillMatches(operation)) {
      return { success: false, pendingHandledByV2: true, pendingChanged: true,
        reason: await changedChecklistPendingReason(operation) };
    }
  }
  const mediaInspection = await inspectChecklistMediaPartial({
    operation,
    checklist,
    mediaPendings: mediaSnapshotPendings,
    answers: answers as Record<string, unknown>,
    localValues: localValues as Array<ChecklistValueV2LocalRow & Record<string, any>>,
    snapshotFields,
    currentFields: currentFields.filter(field =>
      field.empresa_id === companyId &&
      field.template_id === checklist.template_id &&
      !field.retired_at
    ),
    currentFieldIds,
  });
  for (const pending of mediaSnapshotPendings.filter(row => blockedSuccessors.has(row.id))) {
    for (const fieldId of Object.keys(answers)) {
      if (fieldId === pending.campo_id || localValues.some(row => row.campo_id === fieldId && row.snapshot_field_id === pending.campo_id)) {
        mediaInspection.deferredFieldIds.add(fieldId); mediaInspection.deferred = true;
        mediaInspection.blockedReason = 'media_succession_unverified';
      }
    }
  }
  mediaInspection.createOnlyFieldIds.forEach(fieldId => createOnlyFieldIds.add(fieldId));
  const eligibleAnswers = Object.fromEntries(Object.entries(answers as Record<string, unknown>)
    .filter(([fieldId]) => !mediaInspection.deferredFieldIds.has(fieldId)));
  if (Object.keys(eligibleAnswers).length === 0) {
    SyncLogger.OpChecklist?.info?.('Parcial de mídia adiada até a preparação da URL pública exata');
    return { success: false, pendingHandledByV2: true, deferred: !mediaInspection.blockedReason,
      reason: mediaInspection.blockedReason ?? 'media_url_waiting' };
  }

  const dispatchedAttempts = new Map<string, ReturnType<MMKVChecklistValuesV2PreparedStore['get']>>();
  const cancelledRequests = new Map<string, ChecklistPartialUpdateReason>();
  const service = new ChecklistValuesV2Service({
    async upsert(request) {
      await assertPendingSyncSessionCurrent(sessionSnapshot);
      const cancelRequest = (reason: ChecklistPartialUpdateReason): never => {
        request.values.forEach(payload => cancelledRequests.set(payload.idempotency_key, reason));
        throw new Error('media_request_superseded');
      };
      if (!await pendingSnapshotStillMatches(operation)) cancelRequest(await changedChecklistPendingReason(operation));
      for (const payload of request.values) {
        const linked = mediaSnapshotPendings.find(row => row.prepared_public_url === payload.valor &&
          (row.campo_id === payload.campo_id || row.campo_id === payload.snapshot_field_id));
        if (linked) {
          const current = await databaseService.readItem<any>('pending_images', linked.id);
          const currentValue = await databaseService.readItem<any>('guincho_valores_checklist', payload.id);
          if (!current || current.local_uri !== linked.local_uri || current.prepared_public_url !== payload.valor ||
              currentValue?.valor !== payload.valor) cancelRequest('media_identity_unverified');
        }
      }
      await assertPendingSyncSessionCurrent(sessionSnapshot);
      for (const payload of request.values) {
        const fieldId = Object.keys(eligibleAnswers).find(id => checklistValuesV2PreparedStore.get(operation.id, id)?.payload.idempotency_key === payload.idempotency_key);
        if (fieldId) checklistValuesV2PreparedStore.markDispatched(operation.id, fieldId, payload.idempotency_key, { id: operation.id, timestamp: operation.timestamp, data: operation.data },
          payload.observacao !== undefined ? payload.observacao : remoteValues.find(row => row.id === payload.id)?.observacao,
          remoteValues.find(row => row.id === payload.id) ? { ...remoteValues.find(row => row.id === payload.id)! } : (payload.expected_updated_at === null ? { id: payload.id,
            empresa_id: companyId, checklist_id: operation.itemId, campo_id: payload.campo_id, snapshot_field_id: payload.snapshot_field_id, absent: true } : undefined));
        if (fieldId) dispatchedAttempts.set(payload.idempotency_key, checklistValuesV2PreparedStore.get(operation.id, fieldId));
      }
      const { data, error } = await observeChecklistMediaReferenceSend({
        companyId, userId: sessionSnapshot.userId, checklistId: request.checklistId, operationId: operation.id,
        transport: 'rpc', values: request.values, pendings: mediaSnapshotPendings,
      }, () => getSyncSupabaseClient().rpc(request.values.some(row => row.expected_partner_revision !== undefined) ? 'upsert_checklist_values_partner_v1' : request.values.some(row => row.observacao !== undefined) ? 'upsert_checklist_values_v3' : 'upsert_checklist_values_v2', {
        p_checklist_id: request.checklistId,
        p_values: request.values,
        p_client_build: request.clientBuild,
      }));
      if (error) throw error;
      if (!Array.isArray(data)) throw new Error('Resposta invalida da RPC checklist values v2.');
      return data as ChecklistValueV2RpcResult[];
    },
  }, checklistValuesV2PreparedStore);
  const result = await service.process({
    operationId: operation.id,
    operationTimestamp: operation.timestamp,
    checklistId: operation.itemId,
    companyId,
    answers: eligibleAnswers,
    currentFieldIds,
    createOnlyFieldIds,
    snapshotFields,
    localValues,
    remoteValues,
    expectedRemoteUpdatedAtByField: mediaInspection.expectedRemoteUpdatedAtByField,
    clientBuild,
  });
  // Somente o preflight desta chave pode identificar um request que não foi enviado.
  result.outcomes.forEach(outcome => {
    const reason = cancelledRequests.get(outcome.idempotencyKey);
    if (reason && !outcome.terminal) outcome.reason = reason;
  });

  const resolvedMediaFields = new Set<string>();
  const classifiedResult = (changedReason?: ChecklistPartialUpdateReason): ChecklistPartialUpdateProcessResult => {
    const failed = result.outcomes.find(outcome => !outcome.terminal && outcome.reason !== 'request_superseded');
    let failureReason: ChecklistPartialUpdateReason | undefined;
    if (failed) {
      if (failed.reason === 'pending_snapshot_invalid' || failed.reason === 'media_identity_unverified' || failed.reason === 'rpc_request_failed') failureReason = failed.reason;
      else if (failed.reason === 'answer_patch_invalid') failureReason = 'payload_invalid';
      else if (failed.reason === 'note_remote_identity_not_verified') failureReason = 'media_identity_unverified';
      else if (['field_reference_not_resolved_locally', 'replay_remote_value_exists'].includes(failed.reason ?? '')) failureReason = 'field_reference_unverified';
      else failureReason = failed.reason?.startsWith('rpc_') && failed.reason !== 'rpc_rejected' ? 'rpc_outcome_invalid' : 'rpc_rejected';
    }
    failureReason ??= mediaInspection.blockedReason;
    if (changedReason && changedReason !== 'request_superseded') failureReason ??= changedReason;
    if (result.outcomes.some(outcome => outcome.accountingState === 'conflict_materialized' && !resolvedMediaFields.has(outcome.fieldId))) {
      failureReason ??= 'conflict_unresolved';
    }
    if (failureReason) return { success: false, pendingHandledByV2: true, reason: failureReason, pendingChanged: Boolean(changedReason) };
    if (changedReason || result.outcomes.some(outcome => outcome.reason === 'request_superseded')) {
      return { success: false, pendingHandledByV2: true, deferred: true, pendingChanged: true, reason: changedReason ?? 'request_superseded' };
    }
    if (mediaInspection.deferred) return { success: false, pendingHandledByV2: true, deferred: true, reason: 'media_url_waiting' };
    return { success: true, pendingHandledByV2: true };
  };

  await assertPendingSyncSessionCurrent(sessionSnapshot);
  const userId = sessionSnapshot.userId;
  const supersededPreparedOperations: PendingOperation[] = [];
  for (const outcome of result.outcomes.filter(item => item.terminal)) {
    const answer = eligibleAnswers[outcome.fieldId];
    const original = localValues.find(row => row.id === outcome.payload.id);
    const noteOnly = isAnswerPatch(answer) && hasAnswerObservation(answer) &&
      !Object.prototype.hasOwnProperty.call(answer, 'valor') && original &&
      original.empresa_id === companyId && original.checklist_id === operation.itemId &&
      (original.valor ?? null) === (outcome.payload.valor ?? null);
    const sent = dispatchedAttempts.get(outcome.payload.idempotency_key) ?? checklistValuesV2PreparedStore.get(operation.id, outcome.fieldId);
    const previousOperation = sent?.operation;
    const supersededPrepared = previousOperation && (previousOperation.timestamp !== operation.timestamp ||
      JSON.stringify(typeof previousOperation.data === 'string' ? JSON.parse(previousOperation.data) : previousOperation.data) !==
        JSON.stringify(typeof operation.data === 'string' ? JSON.parse(operation.data) : operation.data));
    if (supersededPrepared) supersededPreparedOperations.push({ ...operation, ...previousOperation });
    {
      const mediaAppliedPending = ['server_applied', 'historical_materialized'].includes(outcome.accountingState)
        ? mediaSnapshotPendings.find(row => row.prepared_public_url === outcome.payload.valor &&
          (row.campo_id === outcome.fieldId || row.campo_id === outcome.payload.snapshot_field_id)) : undefined;
      await ensureTerminalEvidence({ operation: supersededPrepared ? { ...operation, ...previousOperation } : operation, companyId, userId, outcome,
        ...(noteOnly && ['server_applied', 'historical_materialized'].includes(outcome.accountingState) ? { mediaObservationValue: original } : {}), mediaAppliedPending,
        mediaAppliedObservation: sent?.expectedObservation,
        mediaAppliedObservationBasis: sent?.observationBasis });
    }
  }
  service.clearPrepared(operation.id, result.terminalFieldIds, result.outcomes.filter(item => item.terminal).map(item => item.payload));
  if (supersededPreparedOperations.length) {
    // A fila atual pode anteceder este ciclo; compare-a com a tentativa antiga confirmada.
    for (const previous of supersededPreparedOperations) {
      const reason = await changedChecklistPendingReason(previous);
      if (reason !== 'request_superseded') return classifiedResult(reason);
    }
    return classifiedResult('request_superseded');
  }
  await acknowledgeChecklistMediaObservation({ checklistId: operation.itemId,
    answers: eligibleAnswers, localValues, outcomes: result.outcomes, snapshot: sessionSnapshot });
  await reconcileChecklistMediaSuccessions(sessionSnapshot, operation.itemId);

  if (!await pendingSnapshotStillMatches(operation)) {
    return classifiedResult(await changedChecklistPendingReason(operation));
  }

  for (const outcome of result.outcomes.filter(item => item.terminal)) {
    await ensureTerminalEvidence({ operation, companyId, userId, outcome });
  }
  await reconcileChecklistMediaSuccessions(sessionSnapshot, operation.itemId);
  service.clearPrepared(operation.id, result.terminalFieldIds, result.outcomes.filter(item => item.terminal).map(item => item.payload));

  if (!await pendingSnapshotStillMatches(operation)) {
    return classifiedResult(await changedChecklistPendingReason(operation));
  }

  const activeFields = new Set([...result.activeFieldIds, ...mediaInspection.deferredFieldIds]);
  const remainingAnswers = Object.fromEntries(
    Object.entries(answers as Record<string, unknown>)
      .filter(([fieldId]) => activeFields.has(fieldId))
  );
  const pendingSnapshot = buildPendingOperationSnapshot(operation);
  if (!pendingSnapshot) {
    return classifiedResult('pending_snapshot_invalid');
  }
  if (Object.keys(remainingAnswers).length > 0) {
    const updated = await databaseService.updateItemIfUnchanged(
      'pending_operations',
      operation.id,
      { data: JSON.stringify(remainingAnswers) },
      pendingSnapshotToExpectedRaw(pendingSnapshot) as Record<string, unknown>,
      true,
      () => isPendingSyncSessionSnapshotStateCurrent(sessionSnapshot)
    );
    if (!updated) {
      return classifiedResult(await changedChecklistPendingReason(operation));
    }
  } else if (!await databaseService.removePendingOperationIfUnchanged(
    operation.id,
    pendingSnapshotToExpectedRaw(pendingSnapshot),
    () => isPendingSyncSessionSnapshotStateCurrent(sessionSnapshot)
  )) {
    return classifiedResult(await changedChecklistPendingReason(operation));
  }

  for (const outcome of result.outcomes.filter(item => item.accountingState === 'conflict_materialized')) {
    const images = await databaseService.listItems<any>('pending_images', { item_id: operation.itemId });
    const linked = images.filter(row => row.prepared_public_url === outcome.payload.valor &&
      (row.campo_id === outcome.fieldId || row.campo_id === outcome.payload.snapshot_field_id));
    if (linked.length === 1 && await resolveTerminalChecklistMedia(linked[0], sessionSnapshot) === 'resolved') {
      resolvedMediaFields.add(outcome.fieldId);
    }
  }

  SyncLogger.OpChecklist?.info?.(
    `Sync v2 checklist concluido | terminal=${result.terminalFieldIds.length} | ativo=${result.activeFieldIds.length}`
  );
  return classifiedResult();
};

async function processChecklistPartialUpdate(
  operation: PendingOperation
): Promise<ChecklistPartialUpdateProcessResult> {
  try {
    const checklist = await databaseService.readItem<any>(
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
      operation.itemId
    );
    if (!checklist?.empresa_id) {
      throw new Error('Checklist ou empresa ausente para partial update.');
    }

    const answers = typeof operation.data === 'string' ? JSON.parse(operation.data) : operation.data;
    const pending = await databaseService.getPendingOperations();
    for (const create of pending) {
      if (create.operation !== 'create' || create.collection !== operation.collection) continue;
      let payload;
      try { payload = typeof create.data === 'string' ? JSON.parse(create.data) : create.data; }
      catch { continue; }
      if (payload?.checklist_id !== operation.itemId || !payload.campo_id
        || !Object.prototype.hasOwnProperty.call(answers ?? {}, payload.campo_id)) continue;
      if ((await resolveHistoricalCreatePayload(payload))?.historical) {
        // A parcial não pode avançar a data da resposta antes do readback e ACK do CREATE.
        SyncLogger.OpChecklist?.info?.('Parcial histórica adiada até confirmação do CREATE da mesma resposta');
        return { success: false, pendingHandledByV2: true, reason: 'historical_create_pending' };
      }
    }

    const v2Enabled = await syncCanaryFeatureService.isEnabledOrFalse(
      String(checklist.empresa_id),
      CHECKLIST_VALUES_V2_FEATURE,
      getSyncClientBuild()
    );
    if (!v2Enabled) {
      if (Object.values(answers ?? {}).some(answer => isAnswerPatch(answer) && answer.expected_partner_revision !== undefined)) {
        return { success: false, pendingHandledByV2: true, reason: 'capability_unavailable' };
      }
      const eligibleAnswers: Record<string, unknown> = {};
      const retainedNotes: Record<string, unknown> = {};
      for (const [fieldId, answer] of Object.entries(answers ?? {})) {
        if (hasAnswerObservation(answer)) retainedNotes[fieldId] = { observacao: answer.observacao };
        const value = checklistAnswerValue(answer);
        if (value !== undefined) eligibleAnswers[fieldId] = value;
      }
      if (Object.keys(eligibleAnswers).length === 0) return { success: false, pendingHandledByV2: true, reason: 'capability_unavailable' };
      const mediaPendings = await databaseService.listItems<any>('pending_images', {
        item_id: operation.itemId,
      });
      if (mediaPendings.length > 0 || Object.values(eligibleAnswers).some(isLocalChecklistMediaReference)) {
        const companyId = String(checklist.empresa_id);
        const localValues = await databaseService.listItems<any>(operation.collection, {
          checklist_id: operation.itemId,
        });
        const currentFields = checklist.template_id
          ? await databaseService.listItems<any>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME, {
              template_id: checklist.template_id,
            })
          : [];
        const currentFieldIds = new Set(currentFields
          .filter(field => field.empresa_id === companyId && !field.retired_at)
          .map(field => String(field.id)));
        const snapshots = await databaseService.listItems<any>(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOTS.NAME,
          { checklist_id: operation.itemId }
        );
        const localSnapshotFields: ChecklistSnapshotFieldReference[] = [];
        for (const snapshot of snapshots.filter(row => row.empresa_id === companyId)) {
          const fields = await databaseService.listItems<any>(
            APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME,
            { snapshot_id: snapshot.id }
          );
          localSnapshotFields.push(...fields.filter(field => field.empresa_id === companyId).map(field => ({
            id: String(field.id),
            originalFieldId: field.original_campo_id ? String(field.original_campo_id) : null,
            tipo: field.tipo,
          })));
        }
        const mediaSnapshotPendings = await databaseService.listItems<any>('pending_images', { item_id: operation.itemId });
  const mediaInspection = await inspectChecklistMediaPartial({
          operation,
          checklist,
          answers: eligibleAnswers,
          localValues: localValues as Array<ChecklistValueV2LocalRow & Record<string, any>>,
          snapshotFields: localSnapshotFields,
          currentFields: currentFields.filter(field =>
            field.empresa_id === companyId &&
            field.template_id === checklist.template_id &&
            !field.retired_at
          ),
          currentFieldIds,
          mediaPendings,
        });
        // O upsert legado não oferece CAS de criação nem de versão anterior.
        for (const fieldId of new Set([...mediaInspection.deferredFieldIds, ...mediaInspection.createOnlyFieldIds])) {
          retainedNotes[fieldId] = answers[fieldId];
          delete eligibleAnswers[fieldId];
        }
      }
      if (Object.keys(eligibleAnswers).length === 0) return { success: false, pendingHandledByV2: true, reason: 'capability_unavailable' };
      const { applied, deferredFieldIds, canonicalFieldIds } = await processChecklistPartialUpdateLegacy({
        ...operation, data: JSON.stringify(eligibleAnswers),
      }, mediaPendings);
      for (const fieldId of deferredFieldIds) retainedNotes[fieldId] = answers[fieldId];
      if (applied) {
        // A nota retoma pela identidade confirmada, sem perder patches de outro alias.
        for (const [fieldId, canonicalFieldId] of Object.entries(canonicalFieldIds)) {
          if (fieldId === canonicalFieldId || !Object.prototype.hasOwnProperty.call(retainedNotes, fieldId)) continue;
          if (Object.prototype.hasOwnProperty.call(retainedNotes, canonicalFieldId)) continue;
          retainedNotes[canonicalFieldId] = retainedNotes[fieldId];
          delete retainedNotes[fieldId];
        }
      }
      if (!applied || Object.keys(retainedNotes).length === 0) {
        return { success: applied, pendingHandledByV2: !applied, ...(!applied ? { reason: 'processing_failed' as const } : {}) };
      }
      // O ACK legado confirma apenas valores. A nota continua na mesma fila,
      // protegida pelo snapshot da operação original e pela sessão vigente.
      const session = activePendingSyncExecution?.snapshot;
      const snapshot = buildPendingOperationSnapshot(operation);
      if (!session || !snapshot) return { success: false, pendingHandledByV2: true, reason: 'pending_snapshot_invalid' };
      const updated = await databaseService.updateItemIfUnchanged(
        'pending_operations', operation.id, { data: JSON.stringify(retainedNotes) },
        pendingSnapshotToExpectedRaw(snapshot) as Record<string, unknown>, true,
        () => isPendingSyncSessionSnapshotStateCurrent(session)
      );
      return { success: false, pendingHandledByV2: true, pendingChanged: !updated, reason: 'capability_unavailable' };
    }

    return await processChecklistPartialUpdateV2(operation, checklist);
  } catch (error) {
    if (error instanceof PendingSyncSessionChangedError) throw error;
    SyncLogger.OpChecklist?.error?.('Falha controlada no checklist partial update:', error);
    return { success: false, pendingHandledByV2: true, reason: 'processing_failed' };
  }
}

async function processChecklistPartialUpdateLegacy(operation: PendingOperation, mediaDiagnosticPendings: readonly Record<string, any>[]): Promise<{
  applied: boolean;
  deferredFieldIds: string[];
  canonicalFieldIds: Record<string, string>;
}> {
  const deferredFieldIds: string[] = [];
  const canonicalFieldIds: Record<string, string> = {};
  const result = (applied: boolean) => ({ applied, deferredFieldIds, canonicalFieldIds });
  try {
    // O nome da tabela deve vir de operation.collection (ex: 'guincho_valores_checklist')
    const tableName = operation.collection;
    const answers = typeof operation.data === 'string' ? JSON.parse(operation.data) : operation.data; // { campo_id: valor, ... }
    // Buscar o checklist para obter o empresa_id
    const checklist = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME, operation.itemId);
    if (!checklist || !checklist.empresa_id) {
      throw new Error('Checklist ou empresa_id não encontrado para o checklist_id: ' + operation.itemId);
    }
    const empresa_id = checklist.empresa_id;
    // Buscar valores existentes para este checklist
    const existentes = await databaseService.listItems<any>(tableName, { checklist_id: operation.itemId });
    const snapshotFields = new Map<string, any>();
    for (const id of new Set([
      ...Object.keys(answers),
      ...existentes.map(row => row.snapshot_field_id).filter(Boolean),
    ])) {
      const field = await databaseService.readItem<any>(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME, String(id)
      );
      if (field) snapshotFields.set(String(id), field);
    }
    // Transformar em array de upserts
    const now = new Date().toISOString();
    let upserts: Array<Record<string, any>> = [];
    for (const [fieldId, valor] of Object.entries(answers)) {
      const requestedSnapshot = snapshotFields.get(fieldId);
      const candidates = existentes.filter(row => row.snapshot_field_id === fieldId || row.campo_id === fieldId ||
        (requestedSnapshot?.original_campo_id && !row.snapshot_field_id && row.campo_id === requestedSnapshot.original_campo_id) ||
        (!requestedSnapshot && snapshotFields.get(row.snapshot_field_id)?.original_campo_id === fieldId));
      const existente = candidates[0];
      const snapshotFieldId = existente?.snapshot_field_id || requestedSnapshot?.id || null;
      const snapshotField = snapshotFieldId ? snapshotFields.get(snapshotFieldId) : null;
      let campoId = snapshotFieldId ? existente?.campo_id ?? null : fieldId;
      let resolvedSnapshotFieldId = snapshotFieldId;
      let ineligible = candidates.length > 1 ||
        (existente && (existente.empresa_id !== empresa_id || existente.checklist_id !== operation.itemId));
      if (snapshotFieldId) {
        const snapshot = snapshotField?.snapshot_id ? await databaseService.readItem<any>(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOTS.NAME, snapshotField.snapshot_id
        ) : null;
        ineligible ||= !snapshotField || snapshotField.empresa_id !== empresa_id ||
          !snapshot || snapshot.empresa_id !== empresa_id || snapshot.checklist_id !== operation.itemId ||
          (checklist.snapshot_id && checklist.snapshot_id !== snapshot.id) ||
          (requestedSnapshot && requestedSnapshot.id !== snapshotFieldId) ||
          (existente?.campo_id && existente.campo_id !== snapshotField.original_campo_id);
        if (!ineligible) {
          if (!existente?.id) ineligible = true;
          else {
            // A edição local pode promover null para o original. Só a identidade
            // remota confirma se a resposta continua física ou já é histórica.
            const remote = await getSyncSupabaseClient().from(tableName)
              .select('id,empresa_id,checklist_id,campo_id,snapshot_field_id')
              .eq('id', existente.id);
            if (remote.error) throw remote.error;
            const remoteRow = Array.isArray(remote.data) && remote.data.length === 1 ? remote.data[0] : null;
            ineligible ||= !Array.isArray(remote.data) || remote.data.length > 1 ||
              (remote.data.length === 1 && (!remoteRow || typeof remoteRow !== 'object' || Array.isArray(remoteRow))) ||
              (remoteRow && (remoteRow.id !== existente.id || remoteRow.empresa_id !== empresa_id ||
                remoteRow.checklist_id !== operation.itemId ||
                (remoteRow.snapshot_field_id && remoteRow.snapshot_field_id !== snapshotFieldId) ||
                (remoteRow.campo_id ? remoteRow.campo_id !== snapshotField.original_campo_id
                  : remoteRow.snapshot_field_id !== snapshotFieldId)));
            if (!ineligible && remoteRow) {
              campoId = remoteRow.campo_id ?? null;
              resolvedSnapshotFieldId = remoteRow.snapshot_field_id ?? null;
            }
            if (!ineligible && campoId) {
              const physicalField = await databaseService.readItem<any>(
                APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME, campoId
              );
              ineligible ||= !physicalField || physicalField.empresa_id !== empresa_id ||
                physicalField.template_id !== checklist.template_id || Boolean(physicalField.retired_at);
            }
          }
        }
      } else if (checklist.snapshot_id) {
        // Sem prova de snapshot, o UUID visual não pode virar um campo físico.
        const physicalField = await databaseService.readItem<any>(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME, fieldId
        );
        ineligible ||= !physicalField || physicalField.empresa_id !== empresa_id ||
          physicalField.template_id !== checklist.template_id || Boolean(physicalField.retired_at);
      }
      if (ineligible) {
        deferredFieldIds.push(fieldId);
        SyncLogger.OpChecklist?.warn?.(`Valor legado adiado: identidade do campo/snapshot não comprovada | campo=${fieldId}`);
        continue;
      }
      canonicalFieldIds[fieldId] = campoId || resolvedSnapshotFieldId;
      upserts.push({
        id: existente?.id, // usar o id do registro existente
        checklist_id: operation.itemId,
        campo_id: campoId,
        ...(snapshotFieldId ? { snapshot_field_id: resolvedSnapshotFieldId } : {}),
        valor: valor,
        empresa_id,
        created_at: existente ? existente.created_at : now, // só define se for novo
        updated_at: now,
      });
    }
    if (upserts.length === 0) return result(false);
    // Normalizar campos de data para formato ISO string
    upserts = upserts.map(normalizeDateFieldsForSupabase);
    SyncLogger.OpChecklist?.info?.(`Upserting ${upserts.length} valores em ${tableName} para checklist ${operation.itemId}`);
    SyncLogger.OpChecklist?.info?.(`upserts preparados: ${upserts.length}`);
    await debugPause('processChecklistPartialUpdate - antes do upsert');
    const { data, error } = await observeChecklistMediaReferenceSend({
      companyId: empresa_id, userId: activePendingSyncExecution?.snapshot.userId ?? '',
      checklistId: operation.itemId, operationId: operation.id, transport: 'legacy_rest',
      values: upserts, pendings: mediaDiagnosticPendings,
    }, () => getSyncSupabaseClient()
      .from(tableName)
      .upsert(upserts, { onConflict: 'id' })
      .select('id,empresa_id,checklist_id,campo_id,snapshot_field_id')); // Confirma as identidades antes do ACK.
    if (error) throw error;
    if (!Array.isArray(data) || data.length !== upserts.length) return result(false);
    const returnedByField = new Map(data.map(row => [row.snapshot_field_id || row.campo_id, row]));
    if (returnedByField.size !== upserts.length || upserts.some(row => {
      const returned = returnedByField.get(row.snapshot_field_id || row.campo_id);
      return !returned?.id || returned.empresa_id !== empresa_id
        || returned.checklist_id !== operation.itemId
        || (returned.campo_id ?? null) !== (row.campo_id ?? null)
        || (returned.snapshot_field_id ?? null) !== (row.snapshot_field_id ?? null)
        || (row.id && returned.id !== row.id);
    })) return result(false);
    return result(true);
  } catch (err) {
    SyncLogger.OpChecklist?.error?.('Falha checklist_partial_update:', err);
    return result(false);
  }
}

// 3. Deleção de checklist
async function processChecklistDelete(operation: PendingOperation): Promise<boolean> {
  return processDeleteOperation(
    APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
    operation as DeleteOp
  );
}

export default {
  checkSyncPrerequisites,
  initializeSyncService,
  shutdownSyncService,
  monitorSyncPrerequisites,
  pullRemoteChanges,
  pullRemoteChangesForRecovery,
  getLastSyncTimestamp,
  saveLastSyncTimestamp,
  isInitialBootstrapComplete,
  markInitialBootstrapComplete,
  pushLocalChanges,
  syncAll
}; 
const NETWORK_TIMEOUT = APP_CONSTANTS.TIMEOUTS.NETWORK_TIMEOUT;

const withNetworkTimeout = async <T>(operation: () => Promise<T>, context: string): Promise<T> => {
  const timeoutMs = NETWORK_TIMEOUT;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        const timeoutError = new Error(`Timeout de rede após ${timeoutMs}ms em ${context}`);
        SyncLogger.Timeout.info(`Timeout detectado em ${context} após ${timeoutMs}ms`);
        reject(timeoutError);
      }, timeoutMs);
    });

    return await Promise.race([operation(), timeoutPromise]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};
